import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { message, Modal, Popconfirm } from "antd";
import { useMsal } from "@azure/msal-react";
import { apiTokenRequest, apiConfig } from "../authConfig";
import { Role as UserRole } from "../constants/roles";
import { useApp } from "../context/AppContext";
import { pickScopingPersistPayload } from "../utils/pickScopingPersistPayload";
import { getActiveImpersonation } from "../utils/impersonationFetch";
import {
  gatherInternalExternalContextModel,
  buildInternalExternalContextHtmlBody,
  CONTEXT_DOCUMENT_TITLE_OPTIONS,
  mergeInternalExternalContextMeta,
} from "../utils/internalExternalContextDocument";
import {
  getOrgChartSpocEffectiveStatus,
  getCompanyArtefactsDocReviewEffectiveStatus,
  isCompanyArtefactsReleasedToDelegates,
  normalizeSpocPersonnelCsvState,
  spocPersonnelLegacySyncFromSelection,
  newSpocPersonnelCsvVersionId,
  orgSetupSubtabPersonaKeyFromRole,
  orgSetupNavTabIndex,
  isOrgSetupSubtabContentSealedReadOnly,
  patchBumpOrgSetupSubtabFurthest,
} from "../utils/organizationalSetupWizard";
import { normalizeArtefactIndexRows, renamePlitsCatalogueTemplateBasename } from "../utils/artefactTemplateCompanyNaming";
import { mergeProjectOnboardingWizard } from "../utils/projectOnboardingWizard";
import { artefactFileBasename, artefactFileNameUiDisplay } from "../utils/artefactDisplayFileName";
import { emailsWithPlaceholders, findEmailPlaceholders, OWNER_NOT_ASSIGNED } from "../utils/emailPlaceholders";
import { parseOrganizationalSetupCsv, displayPerson } from "../utils/parseOrganizationalSetupCsv";
import {
  enrichOrgChartRows,
  computeOrgChartSummary,
  buildOrgForest,
  wouldCreateCycle,
  findDescendantIds,
} from "../utils/orgChartModel";
import { OrgChartCanvas } from "./workbench/OrgChartCanvas";
import { OrgPersonForm } from "./workbench/OrgPersonForm";
import { ArtefactTemplatePreview } from "./ArtefactTemplatePreview";
import { ArtefactBrandedCompareModal } from "./ArtefactBrandedCompareModal";
import { DelegateCompanyArtefactWorkspace } from "./DelegateCompanyArtefactWorkspace";
import { LeadAdvisorApprovalMatrixPanel } from "./LeadAdvisorApprovalMatrixPanel";
import { LeadAdvisorRiskScaleAndKpisPanel } from "./LeadAdvisorRiskScaleAndKpisPanel";
import { canGenerateLeadAdvisorApprovalMatrix } from "../utils/approvalMatrixModel";
import { resolveReadableBlobUrl } from "../utils/blobPreview";
import { durableBlobUrlIfAzure } from "../utils/persistableBlobUrl";
import {
  GAP_MEETING_DEPARTMENT_OPTIONS,
  GAP_MEETING_TYPE_LABELS,
  mapApiMeetingToScheduledRow,
  isMeetingFromOrgSetup,
  formatSpocMeetingDate,
  SPOC_MEETING_TYPE_LABELS,
  MEETING_SCHEDULE_SOURCE_ORG_SETUP,
  normalizeMeetingAttendees,
} from "../utils/gapMeetingScheduleShared";
import SubStepNav from "./page/SubStepNav";
import StageHeader from "./page/StageHeader";
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";

/** Same pattern as Gap Analysis `renderGapAnalysisStatusBar`; phases for organizational setup. */
const ORG_SETUP_STATUS_STEPS = [
  { key: "draft", title: "Draft", sub: "Build & prepare", icon: "file" },
  { key: "review", title: "Under Review", sub: "SPOC reviewing", icon: "search" },
  { key: "approved", title: "Approved", sub: "Structure approved", icon: "check" },
  { key: "published", title: "Published", sub: "Live with delegates", icon: "pencil" },
];

function orgSetupStatusBarIcon(kind) {
  const common = { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24" };
  if (kind === "file") {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    );
  }
  if (kind === "search") {
    return (
      <svg {...common}>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    );
  }
  if (kind === "check") {
    return (
      <svg {...common}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
      />
    </svg>
  );
}

/**
 * 0 Draft — org chart not yet approved (advisor/SPOC prep).
 * 1 Under review — org chart or document package with SPOC.
 * 2 Approved — org chart approved; no open SPOC review (may still prepare release).
 * 3 Published — branded package released to company delegates.
 */
function computeOrgSetupStatusBarIndex(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  if (isCompanyArtefactsReleasedToDelegates(wiz)) return 3;
  const orgEff = getOrgChartSpocEffectiveStatus(w);
  const docEff = getCompanyArtefactsDocReviewEffectiveStatus(w);
  if (orgEff === "pending" || docEff === "pending") return 1;
  if (orgEff === "approved") return 2;
  return 0;
}

function OrganizationalSetupStatusBar({ currentIdx }) {
  const safeIdx = Math.max(0, Math.min(3, Number(currentIdx) || 0));
  const parts = [];
  ORG_SETUP_STATUS_STEPS.forEach((step, i) => {
    const reached = i <= safeIdx;
    parts.push(
      <div key={step.key} className="flex flex-1 flex-col items-center text-center min-w-0 px-0.5">
        <div
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl shadow-md ${
            reached ? "bg-[color:var(--color-step-reached)] text-white" : "border-2 border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]"
          }`}
        >
          {orgSetupStatusBarIcon(step.icon)}
        </div>
        <div
          className={`mt-2.5 text-[8px] font-black uppercase tracking-widest text-balance sm:text-[10px] ${
            reached ? "text-[color:var(--color-accent)]" : "text-[color:var(--color-text-hero)]"
          }`}
        >
          {step.title}
        </div>
        <div className="mt-0.5 text-[8px] font-medium leading-snug text-[color:var(--color-text-muted)] sm:text-[9px]">{step.sub}</div>
      </div>,
    );
    if (i < ORG_SETUP_STATUS_STEPS.length - 1) {
      parts.push(
        <div
          key={`${step.key}-connector`}
          className={`mx-1 h-1 min-w-[12px] max-w-[4rem] flex-1 self-center rounded-full sm:mx-2 ${
            i < safeIdx ? "bg-[color:var(--color-step-reached)]" : "bg-[color:var(--color-bg-alt)]"
          }`}
          aria-hidden
        />,
      );
    }
  });
  return (
    <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-5 shadow-lg sm:px-6 md:px-8">
      <div className="mx-auto flex w-full max-w-5xl items-center">{parts}</div>
    </div>
  );
}

/** Column legend (no sample people): what each header means. Keep in sync with `parseOrganizationalSetupCsv`. */
const SPOC_PERSONNEL_CSV_COLUMN_LEGEND = [
  ["Name", "The person's full name, as it should appear in the organization chart."],
  ["Designation", "Their job title."],
  ["Role", "Their role on this project: Lead advisor, Advisor, Company SPOC or Company Delegate."],
  ["Department", "The department they belong to."],
  ["Reports To", "Their manager, written as Name (Designation), matching that manager's own row exactly."],
];

/** Lists the email drafts that still contain "[...]" placeholders; they cannot be approved or sent until edited. */
function ComplianceEmailPlaceholderWarning({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div data-testid="email-placeholder-warning" className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-xs text-[color:var(--color-warn-fg)]">
      <p className="font-black uppercase tracking-widest text-[color:var(--color-warn-fg)]">
        {items.length} email draft{items.length === 1 ? "" : "s"} still contain placeholders: sending is blocked
      </p>
      <ul className="mt-2 space-y-1">
        {items.map((e) => (
          <li key={e.refId} data-testid="email-placeholder-row">
            <span className="font-mono font-bold">{e.refId}</span>: {e.placeholders.join(", ")}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Visual guide for SPOC personnel upload — keep in sync with `parseOrganizationalSetupCsv` headers. */
function SpocPersonnelCsvFormatReference() {
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--color-info-line)] bg-gradient-to-br from-[color:var(--color-accent-tint)] via-[color:var(--color-bg-white)] to-[color:var(--color-surface-sunken)] shadow-md ring-1 ring-[color:var(--color-info-line)]">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[color:var(--color-info-line)] bg-[color:var(--color-bg-white)] px-5 py-4">
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[color:var(--color-accent)]">CSV format reference</p>
          <p className="mt-1.5 max-w-2xl text-sm font-medium leading-relaxed text-[color:var(--color-text-hero)]">
            One header row, then one row per person. The legend below describes each column; keep the column order and
            the Reports To pattern.
          </p>
        </div>
        <span className="shrink-0 rounded-full border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-fill)] px-3 py-1.5 text-[9px] font-black uppercase tracking-widest text-white shadow-sm">
          Columns
        </span>
      </div>
      <div className="px-5 pb-1 pt-4">
        <div className="overflow-x-auto rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-inner">
          <table className="w-full min-w-[720px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b-2 border-[color:var(--color-accent-fill)] bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-wider text-white/80">
                <th className="whitespace-nowrap px-4 py-3.5">Column</th>
                <th className="px-4 py-3.5">What to enter</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[color:var(--color-border)]">
              {SPOC_PERSONNEL_CSV_COLUMN_LEGEND.map(([col, desc]) => (
                <tr key={col} className="bg-[color:var(--color-bg-white)] font-medium text-[color:var(--color-text-hero)]">
                  <td className="whitespace-nowrap px-4 py-3 text-xs font-bold sm:text-sm">{col}</td>
                  <td className="px-4 py-3 text-xs sm:text-sm">{desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-[color:var(--color-text-muted-dark)]">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-[color:var(--color-bg-alt)] px-2 py-1 text-[color:var(--color-text-hero)]">
            <svg className="h-3.5 w-3.5 shrink-0 text-[color:var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Reports To
          </span>
          <span>
            Use <strong className="font-bold text-[color:var(--color-text-hero)]">Name (Designation)</strong> for the manager, matching that
            person row exactly - this keeps the hierarchy consistent everywhere in the workbench.
          </span>
        </p>
      </div>
      <div className="border-t border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-5 py-4">
        <ul className="grid gap-3 sm:grid-cols-2">
          <li className="flex gap-2 text-xs font-medium leading-snug text-[color:var(--color-text-hero)]">
            <span className="font-black text-[color:var(--color-accent)]" aria-hidden>
              ·
            </span>
            <span>
              Export as <strong className="font-bold text-[color:var(--color-text-hero)]">.csv</strong> in UTF-8. The first line must be the
              five headers in the legend, in that order (commas between columns).
            </span>
          </li>
          <li className="flex gap-2 text-xs font-medium leading-snug text-[color:var(--color-text-hero)]">
            <span className="font-black text-[color:var(--color-accent)]" aria-hidden>
              ·
            </span>
            <span>
              <strong className="font-bold text-[color:var(--color-text-hero)]">Role</strong> is recognized when it includes Lead advisor,
              Company SPOC, Company Delegate, or Advisor (spacing/case can vary slightly).
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
}

/** Normalize person name for matching org chart CSV ↔ project member display name. */
function normalizePersonName(s) {
  return String(s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function artefactOriginalBlobUrl(row, sourceBlobUrlByRowId) {
  const fromRow = row?.blobUrl && String(row.blobUrl).trim();
  if (fromRow) return fromRow;
  const id = row?.id != null ? String(row.id) : "";
  const map = sourceBlobUrlByRowId && typeof sourceBlobUrlByRowId === "object" ? sourceBlobUrlByRowId : {};
  const fromMap = id ? map[id] : "";
  return fromMap && String(fromMap).trim() ? String(fromMap).trim() : "";
}

const ARTEFACT_CLASSIFICATION_PILL = {
  Public: "bg-[color:var(--color-ok-bg)] text-[color:var(--color-ok-fg)] border-[color:var(--color-ok-line)]",
  "Internal - may leave the organization": "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
  "Internal - may not leave the organization": "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)] border-[color:var(--color-info-line)]",
  Confidential: "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)] border-[color:var(--color-warn-line)]",
  "Confidential - Sensitive": "bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-fg)] border-[color:var(--color-danger-line)]",
};

function isAdvisorOrgNavStepComplete(wiz, key) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  switch (key) {
    case "branding":
      return !!w.brandingImportedAt;
    case "org-chart":
      return !!w.orgChartBuiltAt;
    case "artefacts":
      return !!w.companyArtefactsBrandingAppliedAt;
    default:
      return false;
  }
}

/**
 * Sequential unlock: org chart after branding; company artefacts after SPOC approves org chart;
 * remaining tabs after company artefacts branding step (Phase A marker).
 */
function isAdvisorOrgNavStepLocked(wiz, key) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const brandingDone = !!w.brandingImportedAt;
  const orgChartApproved = getOrgChartSpocEffectiveStatus(w) === "approved";
  const artefactsDone = !!w.companyArtefactsBrandingAppliedAt;
  switch (key) {
    case "branding":
      return false;
    case "org-chart":
      return !brandingDone;
    case "artefacts":
      return !orgChartApproved;
    case "context":
    case "compliance-register":
    case "meetings":
      return !artefactsDone;
    default:
      return true;
  }
}

/** Approved org chart visible on delegate / lead advisor dashboards (matches readonly gate clearing). */
function isOrgChartApprovedForReadonlyDashboard(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  if (!w.orgChartBuiltAt || !Array.isArray(w.orgChartRows) || w.orgChartRows.length === 0) return false;
  return getOrgChartSpocEffectiveStatus(w) === "approved";
}

function isSpocOrgNavStepComplete(wiz, key) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  switch (key) {
    case "organizational-details":
      return w.delegateListWorkflowStatus === "sent_to_advisor";
    case "review-org-chart":
      return getOrgChartSpocEffectiveStatus(w) === "approved";
    case "review-documents":
      return (
        getCompanyArtefactsDocReviewEffectiveStatus(w) === "approved" && isCompanyArtefactsReleasedToDelegates(w)
      );
    case "spoc-compliance-register":
      return false;
    case "spoc-meetings":
      return false;
    default:
      return false;
  }
}

function isSpocOrgNavStepLocked(wiz, key) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  const csvSent = w.delegateListWorkflowStatus === "sent_to_advisor";
  const orgApproved = getOrgChartSpocEffectiveStatus(w) === "approved";
  switch (key) {
    case "organizational-details":
      return false;
    case "review-org-chart":
      return !csvSent;
    case "review-documents":
      return !orgApproved;
    case "spoc-compliance-register":
      return !orgApproved;
    case "spoc-meetings":
      return !orgApproved;
    default:
      return true;
  }
}

/** Same routable check as backend compliance-register send-email. */
function isComplianceRegisterRecipientEmail(v) {
  const s = typeof v === "string" ? v.trim() : "";
  return s.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * Detects the backend's Commandment VII content-lock 403 (`complianceRegister.ts`,
 * `validateComplianceRegisterWorkflowTransition`) by its message text — the API has no
 * dedicated error code for it (both this and unrelated 403s share `ErrorCode.FORBIDDEN`).
 * Matched on stable substrings from that message rather than an exact string, so it still
 * catches minor backend wording tweaks. Callers use this to swap the backend's internal
 * "reopen endpoint" phrasing for a persona-appropriate message — never shown verbatim.
 */
function isComplianceRegisterContentLockedErrorMessage(msg) {
  const s = typeof msg === "string" ? msg.toLowerCase() : "";
  return s.includes("content is locked") || s.includes("reopen endpoint");
}

function isDelegateOrgNavStepComplete(_wiz, _key) {
  return false;
}

function isDelegateOrgNavStepLocked(_wiz, _key) {
  return false;
}

function isLeadAdvisorOrgNavStepComplete(wiz, key) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  if (key === "la-org-chart") return isOrgChartApprovedForReadonlyDashboard(wiz);
  if (key === "la-approval-matrix") {
    return typeof w.leadAdvisorApprovalMatrixContinuedAt === "string" && w.leadAdvisorApprovalMatrixContinuedAt.trim().length > 0;
  }
  return false;
}

function isLeadAdvisorOrgNavStepLocked(wiz, key) {
  if (key === "la-org-chart") return false;
  return !isOrgChartApprovedForReadonlyDashboard(wiz);
}

/** Next sub-tab for workflow handoff (red dot); one primary step per role. */
function advisorOrgSetupAttentionKey(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  if (!w.brandingImportedAt) return "branding";
  if (w.delegateListWorkflowStatus === "sent_to_advisor" && !w.orgChartBuiltAt) return "org-chart";
  if (getOrgChartSpocEffectiveStatus(w) === "approved" && !w.companyArtefactsBrandingAppliedAt) return "artefacts";
  if (w.companyArtefactsBrandingAppliedAt) {
    for (const k of ["context", "compliance-register", "meetings"]) {
      if (!isAdvisorOrgNavStepLocked(w, k)) return k;
    }
  }
  return null;
}

function spocOrgSetupAttentionKey(wiz) {
  const w = wiz && typeof wiz === "object" ? wiz : {};
  if (w.brandingImportedAt && w.delegateListWorkflowStatus !== "sent_to_advisor") return "organizational-details";
  if (w.delegateListWorkflowStatus === "sent_to_advisor" && getOrgChartSpocEffectiveStatus(w) === "pending")
    return "review-org-chart";
  if (getOrgChartSpocEffectiveStatus(w) === "approved" && getCompanyArtefactsDocReviewEffectiveStatus(w) === "pending")
    return "review-documents";
  if (
    getOrgChartSpocEffectiveStatus(w) === "approved" &&
    getCompanyArtefactsDocReviewEffectiveStatus(w) === "approved" &&
    !isCompanyArtefactsReleasedToDelegates(w)
  )
    return "review-documents";
  return null;
}

function leadAdvisorOrgSetupAttentionKey(wiz) {
  // "la-risk-kpis" is a view-only step with no completion state, so it never raises the attention dot.
  for (const k of ["la-org-chart", "la-approval-matrix"]) {
    if (isLeadAdvisorOrgNavStepLocked(wiz, k)) continue;
    if (!isLeadAdvisorOrgNavStepComplete(wiz, k)) return k;
  }
  return null;
}

const ORG_SETUP_ATTENTION_LABELS = {
  branding: "Import client branding next",
  "org-chart": "Build the organizational chart next",
  artefacts: "Create company artefacts next",
  context: "Continue to context documents",
  "compliance-register": "Continue to compliance register",
  meetings: "Continue to book meetings",
  "organizational-details": "Upload personnel CSV and send to advisor",
  "review-org-chart": "Review and approve the org chart",
  "review-documents": "Review and approve company documents",
  "spoc-compliance-register": "Review compliance register and owner emails",
  "spoc-meetings": "Assign delegates to advisor-requested meetings",
  "la-org-chart": "Review the approved org chart",
  "la-approval-matrix": "Open approval matrix next",
};

function orgSetupAttentionTitle(wiz, role, tabKey, isLocked, isCompleted) {
  if (isLocked || isCompleted) return null;
  let next = null;
  if (role === "advisor") next = advisorOrgSetupAttentionKey(wiz);
  else if (role === "spoc") next = spocOrgSetupAttentionKey(wiz);
  else if (role === "leadAdvisor") next = leadAdvisorOrgSetupAttentionKey(wiz);
  else return null;
  if (next !== tabKey) return null;
  return ORG_SETUP_ATTENTION_LABELS[tabKey] || "Your next step";
}

/** Read-only artefact template rows (Project onboarding); optional Assign column after branding applied. */
function ArtefactTemplatesTable({
  rows,
  getApiToken,
  busy,
  showAssign,
  showCompare = false,
  assigneeByRowId,
  assignSelectValueByRowId,
  orgAssignOptions,
  assignEmptyHint,
  onAssignChange,
  assignDisabled,
  brandedBlobUrlByRowId = {},
  sourceBlobUrlByRowId = {},
  preferBrandedOpen = false,
  assignRequiresBranded = false,
  onCompareClick,
  compareDisabled = false,
}) {
  if (!rows?.length) return null;
  const showCompareCol = Boolean(showCompare && typeof onCompareClick === "function");
  const tableMin =
    showAssign ?
      showCompareCol ?
        "min-w-[1020px]"
      : "min-w-[940px]"
    : showCompareCol ?
      "min-w-[860px]"
    : "min-w-[720px]";
  return (
    <div className="mt-6 overflow-x-auto rounded-2xl border border-[color:var(--color-border)] shadow-sm">
      <table className={`w-full text-left text-sm ${tableMin}`}>
        <thead>
          <tr className="bg-[color:var(--color-accent-fill)] text-[10px] font-black uppercase tracking-[0.15em] text-white/80">
            <th className="px-4 py-4 min-w-[200px]">Filename</th>
            <th className="px-4 py-4 min-w-[200px]">Title</th>
            <th className="px-4 py-4 min-w-[120px]">Visibility</th>
            <th className="px-4 py-4 w-[100px] text-center">Open</th>
            {showCompareCol ? (
              <th className="px-2 py-4 w-[88px] text-center text-[8px]">Compare</th>
            ) : null}
            {showAssign ? <th className="px-4 py-4 min-w-[220px]">Assign</th> : null}
          </tr>
        </thead>
        <tbody className="divide-y divide-[color:var(--color-border)]">
          {rows.map((a) => {
            const vis = (a.classification || "Unclassified").split(" - ")[0];
            const pill = ARTEFACT_CLASSIFICATION_PILL[vis] || "bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text-muted-dark)] border-[color:var(--color-border)]";
            const bid = String(a.id);
            const branded = brandedBlobUrlByRowId[bid];
            const openUrl = preferBrandedOpen && branded ? branded : a.blobUrl;
            const originalUrl = artefactOriginalBlobUrl(a, sourceBlobUrlByRowId);
            const hasBranded = Boolean(branded);
            const hasPreviewFile =
              Boolean(a.blobUrl && String(a.blobUrl).trim()) || (preferBrandedOpen && hasBranded);
            const assignBlocked =
              assignRequiresBranded && !hasBranded ? true : false;
            return (
              <tr key={a.id} className="font-medium text-[color:var(--color-text-hero)] hover:bg-[color:var(--color-bg-alt)]">
                <td className="px-4 py-3 text-xs text-[color:var(--color-text-muted-dark)]">
                  <span className="line-clamp-2 break-all" title={artefactFileNameUiDisplay(a)}>
                    {artefactFileNameUiDisplay(a) || "-"}
                    {preferBrandedOpen && hasBranded ? (
                      <span className="ml-2 align-middle text-[8px] font-black uppercase text-[color:var(--color-ok-fg)]">
                        Branded
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-bold text-[color:var(--color-text-hero)]">
                  <span className="line-clamp-2">{a.name || "Unnamed artefact"}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded border px-2 py-0.5 text-[8px] font-black uppercase ${pill}`}>
                    {vis}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  {hasPreviewFile ? (
                    <ArtefactTemplatePreview
                      variant="list"
                      blobUrl={openUrl || a.blobUrl}
                      fileLabel={artefactFileBasename(a) || a.templateId}
                      getApiToken={getApiToken}
                    />
                  ) : (
                    <span className="text-xs text-[color:var(--color-text-muted)]">-</span>
                  )}
                </td>
                {showCompareCol ? (
                  <td className="px-2 py-3 text-center">
                    {hasBranded ? (
                      <button
                        type="button"
                        disabled={compareDisabled}
                        onClick={() => onCompareClick(a)}
                        title={
                          originalUrl
                            ? "Original vs branded side by side"
                            : "Open compare - restore original preview after Re-apply branding if the left pane is empty"
                        }
                        className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-2 py-1.5 text-[8px] font-black uppercase tracking-wide text-[color:var(--color-text-hero)] hover:border-[color:var(--color-info-line)] hover:text-[color:var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        Compare
                      </button>
                    ) : (
                      <span className="text-xs text-[color:var(--color-text-muted)]">-</span>
                    )}
                  </td>
                ) : null}
                {showAssign ? (
                  <td className="px-4 py-3">
                    <select
                      value={(assignSelectValueByRowId ? assignSelectValueByRowId[bid] : assigneeByRowId[bid]) || ""}
                      onChange={(e) => onAssignChange?.(a.id, e.target.value)}
                      disabled={
                        assignDisabled || busy || orgAssignOptions.length === 0 || assignBlocked
                      }
                      className="w-full max-w-[280px] rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-semibold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={`Assign ${a.name || "artefact"}`}
                    >
                      <option value="">
                        {assignBlocked
                          ? "- Apply branding first -"
                          : orgAssignOptions.length === 0
                            ? assignEmptyHint || "- No assignees -"
                            : "- Unassigned -"}
                      </option>
                      {orgAssignOptions.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ORG_SETUP_SUBTABS = [
  {
    key: "branding",
    label: "Import client branding",
    title: "Import client branding",
    description: "Pull logo, color palette and fonts from company profiling into this engagement.",
    cta: "Import branding",
  },
  {
    key: "org-chart",
    label: "Create org chart",
    title: "Create organizational chart",
    description: "Review the CSV from the company SPOC and build the personnel directory.",
    cta: "Create org chart",
  },
  {
    key: "artefacts",
    label: "Create company artefacts",
    title: "Create company artefacts",
    description: "Generate company-branded templates and documents.",
    cta: "Create artefacts",
  },
  {
    key: "context",
    label: "Create context documents",
    title: "Create context documents",
    description: "Prepare internal and external context documents using gap analysis inputs.",
    cta: "Create documents",
  },
  {
    key: "compliance-register",
    label: "Create compliance register",
    title: "Create compliance register",
    description: "Map obligations, policies, and evidence slots for your compliance register (preview).",
    cta: "Create register",
  },
  {
    key: "meetings",
    label: "Book meetings",
    title: "Book meetings",
    description: "Schedule working sessions with stakeholders and publish invites.",
    cta: "Book meetings",
  },
];

function getNextAdvisorOrgSetupKey(currentKey) {
  const idx = ORG_SETUP_SUBTABS.findIndex((t) => t.key === currentKey);
  if (idx < 0 || idx >= ORG_SETUP_SUBTABS.length - 1) return null;
  return ORG_SETUP_SUBTABS[idx + 1].key;
}

/** Bottom CTA to advance advisor org-setup sub-tabs (same styling as primary actions). */
function AdvisorOrgSetupContinueFooter({ nextTabLabel, disabled, onContinue, buttonLabel }) {
  const label = buttonLabel ?? (nextTabLabel ? `Continue to ${nextTabLabel}` : null);
  if (!label) return null;
  return (
    <div className="mt-10 flex flex-wrap justify-end border-t border-[color:var(--color-border)] pt-6">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onContinue()}
        className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
      >
        {label}
      </button>
    </div>
  );
}

/** Advisor org setup sub-tabs with step numbers for Gap-style pills. Order matches ORG_SETUP_SUBTABS. */
const ADVISOR_ORG_SETUP_NAV = ORG_SETUP_SUBTABS.map((t, idx) => ({
  key: t.key,
  label: t.label,
  num: idx + 1,
}));

const SPOC_ORG_SUBTABS = [
  {
    key: "organizational-details",
    label: "Organizational details",
    title: "Organizational details",
    description: "Upload the personnel CSV (Name, Designation, Role, Department, Reports To) and send it to the advisor.",
    cta: "Save draft",
    actions: "upload",
  },
  {
    key: "review-org-chart",
    label: "Review & approve org chart",
    title: "Review & approve org chart",
    description: "Review the organizational chart and provide approval.",
    actions: "dual",
  },
  {
    key: "review-documents",
    label: "Review & approve documents",
    title: "Review & approve documents",
    description: "Approve or request revisions for assembled documents.",
    actions: "dual",
  },
  {
    key: "spoc-compliance-register",
    label: "Compliance register",
    title: "Compliance register & owner outreach",
    description:
      "Review each obligation, confirm owner email addresses, and send or copy draft emails. Use bulk send to deliver all ready rows in one run.",
  },
  {
    key: "spoc-meetings",
    label: "Meetings & delegates",
    title: "Meetings & delegates",
    description:
      "When your advisor requests a workshop without naming attendees, assign company delegates here. Invited users receive an in-app notification.",
  },
];

const SPOC_ORG_SETUP_NAV = SPOC_ORG_SUBTABS.map((t, idx) => ({
  key: t.key,
  label: t.label,
  num: idx + 1,
}));

/** Single delegate view: assigned branded documents only (no org-chart / matrix / KPI sub-steps). */
const DELEGATE_ORG_SUBTABS = [
  {
    key: "delegate-company-artefacts",
    label: "Company artefacts",
    title: "Company artefacts",
    description: "Fill and submit assigned branded documents after your advisor releases them.",
  },
];

const DELEGATE_ORG_SETUP_NAV = DELEGATE_ORG_SUBTABS.map((t, idx) => ({
  key: t.key,
  label: t.label,
  num: idx + 1,
}));

/** Lead Advisor only — readonly org chart after SPOC approval; other tabs placeholders. Advisor/SPOC tabs unchanged. */
const LEAD_ADVISOR_ORG_SUBTABS = [
  {
    key: "la-org-chart",
    label: "Organization chart",
    title: "Organization chart",
    description: "Approved organizational chart and personnel directory (view only).",
  },
  {
    key: "la-approval-matrix",
    label: "Approval matrix",
    title: "Approval matrix",
    description: "Route approvals from submitted company artefacts - escalation and SLA set by Lead Advisor.",
  },
  {
    key: "la-risk-kpis",
    label: "Risk assessment scale and KPIs",
    title: "Risk assessment scale and KPIs",
    description:
      "Configured risk point scale from onboarding, severity reference table, and PLITS KPI Master checklist (operational values when available).",
  },
];

const LEAD_ADVISOR_ORG_SETUP_NAV = LEAD_ADVISOR_ORG_SUBTABS.map((t, idx) => ({
  key: t.key,
  label: t.label,
  num: idx + 1,
}));

function isAdvisoryWorkspaceUser(role) {
  return role === UserRole.ADVISOR || role === UserRole.LEAD_ADVISOR || role === UserRole.ADMIN;
}

function attrValue(attributes, key) {
  const a = Array.isArray(attributes) ? attributes.find((x) => x.key === key) : null;
  if (!a) return "";
  const v = a.value;
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

const ROLE_BADGE_CLASS = {
  LEAD_ADVISOR: "border-[color:var(--color-accent)] text-[color:var(--color-accent)] bg-[color:var(--color-accent-tint)]",
  COMPANY_SPOC: "border-[color:var(--color-ok-fg)] text-[color:var(--color-ok-fg)] bg-[color:var(--color-ok-bg)]",
  COMPANY_DELEGATE: "border-[color:var(--color-border-strong)] text-[color:var(--color-text-hero)] bg-[color:var(--color-bg-alt)]",
  ADVISOR: "border-[color:var(--color-accent)] text-[color:var(--color-accent)] bg-[color:var(--color-accent-tint)]",
  UNKNOWN: "border-[color:var(--color-border-strong)] text-[color:var(--color-text-muted-dark)] bg-[color:var(--color-surface-sunken)]",
};

function roleLabel(token) {
  return String(token || "UNKNOWN").replace(/_/g, " ");
}

/** Match CompanyProfileDocument: palette may be #hex or JSON with primary (and optional keys). */
function parseCompanyColorPalette(raw) {
  if (raw == null || raw === "") return { swatches: [], primary: null, rawText: "" };
  const str = String(raw).trim();
  try {
    const parsed = JSON.parse(str);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const orderedKeys = ["primary", "secondary", "accent", "background", "text"];
      const swatches = [];
      for (const k of orderedKeys) {
        const v = parsed[k];
        if (typeof v === "string" && v.trim()) swatches.push({ key: k, value: v.trim() });
      }
      for (const [k, v] of Object.entries(parsed)) {
        if (orderedKeys.includes(k)) continue;
        if (typeof v === "string" && v.trim()) swatches.push({ key: k, value: v.trim() });
      }
      const primary = (typeof parsed.primary === "string" && parsed.primary.trim()) || swatches[0]?.value || null;
      return { swatches, primary, rawText: str };
    }
  } catch {
    /* plain string */
  }
  return { swatches: [{ key: "primary", value: str }], primary: str, rawText: str };
}

function firstFontFamilyLabel(stack) {
  const s = String(stack || "").trim();
  if (!s) return "-";
  const quoted = s.match(/^["']([^"']+)["']/);
  if (quoted) return quoted[1];
  return s.split(",")[0]?.trim().replace(/^["']|["']$/g, "") || "-";
}

function isProbablyUrl(s) {
  const t = String(s || "").trim();
  return /^https?:\/\//i.test(t) || t.startsWith("data:") || t.startsWith("/");
}

/**
 * Workbench step 5 — organizational setup.
 * Advisor/Admin: branding, build org chart, placeholders. Lead Advisor: four dashboard tabs with readonly org chart after SPOC approval.
 */
export function OrganizationalSetup({ project, user, onAfterComplianceRegisterSentToSpoc }) {
  const { updateProjectProfile, saveScopingProfile, fetchProjectProfile, fetchProjectMembers, updateProjectMembers, createMeeting, updateMeeting, fetchMeetings, fetchNotifications, updateMeetingAttendees } =
    useApp();
  const { instance } = useMsal();
  const msalActiveAccountId = instance.getActiveAccount()?.homeAccountId ?? "";
  const getApiToken = useCallback(async () => {
    const account = instance.getActiveAccount();
    const tokenResponse = await instance.acquireTokenSilent({
      ...apiTokenRequest,
      account,
    });
    return tokenResponse.accessToken;
  }, [instance]);
  const isSpoc = user?.role === UserRole.COMPANY_SPOC;
  const isDelegate = user?.role === UserRole.COMPANY_DELEGATE;
  const isLeadAdvisor = user?.role === UserRole.LEAD_ADVISOR;
  const isAdvisory = isAdvisoryWorkspaceUser(user?.role);
  const showViewOnlyBanner = !isAdvisory && !isSpoc && !isDelegate;

  const [sub, setSub] = useState(() => {
    if (isSpoc) return "organizational-details";
    if (isDelegate) return "delegate-company-artefacts";
    if (user?.role === UserRole.LEAD_ADVISOR) return "la-org-chart";
    return "branding";
  });
  const [busy, setBusy] = useState(false);
  const [advMeetDate, setAdvMeetDate] = useState("");
  const [advMeetType, setAdvMeetType] = useState("in_person");
  const [advMeetTitle, setAdvMeetTitle] = useState("");
  const [advMeetNotes, setAdvMeetNotes] = useState("");
  const [advMeetDeptSelect, setAdvMeetDeptSelect] = useState("");
  const [advMeetDeptCustom, setAdvMeetDeptCustom] = useState("");
  const [advMeetLink, setAdvMeetLink] = useState("");
  const [advMeetLocation, setAdvMeetLocation] = useState("");
  const [advMeetScheduled, setAdvMeetScheduled] = useState([]);
  const [advMeetRescheduleId, setAdvMeetRescheduleId] = useState(null);
  const [advMeetRescheduleData, setAdvMeetRescheduleData] = useState({});
  const [advMeetSaving, setAdvMeetSaving] = useState(false);
  const [spocOsMeetRaw, setSpocOsMeetRaw] = useState([]);
  const [spocOsMeetLoading, setSpocOsMeetLoading] = useState(false);
  const [spocOrgTabProjectMembers, setSpocOrgTabProjectMembers] = useState([]);
  const [spocOrgRosterLoading, setSpocOrgRosterLoading] = useState(false);
  const [spocOsDelegatePicksByMeeting, setSpocOsDelegatePicksByMeeting] = useState({});
  const [spocOsSavingMeetingId, setSpocOsSavingMeetingId] = useState(null);
  const [brandingPaletteOpen, setBrandingPaletteOpen] = useState(false);
  const [brandingLogoModalOpen, setBrandingLogoModalOpen] = useState(false);
  const [brandingLogoResolvedSrc, setBrandingLogoResolvedSrc] = useState(null);
  const [brandingLogoResolveLoading, setBrandingLogoResolveLoading] = useState(false);
  const brandingLogoResolveCacheRef = useRef({ raw: "", display: null });
  const spocFileInputRef = useRef(null);
  const [spocDropHover, setSpocDropHover] = useState(false);
  /** 'closed' | 'add' | 'edit' */
  const [personFormMode, setPersonFormMode] = useState("closed");
  const [editingRowId, setEditingRowId] = useState(null);

  const tabList = isSpoc
    ? SPOC_ORG_SUBTABS
    : isDelegate
      ? DELEGATE_ORG_SUBTABS
      : isLeadAdvisor
        ? LEAD_ADVISOR_ORG_SUBTABS
        : ORG_SETUP_SUBTABS;
  const keyForUi = useMemo(() => (tabList.some((t) => t.key === sub) ? sub : tabList[0].key), [sub, tabList]);
  const activeTab = useMemo(() => tabList.find((t) => t.key === keyForUi) ?? tabList[0], [keyForUi, tabList]);

  const wiz = project?.profile?.organizationalSetupWizard && typeof project.profile.organizationalSetupWizard === "object"
    ? project.profile.organizationalSetupWizard
    : {};

  const orgChartReviewEffective = useMemo(() => getOrgChartSpocEffectiveStatus(wiz), [wiz]);
  const companyArtefactsDocReviewEffective = useMemo(
    () => getCompanyArtefactsDocReviewEffectiveStatus(wiz),
    [wiz],
  );
  const companyArtefactsReleased = useMemo(() => isCompanyArtefactsReleasedToDelegates(wiz), [wiz]);

  /**
   * @param {object} patch
   * @param {object} [extraProfile]
   * @param {{ impersonated?: boolean }} [opts] — forwarded to `saveScopingProfile`. Opt-in,
   *   defaults to `false`; every pre-existing call site (which passes 1 or 2 args) is
   *   byte-identical. `impersonated: true` is used only by the two Act-on-behalf callbacks
   *   below (R026/R027) so an active LA impersonation session's `X-Impersonation-Id` header
   *   rides along on that specific save.
   */
  const persistWizard = useCallback(
    async (patch, extraProfile = {}, { impersonated = false } = {}) => {
      if (!project?.id || !saveScopingProfile || !updateProjectProfile) {
        message.error("Cannot save - sign in or open a project.");
        return false;
      }
      const base = project.profile || {};
      const prevWiz =
        base.organizationalSetupWizard && typeof base.organizationalSetupWizard === "object"
          ? base.organizationalSetupWizard
          : {};
      const mergedWizard = { ...prevWiz, ...patch };
      const newProfile = { ...base, organizationalSetupWizard: mergedWizard, ...extraProfile };
      updateProjectProfile(project.id, newProfile);
      /** Server merges `organizationalSetupWizard` shallowly; send only this patch + branding extras — not the full scoping profile (avoids huge bodies / default JSON limits). */
      const ok = await saveScopingProfile(
        project.id,
        pickScopingPersistPayload({ organizationalSetupWizard: patch, ...extraProfile }),
        { impersonated },
      );
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      if (!ok) message.error("Failed to save organizational setup.");
      return ok;
    },
    [project?.id, project?.profile, saveScopingProfile, updateProjectProfile, fetchProjectProfile],
  );

  const orgSetupNavForSealing = useMemo(() => {
    if (isAdvisory && !isLeadAdvisor) return ADVISOR_ORG_SETUP_NAV;
    if (isSpoc) return SPOC_ORG_SETUP_NAV;
    if (isDelegate) return DELEGATE_ORG_SETUP_NAV;
    if (isLeadAdvisor) return LEAD_ADVISOR_ORG_SETUP_NAV;
    return null;
  }, [isAdvisory, isLeadAdvisor, isSpoc, isDelegate]);

  const orgSetupSubtabPersonaKeyResolved = useMemo(
    () => orgSetupSubtabPersonaKeyFromRole(user?.role),
    [user?.role],
  );

  const contentSealedReadOnly = useMemo(() => {
    if (!orgSetupNavForSealing?.length || !orgSetupSubtabPersonaKeyResolved) return false;
    // Lead Advisor sub-steps are not a one-way funnel: after opening Risk KPIs (or beyond), they must still
    // revisit Approval Matrix to generate/refresh rows when delegates finish submissions. Furthest-tab sealing
    // would set readOnly here and disable "Generate / refresh matrix" incorrectly.
    if (orgSetupSubtabPersonaKeyResolved === "leadAdvisor") return false;
    // Advisors must be able to return to Create compliance register after advancing (e.g. Book meetings) to
    // regenerate rows, test AI, or after clearing complianceRegisterPackage — furthest-index sealing blocks that.
    if (orgSetupSubtabPersonaKeyResolved === "advisorWorkbench" && keyForUi === "compliance-register") {
      return false;
    }
    if (
      orgSetupSubtabPersonaKeyResolved === "companySpoc" &&
      (keyForUi === "spoc-compliance-register" || keyForUi === "spoc-meetings")
    ) {
      return false;
    }
    return isOrgSetupSubtabContentSealedReadOnly(
      orgSetupNavForSealing,
      wiz,
      orgSetupSubtabPersonaKeyResolved,
      keyForUi,
    );
  }, [orgSetupNavForSealing, orgSetupSubtabPersonaKeyResolved, wiz, keyForUi]);

  const orgPhaseMutationsLocked = showViewOnlyBanner || contentSealedReadOnly;

  const syncAdvisorOrgSetupMeetings = useCallback(async () => {
    if (!project?.id || !fetchMeetings) return;
    try {
      const all = await fetchMeetings();
      const forProject = (all || []).filter((x) => x.projectId === project.id && isMeetingFromOrgSetup(x));
      forProject.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime());
      setAdvMeetScheduled(forProject.map(mapApiMeetingToScheduledRow));
    } catch (e) {
      console.error("Failed to sync org setup meetings:", e);
    }
  }, [project?.id, fetchMeetings]);

  useEffect(() => {
    if (!isAdvisory || isLeadAdvisor || keyForUi !== "meetings" || !project?.id) return;
    void syncAdvisorOrgSetupMeetings();
  }, [isAdvisory, isLeadAdvisor, keyForUi, project?.id, syncAdvisorOrgSetupMeetings]);

  useEffect(() => {
    if (!isAdvisory || isLeadAdvisor || !project?.id) return;
    const onVisibleOrFocus = () => {
      if (document.visibilityState !== "visible" || keyForUi !== "meetings") return;
      void syncAdvisorOrgSetupMeetings();
    };
    window.addEventListener("focus", onVisibleOrFocus);
    document.addEventListener("visibilitychange", onVisibleOrFocus);
    return () => {
      window.removeEventListener("focus", onVisibleOrFocus);
      document.removeEventListener("visibilitychange", onVisibleOrFocus);
    };
  }, [isAdvisory, isLeadAdvisor, project?.id, keyForUi, syncAdvisorOrgSetupMeetings]);

  const syncSpocOrgSetupMeetings = useCallback(async () => {
    if (!project?.id || !fetchMeetings) return;
    setSpocOsMeetLoading(true);
    try {
      const all = await fetchMeetings();
      const list = (all || []).filter((x) => x.projectId === project.id && isMeetingFromOrgSetup(x));
      list.sort((a, b) => new Date(a.meetingDate).getTime() - new Date(b.meetingDate).getTime());
      setSpocOsMeetRaw(list);
    } catch (e) {
      console.error("Failed to sync SPOC org meetings:", e);
    } finally {
      setSpocOsMeetLoading(false);
    }
  }, [project?.id, fetchMeetings]);

  useEffect(() => {
    if (!isSpoc || keyForUi !== "spoc-meetings" || !project?.id) return;
    void syncSpocOrgSetupMeetings();
  }, [isSpoc, keyForUi, project?.id, syncSpocOrgSetupMeetings]);

  useEffect(() => {
    if (!isSpoc || !project?.id) return;
    const onVis = () => {
      if (document.visibilityState !== "visible" || keyForUi !== "spoc-meetings") return;
      void syncSpocOrgSetupMeetings();
    };
    window.addEventListener("focus", onVis);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onVis);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [isSpoc, project?.id, keyForUi, syncSpocOrgSetupMeetings]);

  useEffect(() => {
    if (!isSpoc || keyForUi !== "spoc-meetings" || !project?.id) return;
    let cancelled = false;
    setSpocOrgRosterLoading(true);
    void (async () => {
      const mem = await fetchProjectMembers(project.id);
      if (!cancelled) {
        setSpocOrgTabProjectMembers(Array.isArray(mem) ? mem : []);
        setSpocOrgRosterLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSpoc, keyForUi, project?.id, fetchProjectMembers]);

  useEffect(() => {
    if (!isSpoc || keyForUi !== "spoc-meetings") return;
    setSpocOsDelegatePicksByMeeting({});
  }, [isSpoc, keyForUi, project?.id]);

  const spocOrgMeetAwaiting = useMemo(
    () =>
      spocOsMeetRaw.filter(
        (m) => m.status === "awaiting_delegates" && normalizeMeetingAttendees(m.attendees).length === 0,
      ),
    [spocOsMeetRaw],
  );

  const spocOrgMeetScheduled = useMemo(
    () =>
      spocOsMeetRaw.filter((m) => {
        const st = m.status || "";
        if (st === "cancelled" || st === "completed") return false;
        if (st === "scheduled" || st === "rescheduled" || st === "confirmed") return true;
        if (st === "awaiting_delegates" && normalizeMeetingAttendees(m.attendees).length > 0) return true;
        return false;
      }),
    [spocOsMeetRaw],
  );

  const spocMeetingsAwaitingCount = spocOrgMeetAwaiting.length;

  const toggleSpocOsMeetingDelegatePick = useCallback((meetingId, userId) => {
    setSpocOsDelegatePicksByMeeting((prev) => {
      const cur = Array.isArray(prev[meetingId]) ? [...prev[meetingId]] : [];
      const idx = cur.indexOf(userId);
      if (idx >= 0) cur.splice(idx, 1);
      else cur.push(userId);
      return { ...prev, [meetingId]: cur };
    });
  }, []);

  const confirmSpocOsDelegatesForMeeting = useCallback(
    async (meetingId, pickIds) => {
      const ids = Array.isArray(pickIds) ? pickIds : [];
      if (ids.length === 0 || !updateMeetingAttendees) return;
      setSpocOsSavingMeetingId(meetingId);
      try {
        const attendees = spocOrgTabProjectMembers
          .filter((m) => ids.includes(m.id))
          .map((m) => ({
            userId: m.id,
            name: m.name || m.email,
            email: m.email,
            role: m.role,
          }));
        const ok = await updateMeetingAttendees(meetingId, attendees);
        if (ok) {
          message.success("Delegates assigned - meeting is now scheduled.");
          setSpocOsDelegatePicksByMeeting((prev) => {
            const next = { ...prev };
            delete next[meetingId];
            return next;
          });
          await syncSpocOrgSetupMeetings();
          if (fetchNotifications) fetchNotifications();
        } else {
          message.error("Could not assign delegates. Try again.");
        }
      } finally {
        setSpocOsSavingMeetingId(null);
      }
    },
    [spocOrgTabProjectMembers, updateMeetingAttendees, syncSpocOrgSetupMeetings, fetchNotifications],
  );

  const advisorOrgSetupResolveMeetDept = useCallback(() => {
    return advMeetDeptSelect === "Other"
      ? String(advMeetDeptCustom || "").trim()
      : String(advMeetDeptSelect || "").trim();
  }, [advMeetDeptSelect, advMeetDeptCustom]);

  const onAdvisorOrgSetupScheduleMeeting = useCallback(async () => {
    if (!project?.id || orgPhaseMutationsLocked || !createMeeting) {
      if (!createMeeting) message.error("Cannot schedule meeting - sign in and try again.");
      return;
    }
    const dept = advisorOrgSetupResolveMeetDept();
    if (!advMeetDate || !String(advMeetTitle || "").trim() || !dept) {
      message.warning("Enter meeting title, department, and date.");
      return;
    }
    setAdvMeetSaving(true);
    try {
      const meeting = await createMeeting(project.id, {
        title: String(advMeetTitle).trim(),
        meetingDate: advMeetDate,
        meetingType: advMeetType,
        notes: advMeetNotes || "",
        attendees: [],
        department: dept,
        meetingLink: advMeetLink || undefined,
        meetingLocation: advMeetLocation || undefined,
        scheduleSource: MEETING_SCHEDULE_SOURCE_ORG_SETUP,
      });
      if (!meeting) {
        message.error("Could not save the meeting. Check your connection and try again.");
        return;
      }
      setAdvMeetTitle("");
      setAdvMeetDate("");
      setAdvMeetNotes("");
      setAdvMeetType("in_person");
      setAdvMeetDeptSelect("");
      setAdvMeetDeptCustom("");
      setAdvMeetLink("");
      setAdvMeetLocation("");
      await syncAdvisorOrgSetupMeetings();
      if (fetchNotifications) fetchNotifications();
    } finally {
      setAdvMeetSaving(false);
    }
  }, [
    project?.id,
    orgPhaseMutationsLocked,
    createMeeting,
    advisorOrgSetupResolveMeetDept,
    advMeetDate,
    advMeetTitle,
    advMeetType,
    advMeetNotes,
    advMeetLink,
    advMeetLocation,
    syncAdvisorOrgSetupMeetings,
    fetchNotifications,
  ]);

  const onAdvisorOrgSetupConfirmReschedule = useCallback(async () => {
    if (!advMeetRescheduleId || !updateMeeting || !advMeetRescheduleData?.meetingDate) return;
    setAdvMeetSaving(true);
    try {
      const result = await updateMeeting(advMeetRescheduleId, advMeetRescheduleData);
      if (result) {
        setAdvMeetRescheduleId(null);
        setAdvMeetRescheduleData({});
        await syncAdvisorOrgSetupMeetings();
        if (fetchNotifications) fetchNotifications();
      } else {
        message.error("Could not update the meeting.");
      }
    } finally {
      setAdvMeetSaving(false);
    }
  }, [
    advMeetRescheduleId,
    advMeetRescheduleData,
    updateMeeting,
    syncAdvisorOrgSetupMeetings,
    fetchNotifications,
  ]);

  const certificationTargetDefault = useMemo(
    () =>
      project?.certification ||
      (Array.isArray(project?.standards) && project.standards.length ? project.standards[0] : "") ||
      "",
    [project?.certification, project?.standards],
  );

  const [contextCertTarget, setContextCertTarget] = useState("");
  useEffect(() => {
    setContextCertTarget(certificationTargetDefault);
  }, [project?.id, certificationTargetDefault]);

  const [contextDocMetaDraft, setContextDocMetaDraft] = useState(null);
  useEffect(() => {
    setContextDocMetaDraft(null);
  }, [project?.id]);

  const baselineContextDocMeta = useMemo(() => {
    if (!project) return null;
    return mergeInternalExternalContextMeta(
      project.profile?.internalExternalContextDocumentMeta,
      project.profile || {},
      project,
    );
  }, [project, project?.id, project?.profile?.internalExternalContextDocumentMeta, project?.companyName]);

  const effectiveContextDocMeta = contextDocMetaDraft ?? baselineContextDocMeta;

  const internalContextModel = useMemo(() => {
    if (!project) return null;
    return gatherInternalExternalContextModel({
      ...project,
      certification: contextCertTarget,
    });
  }, [project, contextCertTarget]);

  const internalContextPreviewModel = useMemo(() => {
    if (!internalContextModel || !effectiveContextDocMeta) return null;
    const profile = project?.profile || {};
    const companyLogoUrl =
      typeof profile.companyLogo === "string" && profile.companyLogo.trim()
        ? profile.companyLogo.trim()
        : typeof effectiveContextDocMeta.logoNote === "string" && effectiveContextDocMeta.logoNote.trim()
          ? effectiveContextDocMeta.logoNote.trim()
          : "";
    const logoPlaceholder = companyLogoUrl
      ? ` Client branding logo URL on file (embed manually if needed): ${companyLogoUrl}`
      : " Logo: import from Organizational Setup branding when publishing (DOCX preview shows text placeholder only).";
    return {
      ...internalContextModel,
      meta: { ...effectiveContextDocMeta, logoPlaceholder },
    };
  }, [internalContextModel, effectiveContextDocMeta, project?.profile]);

  const internalContextPreviewHtml = useMemo(() => {
    if (!internalContextPreviewModel) return "";
    return buildInternalExternalContextHtmlBody(internalContextPreviewModel);
  }, [internalContextPreviewModel]);

  const persistInternalContextDocMeta = useCallback(async () => {
    if (!project?.id || !effectiveContextDocMeta || orgPhaseMutationsLocked) return false;
    setBusy(true);
    try {
      const profile = project.profile || {};
      const snapshot = { ...effectiveContextDocMeta };
      const nextProfile = { ...profile, internalExternalContextDocumentMeta: snapshot };
      updateProjectProfile(project.id, nextProfile);
      const ok = await saveScopingProfile(
        project.id,
        pickScopingPersistPayload({ internalExternalContextDocumentMeta: snapshot }),
      );
      if (ok && fetchProjectProfile) await fetchProjectProfile(project.id);
      if (ok) {
        message.success("Document control fields saved.");
        setContextDocMetaDraft(null);
      } else message.error("Failed to save document control.");
      return ok;
    } finally {
      setBusy(false);
    }
  }, [
    project?.id,
    project?.profile,
    effectiveContextDocMeta,
    orgPhaseMutationsLocked,
    saveScopingProfile,
    updateProjectProfile,
    fetchProjectProfile,
  ]);

  const onGenerateInternalContextDocx = useCallback(async () => {
    if (!project?.id || !effectiveContextDocMeta || orgPhaseMutationsLocked) return;
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      const metaPayload = { ...effectiveContextDocMeta };
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/internal-external-context-document`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({
            certificationTarget: contextCertTarget,
            internalExternalContextDocumentMeta: metaPayload,
          }),
        },
      );
      if (!res.ok) {
        const errText = await res.text().catch(() => "");
        message.error(errText.slice(0, 240) || "Export failed.");
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeFn = String(project.companyName || project.name || "project")
        .replace(/[^a-zA-Z0-9._-]+/g, "_")
        .slice(0, 80) || "project";
      a.href = url;
      a.download = `Internal-external-context-scope-${safeFn}.docx`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      message.success("DOCX downloaded.");
    } catch (e) {
      message.error(e?.message || "Export failed.");
    } finally {
      setBusy(false);
    }
  }, [
    project?.id,
    project?.companyName,
    project?.name,
    effectiveContextDocMeta,
    contextCertTarget,
    orgPhaseMutationsLocked,
    instance,
  ]);

  const patchContextDocMeta = useCallback(
    (partial) => {
      setContextDocMetaDraft((prev) => {
        const base = prev ?? baselineContextDocMeta;
        if (!base) return prev;
        return { ...base, ...partial };
      });
    },
    [baselineContextDocMeta],
  );

  const complianceRegisterPackage = useMemo(() => {
    const p = project?.profile?.complianceRegisterPackage;
    return p && typeof p === "object" && !Array.isArray(p) ? p : null;
  }, [project?.profile?.complianceRegisterPackage]);

  const [crOpenRefId, setCrOpenRefId] = useState(null);
  const crComplianceExpandedRow = useMemo(() => {
    if (!crOpenRefId || !complianceRegisterPackage?.rows?.length) return null;
    return complianceRegisterPackage.rows.find((x) => x.refId === crOpenRefId) ?? null;
  }, [crOpenRefId, complianceRegisterPackage?.rows]);

  const [crRecipientDraft, setCrRecipientDraft] = useState("");

  useEffect(() => {
    if (crComplianceExpandedRow) {
      setCrRecipientDraft(crComplianceExpandedRow.recipientEmail?.trim() ?? "");
    }
  }, [
    crOpenRefId,
    crComplianceExpandedRow?.recipientEmail,
    complianceRegisterPackage?.lastEmailsGeneratedAt,
  ]);

  const [spocComplianceOpenRefId, setSpocComplianceOpenRefId] = useState(null);

  /** Kept in sync while a row is expanded so Send matches the inbox field (avoid defaultValue + stale package row). */
  const spocComplianceExpandedRow = useMemo(() => {
    if (!spocComplianceOpenRefId || !complianceRegisterPackage?.rows?.length) return null;
    return complianceRegisterPackage.rows.find((x) => x.refId === spocComplianceOpenRefId) ?? null;
  }, [spocComplianceOpenRefId, complianceRegisterPackage?.rows]);

  const [spocRecipientDraft, setSpocRecipientDraft] = useState("");

  useEffect(() => {
    if (spocComplianceExpandedRow) {
      setSpocRecipientDraft(spocComplianceExpandedRow.recipientEmail?.trim() ?? "");
    }
  }, [
    spocComplianceOpenRefId,
    spocComplianceExpandedRow?.recipientEmail,
    complianceRegisterPackage?.lastEmailsGeneratedAt,
  ]);

  const spocComplianceBulkEligibleRefIds = useMemo(() => {
    const pkg = complianceRegisterPackage;
    if (!pkg?.rows?.length) return [];
    return pkg.rows
      .filter((r) => {
        const draft = pkg.emailsByRefId?.[r.refId];
        const to = (r.recipientEmail || "").trim();
        return Boolean(draft?.bodyPlain && findEmailPlaceholders(draft).length === 0 && isComplianceRegisterRecipientEmail(to));
      })
      .map((r) => r.refId);
  }, [complianceRegisterPackage]);

  const placeholderEmails = useMemo(() => emailsWithPlaceholders(complianceRegisterPackage), [complianceRegisterPackage]);

  const persistComplianceRegisterPackage = useCallback(
    async (nextPkg) => {
      if (!project?.id || !updateProjectProfile) return false;
      const base = project.profile || {};
      const prevPkg = base.complianceRegisterPackage;
      // Optimistic local update — rolled back below if the save is rejected. Never let this
      // stand as a fake "saved" state (Commandment VII content-lock 403 is the live case).
      updateProjectProfile(project.id, { ...base, complianceRegisterPackage: nextPkg });
      // Fetches directly (rather than going through saveScopingProfile) so the real backend
      // error message is available here, same as onComplianceRegisterGenerateEmails below —
      // saveScopingProfile only returns a bare boolean, which would force the content-lock
      // message onto every failure (network error, 500, expired token), not just the genuine
      // Commandment VII 403.
      let ok = false;
      let errorMessage = "";
      try {
        const account = instance.getActiveAccount();
        const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/profile`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({
            scopingProfile: pickScopingPersistPayload({ complianceRegisterPackage: nextPkg }),
          }),
        });
        const json = await res.json().catch(() => ({}));
        ok = res.ok && json.success !== false;
        if (!ok) errorMessage = json.error?.message || "";
      } catch {
        ok = false;
        errorMessage = "";
      }
      if (ok) {
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
      } else {
        // Roll back the optimistic write — the backend refused it (e.g. the package has left
        // advisor draft and its content is now locked), so the edit never actually saved.
        updateProjectProfile(project.id, { ...base, complianceRegisterPackage: prevPkg });
        message.error(
          isComplianceRegisterContentLockedErrorMessage(errorMessage)
            ? "This compliance register has already been sent and can no longer be edited here. Ask your Lead Advisor to reopen it for corrections."
            : "Could not save your changes. Please try again.",
        );
      }
      return ok;
    },
    [project?.id, project?.profile, updateProjectProfile, fetchProjectProfile, instance],
  );

  const crCertificationHint = useMemo(
    () =>
      project?.certification ||
      (Array.isArray(project?.standards) && project.standards.length ? project.standards[0] : "") ||
      "",
    [project?.certification, project?.standards],
  );

  const onComplianceRegisterGenerate = useCallback(async () => {
    if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked || !project?.id) return;
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/compliance-register/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenResponse.accessToken}`,
        },
        body: JSON.stringify({ certificationTarget: crCertificationHint }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not generate compliance register.");
        return;
      }
      const pkg = json.data?.complianceRegisterPackage;
      if (pkg) {
        updateProjectProfile(project.id, { ...(project.profile || {}), complianceRegisterPackage: pkg });
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
      }
      message.success("Compliance register generated.");
    } finally {
      setBusy(false);
    }
  }, [
    isAdvisory,
    isLeadAdvisor,
    orgPhaseMutationsLocked,
    project?.id,
    project?.profile,
    crCertificationHint,
    instance,
    updateProjectProfile,
    fetchProjectProfile,
  ]);

  const onComplianceRegisterGenerateEmails = useCallback(async () => {
    if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked || !project?.id) return;
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/compliance-register/generate-emails`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({}),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(
          isComplianceRegisterContentLockedErrorMessage(json.error?.message)
            ? "This compliance register has already been sent to the SPOC. Ask a Lead Advisor to reopen it before regenerating."
            : json.error?.message || "Could not generate owner emails.",
        );
        return;
      }
      const pkg = json.data?.complianceRegisterPackage;
      const aug = json.data?.recipientAugmentation;
      if (pkg) {
        updateProjectProfile(project.id, { ...(project.profile || {}), complianceRegisterPackage: pkg });
        if (fetchProjectProfile) await fetchProjectProfile(project.id);
      }
      const withPlaceholders = Array.isArray(json.data?.emailsWithPlaceholders) ? json.data.emailsWithPlaceholders : [];
      if (withPlaceholders.length > 0) {
        message.warning(
          `${withPlaceholders.length} email draft(s) still contain placeholders and cannot be sent until edited: ${withPlaceholders
            .map((e) => `${e.refId} ${(e.placeholders || []).join(" ")}`)
            .join("; ")}`,
        );
      }
      const missing = typeof aug?.stillMissingRecipient === "number" ? aug.stillMissingRecipient : 0;
      const filled = typeof aug?.recipientsNewlyFilled === "number" ? aug.recipientsNewlyFilled : 0;
      if (missing > 0) {
        message.warning(
          `Drafts saved. ${missing} row(s) still have no mapped recipient email - align org chart delegates with responsible owners (same names as roster) and run Generate owner emails again, or add emails manually.${filled > 0 ? ` (${filled} recipient field(s) filled this run.)` : ""}`,
        );
      } else {
        message.success(
          filled > 0
            ? `Owner email drafts generated. Filled ${filled} recipient field(s) from org chart mapping.`
            : "Owner email drafts generated.",
        );
      }
    } finally {
      setBusy(false);
    }
  }, [
    isAdvisory,
    isLeadAdvisor,
    orgPhaseMutationsLocked,
    project?.id,
    project?.profile,
    instance,
    updateProjectProfile,
    fetchProjectProfile,
  ]);

  const onComplianceRegisterSendToSpoc = useCallback(async () => {
    if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked || !project?.id) return;
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/compliance-register/send-to-spoc`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({}),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Could not flag package for SPOC.");
        return;
      }
      const pkg = json.data?.complianceRegisterPackage;
      let mergedProfile = null;
      if (pkg) {
        updateProjectProfile(project.id, { ...(project.profile || {}), complianceRegisterPackage: pkg });
        if (fetchProjectProfile) mergedProfile = await fetchProjectProfile(project.id);
      } else if (fetchProjectProfile) {
        mergedProfile = await fetchProjectProfile(project.id);
      }
      message.success("Package marked as sent to company SPOC.");
      onAfterComplianceRegisterSentToSpoc?.({ profile: mergedProfile, complianceRegisterPackage: pkg });
    } finally {
      setBusy(false);
    }
  }, [
    isAdvisory,
    isLeadAdvisor,
    orgPhaseMutationsLocked,
    project?.id,
    project?.profile,
    instance,
    updateProjectProfile,
    fetchProjectProfile,
    onAfterComplianceRegisterSentToSpoc,
  ]);

  const onSpocComplianceSendEmail = useCallback(
    async (refId, toOverride) => {
      if (!project?.id || orgPhaseMutationsLocked) return;
      setBusy(true);
      try {
        const account = instance.getActiveAccount();
        const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${project.id}/compliance-register/send-email`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${tokenResponse.accessToken}`,
            },
            body: JSON.stringify({ refId, ...(toOverride ? { to: toOverride } : {}) }),
          },
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || "Send failed. Check recipient email.");
          return;
        }
        message.success(`Email sent to ${json.data?.to || "recipient"}.`);
      } catch (e) {
        message.error(e?.message || "Send failed.");
      } finally {
        setBusy(false);
      }
    },
    [project?.id, orgPhaseMutationsLocked, instance],
  );

  const onSpocComplianceBulkSend = useCallback(async () => {
    if (!project?.id || orgPhaseMutationsLocked) return;
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/compliance-register/send-emails-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({}),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Bulk send failed.");
        return;
      }
      const summary = json.data?.summary;
      const results = json.data?.results || [];
      const truncated = Boolean(summary?.truncated);
      const cap = summary?.cap;
      const skippedDueToCap = summary?.skippedDueToCap ?? 0;
      const failed = results.filter((x) => x && !x.ok);
      if (summary?.failed > 0 || failed.length) {
        const detail = failed.slice(0, 5).map((f) => `${f.refId}: ${f.error || "failed"}`).join("; ");
        message.warning(
          `Sent ${summary?.sent ?? 0}, failed ${summary?.failed ?? failed.length}${
            truncated ? ` (only first ${cap} rows per request)` : ""
          }${detail ? ` - ${detail}` : ""}`,
        );
      } else {
        message.success(
          `Sent ${summary?.sent ?? 0} email(s)${
            truncated && skippedDueToCap > 0 ? ` (${skippedDueToCap} not sent - batch limited to ${cap})` : ""
          }.`,
        );
      }
    } catch (e) {
      message.error(e?.message || "Bulk send failed.");
    } finally {
      setBusy(false);
    }
  }, [project?.id, orgPhaseMutationsLocked, instance]);

  const selectOrgSubtab = useCallback(
    async (nextKey) => {
      setSub(nextKey);
      const list = orgSetupNavForSealing;
      const pk = orgSetupSubtabPersonaKeyResolved;
      if (!list?.length || !pk) return;
      const idx = orgSetupNavTabIndex(list, nextKey);
      if (idx < 0) return;
      const patch = patchBumpOrgSetupSubtabFurthest(wiz, pk, idx);
      if (!patch) return;
      await persistWizard(patch);
    },
    [orgSetupNavForSealing, orgSetupSubtabPersonaKeyResolved, wiz, persistWizard],
  );

  const onLeadAdvisorContinueFromApprovalMatrix = useCallback(async () => {
    if (!isLeadAdvisor || !project?.id) return;
    setBusy(true);
    try {
      const ok = await persistWizard({
        leadAdvisorApprovalMatrixContinuedAt: new Date().toISOString(),
      });
      if (!ok) return;
      await selectOrgSubtab("la-risk-kpis");
    } finally {
      setBusy(false);
    }
  }, [isLeadAdvisor, project?.id, persistWizard, selectOrgSubtab]);

  const approvalMatrixReadOnly =
    !!wiz.leadAdvisorApprovalMatrixContinuedAt || contentSealedReadOnly;

  const onImportBranding = async () => {
    if (!isAdvisory || orgPhaseMutationsLocked) return;
    setBusy(true);
    try {
      const attrs = project?.profile?.attributes || [];
      const logo = durableBlobUrlIfAzure(attrValue(attrs, "companyLogo"));
      const palette = attrValue(attrs, "companyColorPalette");
      const font = attrValue(attrs, "companyFont");
      if (!logo && !palette && !font) {
        message.warning("No logo, palette, or font found in company profiling yet. Complete profiling first or upload there.");
      }
      const ok = await persistWizard(
        { brandingImportedAt: new Date().toISOString() },
        {
          ...(logo ? { companyLogo: logo } : {}),
          ...(palette ? { companyColorPalette: palette } : {}),
          ...(font ? { companyFont: font } : {}),
        },
      );
      if (ok) message.success("Branding imported from profiling into this engagement.");
    } finally {
      setBusy(false);
    }
  };

  const onSpocSaveDraft = async (csvText, fileName) => {
    if (!isSpoc || orgPhaseMutationsLocked) return;
    setBusy(true);
    try {
      const nowIso = new Date().toISOString();
      const { versions: prev } = normalizeSpocPersonnelCsvState(wiz);
      const nextVer = prev.length ? Math.max(...prev.map((v) => v.version), 0) + 1 : 1;
      const id = newSpocPersonnelCsvVersionId();
      const newVersions = [
        ...prev.map((v) => ({
          id: v.id,
          version: v.version,
          fileName: v.fileName,
          uploadedAt: v.uploadedAt,
          csvText: v.csvText,
        })),
        {
          id,
          version: nextVer,
          fileName: fileName || "personnel.csv",
          uploadedAt: nowIso,
          csvText,
        },
      ];
      const legacy = spocPersonnelLegacySyncFromSelection(newVersions, id);
      const ok = await persistWizard({
        spocPersonnelCsvVersions: newVersions,
        spocPersonnelCsvSelectedVersionId: id,
        ...legacy,
        delegateListWorkflowStatus: "draft",
      });
      if (ok) message.success(`Version ${nextVer} saved.`);
    } finally {
      setBusy(false);
    }
  };

  const onSpocSelectPersonnelCsvVersion = async (versionId) => {
    if (!isSpoc || orgPhaseMutationsLocked || spocCsvLocked || busy) return;
    const { versions } = normalizeSpocPersonnelCsvState(wiz);
    if (!versions.some((v) => v.id === versionId)) return;
    setBusy(true);
    try {
      const legacy = spocPersonnelLegacySyncFromSelection(versions, versionId);
      const ok = await persistWizard({
        spocPersonnelCsvSelectedVersionId: versionId,
        ...legacy,
      });
      if (!ok) message.error("Could not switch version.");
    } finally {
      setBusy(false);
    }
  };

  const onSpocSendForReview = async () => {
    if (!isSpoc || orgPhaseMutationsLocked) return;
    const { versions, selectedId } = normalizeSpocPersonnelCsvState(wiz);
    const v = versions.find((x) => x.id === selectedId);
    const activeText = (v?.csvText ?? wiz.delegateListCsvText ?? "").trim();
    if (!activeText) {
      message.warning("Upload a CSV file first.");
      return;
    }
    setBusy(true);
    try {
      const ok = await persistWizard({
        delegateListSentForReviewAt: new Date().toISOString(),
        delegateListWorkflowStatus: "sent_to_advisor",
      });
      if (ok) message.success("Sent to advisor for org chart creation.");
    } finally {
      setBusy(false);
    }
  };

  const spocCsvLocked = wiz.delegateListWorkflowStatus === "sent_to_advisor";

  const ingestSpocCsvFile = (file) => {
    if (!isSpoc || orgPhaseMutationsLocked || busy || spocCsvLocked || !file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      void onSpocSaveDraft(text, file.name);
    };
    reader.readAsText(file);
  };

  const onSpocClearDelegateCsv = async () => {
    if (!isSpoc || orgPhaseMutationsLocked || spocCsvLocked || busy) return;
    setBusy(true);
    try {
      const { versions, selectedId } = normalizeSpocPersonnelCsvState(wiz);
      const filtered = versions.filter((v) => v.id !== selectedId);
      const newSelectedId = filtered.length ? filtered[filtered.length - 1].id : "";
      const legacy = newSelectedId
        ? spocPersonnelLegacySyncFromSelection(filtered, newSelectedId)
        : {
            delegateListCsvText: "",
            delegateListFileName: "",
            delegateListUploadedAt: "",
          };
      const ok = await persistWizard({
        spocPersonnelCsvVersions: filtered,
        spocPersonnelCsvSelectedVersionId: newSelectedId,
        ...legacy,
        delegateListWorkflowStatus: "draft",
      });
      if (ok) message.success(filtered.length ? "Version removed." : "All versions cleared. Upload when ready.");
    } finally {
      setBusy(false);
    }
  };

  const onBuildOrgChart = async () => {
    if (!isAdvisory || orgPhaseMutationsLocked) return;
    const text = wiz.delegateListCsvText;
    if (!text) {
      message.error("No CSV available.");
      return;
    }
    const parsed = parseOrganizationalSetupCsv(text);
    if (!parsed.ok) {
      message.error(parsed.error || "Invalid CSV");
      return;
    }
    const enriched = enrichOrgChartRows(parsed.rows);
    const summary = computeOrgChartSummary(enriched);
    setBusy(true);
    try {
      const ok = await persistWizard({
        orgChartBuiltAt: new Date().toISOString(),
        orgChartRows: enriched,
        orgChartSummary: summary,
        orgChartSpocReviewStatus: "draft",
      });
      if (ok) message.success("Personnel directory built.");
    } finally {
      setBusy(false);
    }
  };

  const normalizedOrgRows = useMemo(() => enrichOrgChartRows(wiz.orgChartRows || []), [wiz.orgChartRows]);

  const liveOrgSummary = useMemo(() => computeOrgChartSummary(normalizedOrgRows), [normalizedOrgRows]);

  const orgForest = useMemo(() => buildOrgForest(normalizedOrgRows), [normalizedOrgRows]);

  const managerOptionsForForm = useMemo(() => {
    const desc = editingRowId ? findDescendantIds(normalizedOrgRows, editingRowId) : new Set();
    return normalizedOrgRows
      .filter((r) => r.id !== editingRowId && !desc.has(r.id))
      .map((r) => ({ id: r.id, selfDisplay: r.selfDisplay }));
  }, [normalizedOrgRows, editingRowId]);

  const editingRowForForm = useMemo(
    () => (editingRowId ? normalizedOrgRows.find((r) => r.id === editingRowId) ?? null : null),
    [normalizedOrgRows, editingRowId],
  );

  const persistOrgChartRows = useCallback(async (nextRows) => {
    if (orgPhaseMutationsLocked) return false;
    const enriched = enrichOrgChartRows(nextRows);
    const summary = computeOrgChartSummary(enriched);
    setBusy(true);
    try {
      const ok = await persistWizard({ orgChartRows: enriched, orgChartSummary: summary });
      if (!ok) message.error("Failed to save org chart.");
      return ok;
    } finally {
      setBusy(false);
    }
  }, [persistWizard, orgPhaseMutationsLocked]);

  const closePersonForm = useCallback(() => {
    setPersonFormMode("closed");
    setEditingRowId(null);
  }, []);

  const onSavePersonForm = useCallback(
    async (form) => {
      if (!form.name?.trim()) {
        message.warning("Name is required.");
        return;
      }
      const rows = normalizedOrgRows;
      const parentId = form.parentId || null;
      if (personFormMode === "edit" && editingRowId && parentId && wouldCreateCycle(rows, editingRowId, parentId)) {
        message.error("That manager would create a circular reporting line.");
        return;
      }
      const parent = parentId ? rows.find((r) => r.id === parentId) : null;
      const reportsToRaw = parent ? displayPerson(parent.name, parent.designation) : "";

      let ok = false;
      if (personFormMode === "edit" && editingRowId) {
        const next = rows.map((r) =>
          r.id === editingRowId
            ? {
                ...r,
                name: form.name.trim(),
                designation: form.designation.trim(),
                roleRaw: form.roleRaw.trim(),
                department: form.department.trim(),
                reportsToRaw,
                parentId,
              }
            : r,
        );
        ok = await persistOrgChartRows(next);
        if (ok) message.success("Person updated.");
      }
      if (ok) closePersonForm();
    },
    [normalizedOrgRows, personFormMode, editingRowId, persistOrgChartRows, closePersonForm],
  );

  const onDeleteOrgPerson = useCallback(
    async (row) => {
      const id = row.id;
      const next = normalizedOrgRows
        .filter((r) => r.id !== id)
        .map((r) => (r.parentId === id ? { ...r, parentId: null, reportsToRaw: "" } : r));
      const ok = await persistOrgChartRows(next);
      if (ok) {
        if (editingRowId === id) closePersonForm();
        message.info("Person removed. Their direct reports were moved to top level.");
      }
    },
    [normalizedOrgRows, editingRowId, persistOrgChartRows, closePersonForm],
  );

  const onSendOrgChartToSpoc = useCallback(async () => {
    if (!isAdvisory || orgPhaseMutationsLocked) return;
    if (!normalizedOrgRows.length) {
      message.warning("Build the org chart and add people before sending for review.");
      return;
    }
    setBusy(true);
    try {
      const ok = await persistWizard({
        orgChartSpocReviewStatus: "pending",
        orgChartSentToSpocAt: new Date().toISOString(),
      });
      if (ok) message.success("Org chart sent to company SPOC for review.");
    } finally {
      setBusy(false);
    }
  }, [isAdvisory, orgPhaseMutationsLocked, normalizedOrgRows.length, persistWizard]);

  const onSpocApproveOrgChart = useCallback(async () => {
    if (!isSpoc || orgPhaseMutationsLocked || busy) return;
    setBusy(true);
    try {
      const ok = await persistWizard({
        orgChartSpocReviewStatus: "approved",
        orgChartSpocApprovedAt: new Date().toISOString(),
      });
      if (ok) message.success("Org chart approved.");
    } finally {
      setBusy(false);
    }
  }, [isSpoc, busy, persistWizard, orgPhaseMutationsLocked]);

  /**
   * LA "Act on behalf of Company SPOC" completion — R026/R027
   * (.claude/design/approval-matrix-clarification.md "Read B"). Mirrors
   * `onSpocApproveOrgChart` byte-for-byte except: (a) it is not gated on the real `isSpoc`
   * role (an LA never holds that role), and (b) it requires a live impersonation session
   * already scoped to `company_spoc` on THIS project before it will act, and (c) it passes
   * `{ impersonated: true }` so the save carries `X-Impersonation-Id`. Never call this for a
   * real SPOC — `onSpocApproveOrgChart` stays their only path, unchanged.
   */
  const onSpocApproveOrgChartImpersonated = useCallback(async () => {
    if (orgPhaseMutationsLocked || busy) return;
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "company_spoc" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Company SPOC is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    setBusy(true);
    try {
      const ok = await persistWizard(
        {
          orgChartSpocReviewStatus: "approved",
          orgChartSpocApprovedAt: new Date().toISOString(),
        },
        {},
        { impersonated: true },
      );
      if (ok) message.success("Org chart approved (acting on behalf of Company SPOC).");
    } finally {
      setBusy(false);
    }
  }, [busy, persistWizard, orgPhaseMutationsLocked, project?.id]);

  const onSpocRequestOrgChartChanges = useCallback(async () => {
    if (!isSpoc || orgPhaseMutationsLocked || busy) return;
    setBusy(true);
    try {
      const ok = await persistWizard({ orgChartSpocReviewStatus: "draft" });
      if (ok) message.info("Returned to advisor for changes.");
    } finally {
      setBusy(false);
    }
  }, [isSpoc, busy, persistWizard, orgPhaseMutationsLocked]);

  const company = project?.companyName || "Client";
  const cert = project?.certification || "Engagement";
  const contextLine = `${company} · ${cert}`;

  /** Lead Advisor / advisory progress strip (step 5 header). */
  const advisoryStatusLine = useMemo(() => {
    if (!isAdvisory || isSpoc) return null;
    if (!wiz.brandingImportedAt) return "Status: Awaiting advisor - import client branding.";
    if (wiz.delegateListWorkflowStatus !== "sent_to_advisor")
      return "Status: Awaiting SPOC - organizational details (CSV) not sent for review.";
    if (!wiz.orgChartBuiltAt) return "Status: SPOC shared file - advisor should build org chart.";
    if (orgChartReviewEffective === "pending")
      return "Status: Awaiting company SPOC review of the organizational chart.";
    if (orgChartReviewEffective === "approved")
      return "Status: Org chart approved by company SPOC - ready for next steps.";
    return "Status: Org chart drafted - ready for next steps.";
  }, [isAdvisory, isSpoc, wiz, orgChartReviewEffective]);

  /** Gate readonly org chart (delegate + Lead Advisor dashboards) until SPOC approves. */
  const readOnlyOrgChartGateMessage = useMemo(() => {
    if (!wiz.orgChartBuiltAt || !Array.isArray(wiz.orgChartRows) || wiz.orgChartRows.length === 0) {
      return "Status: Org chart drafted - ready for next steps.";
    }
    if (orgChartReviewEffective === "pending") {
      return "Status: Awaiting company SPOC approval of the organizational chart.";
    }
    if (orgChartReviewEffective === "draft") {
      return "Status: Org chart drafted - ready for next steps.";
    }
    return null;
  }, [wiz.orgChartBuiltAt, wiz.orgChartRows, orgChartReviewEffective]);

  const artefactIndexRows = useMemo(() => {
    const rows = project?.profile?.projectOnboardingWizard?.artefactIndexRows;
    const list = Array.isArray(rows) ? rows : [];
    const company = project?.companyName || project?.name || "";
    return normalizeArtefactIndexRows(list, company);
  }, [
    project?.profile?.projectOnboardingWizard?.artefactIndexRows,
    project?.companyName,
    project?.name,
  ]);

  /**
   * UAT backlog row 26 (Organizational Setup, "Create company artefacts"): "Company
   * artifacts will be loaded manually by a team member, needs necessary upload capability."
   * Lets an Advisor add an artefact template directly from this tab instead of being forced
   * back to Project Onboarding. Mirrors ProjectOnboarding.jsx's
   * handleArtefactTemplateFileSelected / confirmArtefactUploadModal / persistOnboardingWizardPatch
   * exactly — same upload endpoint, same title/classification confirm-modal step, and persists
   * into the SAME `projectOnboardingWizard.artefactIndexRows` field this tab already reads and
   * the downstream branding/assign flow depends on (no separate field). One deliberate
   * deviation from the mirrored pattern: ProjectOnboarding's modal offers a classification
   * *dropdown* sourced from that screen's own document-classification-type CSV step
   * (`docClassRows`), which has no equivalent here — this tab uses a free-text classification
   * input instead (optional; an empty value already renders as "Unclassified" in
   * ArtefactTemplatesTable, same as any other row).
   *
   * ISO/IEC 27002:2022 A.5.15 Access Control — write path gated Advisor-only server-side
   * (`assertProjectOnboardingWizardSaveAllowed`); this tab already renders only for
   * `isAdvisory && !isLeadAdvisor` (see above), so the upload affordance never reaches a role
   * the server would reject. CLAUDE.md §4 Stage 5 "Organizational setup."
   */
  const companyArtefactFileInputRef = useRef(null);
  const [companyArtefactUploadBusy, setCompanyArtefactUploadBusy] = useState(false);
  const [companyArtefactUploadModalOpen, setCompanyArtefactUploadModalOpen] = useState(false);
  const [pendingCompanyArtefactBlobUrl, setPendingCompanyArtefactBlobUrl] = useState("");
  const [pendingCompanyArtefactFileName, setPendingCompanyArtefactFileName] = useState("");
  const [companyArtefactUploadTitle, setCompanyArtefactUploadTitle] = useState("");
  const [companyArtefactUploadClassification, setCompanyArtefactUploadClassification] = useState("");

  const closeCompanyArtefactUploadModal = useCallback(() => {
    setCompanyArtefactUploadModalOpen(false);
    setPendingCompanyArtefactBlobUrl("");
    setPendingCompanyArtefactFileName("");
    setCompanyArtefactUploadTitle("");
    setCompanyArtefactUploadClassification("");
  }, []);

  const handleCompanyArtefactFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!project?.id) {
        message.warning("No project selected.");
        e.target.value = "";
        return;
      }
      setCompanyArtefactUploadBusy(true);
      try {
        const token = await getApiToken();
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${apiConfig.baseUrl}/api/v1/projects/${project.id}/artefact-template/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json.success) {
          message.error(json.error?.message || json.message || "Upload failed.");
          return;
        }
        const data = json.data || {};
        const url = data.url;
        const fileName = data.fileName;
        if (!url || !fileName) {
          message.error("Invalid upload response.");
          return;
        }
        const base = (fileName.split(/[/\\]/).pop() || fileName).replace(/\.[^.]+$/, "") || "Artefact";
        setPendingCompanyArtefactBlobUrl(url);
        setPendingCompanyArtefactFileName(fileName);
        setCompanyArtefactUploadTitle(base);
        setCompanyArtefactUploadClassification("");
        setCompanyArtefactUploadModalOpen(true);
      } catch (err) {
        console.warn("company artefact upload failed", err);
        message.error(err?.message || "Upload failed.");
      } finally {
        setCompanyArtefactUploadBusy(false);
        e.target.value = "";
      }
    },
    [project?.id, getApiToken],
  );

  const confirmCompanyArtefactUploadModal = useCallback(async () => {
    const t = companyArtefactUploadTitle.trim();
    if (!t) {
      message.warning("Enter a title for this artefact.");
      return;
    }
    if (!pendingCompanyArtefactBlobUrl || !pendingCompanyArtefactFileName) {
      message.error("Upload is missing; try again.");
      return;
    }
    if (!project?.id || !saveScopingProfile || !updateProjectProfile) {
      message.warning("Cannot save right now.");
      return;
    }
    const company = project?.companyName || project?.name || "";
    const baseName = pendingCompanyArtefactFileName.split(/[/\\]/).pop() || pendingCompanyArtefactFileName;
    const displayName = renamePlitsCatalogueTemplateBasename(company, baseName) || baseName;
    const newRow = {
      id: `art-${Date.now()}`,
      name: t,
      templateId: displayName,
      classification: companyArtefactUploadClassification.trim(),
      blobUrl: pendingCompanyArtefactBlobUrl,
      fileName: displayName,
    };
    setCompanyArtefactUploadBusy(true);
    try {
      const nextRows = [...artefactIndexRows.map((r) => ({ ...r })), newRow];
      const base = project.profile || {};
      const newProfile = mergeProjectOnboardingWizard(base, { artefactIndexRows: nextRows });
      const ok = await saveScopingProfile(project.id, pickScopingPersistPayload(newProfile));
      if (!ok) {
        message.error("Could not save the artefact. Check your connection and try again.");
        return;
      }
      updateProjectProfile(project.id, newProfile);
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
      closeCompanyArtefactUploadModal();
      message.success("Artefact added.");
    } finally {
      setCompanyArtefactUploadBusy(false);
    }
  }, [
    companyArtefactUploadTitle,
    companyArtefactUploadClassification,
    pendingCompanyArtefactBlobUrl,
    pendingCompanyArtefactFileName,
    artefactIndexRows,
    project?.id,
    project?.profile,
    project?.companyName,
    project?.name,
    saveScopingProfile,
    updateProjectProfile,
    fetchProjectProfile,
    closeCompanyArtefactUploadModal,
  ]);

  const findOrgDelegateRowForMember = useCallback(
    (member) => {
      if (!member) return null;
      const want = normalizePersonName(member.name);
      return (
        normalizedOrgRows.find(
          (r) => r.roleNormalized === "COMPANY_DELEGATE" && normalizePersonName(r.name) === want,
        ) || null
      );
    },
    [normalizedOrgRows],
  );

  const artefactDelegateAssignOptions = useMemo(() => {
    const members = project?.members || [];
    const dels = members.filter((m) => String(m.role || "").toLowerCase() === "company_delegate");
    return dels.map((m) => {
      const orgRow = findOrgDelegateRowForMember(m);
      const label = orgRow
        ? displayPerson(m.name, orgRow.designation)
        : `${m.name} (add org-chart Company Delegate with this name)`;
      return { id: String(m.id), label };
    });
  }, [project?.members, findOrgDelegateRowForMember]);

  /** Delegates that appear on the org chart as COMPANY_DELEGATE (required for assignment). */
  const artefactEligibleDelegates = useMemo(() => {
    const members = project?.members || [];
    const dels = members.filter((m) => String(m.role || "").toLowerCase() === "company_delegate");
    return dels
      .map((m) => ({ member: m, orgRow: findOrgDelegateRowForMember(m) }))
      .filter((x) => x.orgRow != null);
  }, [project?.members, findOrgDelegateRowForMember]);

  const resolveDelegateUserIdForOrgRow = useCallback(
    (orgRowId) => {
      const row = normalizedOrgRows.find((r) => r.id === orgRowId);
      if (!row) return { userId: null, error: "Select a delegate from the org chart." };
      if (row.roleNormalized !== "COMPANY_DELEGATE") {
        return { userId: null, error: "Only org-chart rows with the Company Delegate role can be assigned." };
      }
      const members = project?.members || [];
      const dels = members.filter((m) => String(m.role || "").toLowerCase() === "company_delegate");
      const matches = dels.filter((m) => normalizePersonName(m.name) === normalizePersonName(row.name));
      if (matches.length === 0) {
        return {
          userId: null,
          error:
            "No project member matches this person - invite the delegate to the project with the same display name as in the org chart.",
        };
      }
      if (matches.length > 1) {
        return {
          userId: null,
          error: "Multiple delegates share this name - use unique names in the org chart or project roster.",
        };
      }
      return { userId: matches[0].id, error: null };
    },
    [normalizedOrgRows, project?.members],
  );

  const artefactAssigneeByRowId = useMemo(() => {
    const m = wiz.companyArtefactAssigneeByRowId;
    return m && typeof m === "object" ? { ...m } : {};
  }, [wiz.companyArtefactAssigneeByRowId]);

  const artefactAssignSelectValueByRowId = useMemo(() => {
    const del =
      wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
      typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
        ? { ...wiz.companyArtefactAssigneeDelegateMemberIdByRowId }
        : {};
    const org = artefactAssigneeByRowId;
    const m = {};
    const allTids = new Set([...Object.keys(del), ...Object.keys(org)]);
    for (const tid of allTids) {
      if (del[tid]) {
        m[tid] = String(del[tid]);
        continue;
      }
      const orgId = org[tid];
      if (orgId) {
        const { userId } = resolveDelegateUserIdForOrgRow(orgId);
        if (userId) m[tid] = String(userId);
      }
    }
    return m;
  }, [wiz.companyArtefactAssigneeDelegateMemberIdByRowId, artefactAssigneeByRowId, resolveDelegateUserIdForOrgRow]);

  const brandedBlobUrlByRowId = useMemo(() => {
    const m = wiz.companyArtefactBrandedBlobUrlByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactBrandedBlobUrlByRowId]);

  const sourceBlobUrlByRowId = useMemo(() => {
    const m = wiz.companyArtefactBrandingSourceBlobUrlByRowId;
    return m && typeof m === "object" && !Array.isArray(m) ? { ...m } : {};
  }, [wiz.companyArtefactBrandingSourceBlobUrlByRowId]);

  /** Same checks as onSendCompanyArtefactsDocToSpoc — controls visibility of Send to SPOC. */
  const canSendCompanyArtefactsDocToSpoc = useMemo(() => {
    const brandedIds = Object.keys(brandedBlobUrlByRowId);
    if (brandedIds.length === 0) return false;
    const delMap =
      wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
      typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
        ? wiz.companyArtefactAssigneeDelegateMemberIdByRowId
        : {};
    return brandedIds.every((bid) => Boolean(artefactAssigneeByRowId[bid]) && Boolean(delMap[bid]));
  }, [brandedBlobUrlByRowId, artefactAssigneeByRowId, wiz.companyArtefactAssigneeDelegateMemberIdByRowId]);

  const [compareRow, setCompareRow] = useState(null);
  const [compareHideDownloads, setCompareHideDownloads] = useState(false);
  const [bulkAssignDelegateMemberId, setBulkAssignDelegateMemberId] = useState("");
  const orgMembersFetchedRef = useRef(false);

  useEffect(() => {
    if (artefactEligibleDelegates.length === 0) {
      setBulkAssignDelegateMemberId("");
      return;
    }
    setBulkAssignDelegateMemberId((prev) => {
      const prevStr = prev ? String(prev) : "";
      if (prevStr && artefactEligibleDelegates.some((x) => String(x.member.id) === prevStr)) {
        return prevStr;
      }
      return String(artefactEligibleDelegates[0].member.id);
    });
  }, [artefactEligibleDelegates]);

  useEffect(() => {
    orgMembersFetchedRef.current = false;
  }, [project?.id]);

  useEffect(() => {
    if (orgMembersFetchedRef.current || !project?.id || !fetchProjectMembers) return;
    if (Array.isArray(project.members) && project.members.length > 0) return;
    orgMembersFetchedRef.current = true;
    (async () => {
      const members = await fetchProjectMembers(project.id);
      if (Array.isArray(members) && members.length > 0 && updateProjectMembers) {
        updateProjectMembers(members);
      }
    })();
  }, [project?.id, project?.members, fetchProjectMembers, updateProjectMembers]);

  const onArtefactAssigneeChange = useCallback(
    async (rowId, personId) => {
      if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked) return;
      if (companyArtefactsDocReviewEffective === "pending") {
        message.warning("Assignments are locked while documents are with the company SPOC.");
        return;
      }
      const prev =
        wiz.companyArtefactAssigneeByRowId && typeof wiz.companyArtefactAssigneeByRowId === "object"
          ? { ...wiz.companyArtefactAssigneeByRowId }
          : {};
      const prevDel =
        wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
        typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
          ? { ...wiz.companyArtefactAssigneeDelegateMemberIdByRowId }
          : {};
      const sid = String(rowId);
      if (!personId) {
        delete prev[sid];
        delete prevDel[sid];
        const ok = await persistWizard({
          companyArtefactAssigneeByRowId: prev,
          companyArtefactAssigneeDelegateMemberIdByRowId: prevDel,
        });
        if (!ok) message.error("Failed to save assignee.");
        return;
      }
      const members = project?.members || [];
      const member = members.find((m) => String(m.id) === String(personId));
      if (!member) {
        message.error("Could not find this person on the project.");
        return;
      }
      const orgRow = findOrgDelegateRowForMember(member);
      if (!orgRow) {
        message.error(
          "This delegate must appear on the org chart as Company Delegate with the same display name as in the project roster.",
        );
        return;
      }
      prev[sid] = orgRow.id;
      prevDel[sid] = member.id;
      const ok = await persistWizard({
        companyArtefactAssigneeByRowId: prev,
        companyArtefactAssigneeDelegateMemberIdByRowId: prevDel,
      });
      if (!ok) message.error("Failed to save assignee.");
    },
    [
      isAdvisory,
      isLeadAdvisor,
      orgPhaseMutationsLocked,
      persistWizard,
      wiz.companyArtefactAssigneeByRowId,
      wiz.companyArtefactAssigneeDelegateMemberIdByRowId,
      companyArtefactsDocReviewEffective,
      findOrgDelegateRowForMember,
      project?.members,
    ],
  );

  const onAssignAllArtefactsToDelegate = useCallback(
    async (delegateMemberId) => {
      if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked) return;
      if (companyArtefactsDocReviewEffective === "pending") {
        message.warning("Assignments are locked while documents are with the company SPOC.");
        return;
      }
      const brandedIds = Object.keys(brandedBlobUrlByRowId);
      if (brandedIds.length === 0) {
        message.warning("Apply branding first - there are no branded documents to assign.");
        return;
      }
      if (!delegateMemberId) {
        message.warning("Add a company delegate on the project and org chart first.");
        return;
      }
      const members = project?.members || [];
      const member = members.find((m) => String(m.id) === String(delegateMemberId));
      if (!member) {
        message.error("Could not find this person on the project.");
        return;
      }
      const orgRow = findOrgDelegateRowForMember(member);
      if (!orgRow) {
        message.error(
          "This delegate must appear on the org chart as Company Delegate with the same display name as in the project roster.",
        );
        return;
      }
      const prev =
        wiz.companyArtefactAssigneeByRowId && typeof wiz.companyArtefactAssigneeByRowId === "object"
          ? { ...wiz.companyArtefactAssigneeByRowId }
          : {};
      const prevDel =
        wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
        typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
          ? { ...wiz.companyArtefactAssigneeDelegateMemberIdByRowId }
          : {};
      for (const bid of brandedIds) {
        prev[bid] = orgRow.id;
        prevDel[bid] = member.id;
      }
      const ok = await persistWizard({
        companyArtefactAssigneeByRowId: prev,
        companyArtefactAssigneeDelegateMemberIdByRowId: prevDel,
      });
      if (ok) message.success(`Assigned ${brandedIds.length} document(s) to ${member.name}.`);
      else message.error("Failed to save assignments.");
    },
    [
      isAdvisory,
      isLeadAdvisor,
      orgPhaseMutationsLocked,
      companyArtefactsDocReviewEffective,
      brandedBlobUrlByRowId,
      project?.members,
      findOrgDelegateRowForMember,
      persistWizard,
      wiz.companyArtefactAssigneeByRowId,
      wiz.companyArtefactAssigneeDelegateMemberIdByRowId,
    ],
  );

  const onSendCompanyArtefactsDocToSpoc = useCallback(async () => {
    if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked) return;
    const brandedIds = Object.keys(brandedBlobUrlByRowId);
    if (brandedIds.length === 0) {
      message.warning("Apply branding so at least one branded template exists before sending.");
      return;
    }
    const delMap =
      wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
      typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
        ? wiz.companyArtefactAssigneeDelegateMemberIdByRowId
        : {};
    for (const bid of brandedIds) {
      if (!artefactAssigneeByRowId[bid]) {
        message.warning("Assign every branded row to a delegate before sending for SPOC review.");
        return;
      }
      if (!delMap[bid]) {
        message.warning("Reconfirm each Assign selection so delegate accounts are linked before sending.");
        return;
      }
    }
    setBusy(true);
    try {
      const ok = await persistWizard({
        companyArtefactsDocReviewStatus: "pending",
        companyArtefactsDocSentToSpocAt: new Date().toISOString(),
      });
      if (ok) message.success("Document package sent to company SPOC for review.");
    } finally {
      setBusy(false);
    }
  }, [
    isAdvisory,
    isLeadAdvisor,
    orgPhaseMutationsLocked,
    persistWizard,
    brandedBlobUrlByRowId,
    artefactAssigneeByRowId,
    wiz.companyArtefactAssigneeDelegateMemberIdByRowId,
  ]);

  const onSpocApproveAndReleaseCompanyArtefactsDoc = useCallback(async () => {
    if (!isSpoc || orgPhaseMutationsLocked || busy) return;
    if (companyArtefactsDocReviewEffective !== "pending") return;
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const ok = await persistWizard({
        companyArtefactsDocReviewStatus: "approved",
        companyArtefactsDocSpocApprovedAt: now,
        companyArtefactsReleasedToDelegatesAt: now,
      });
      if (ok) message.success("Documents approved and released to assigned delegates.");
    } finally {
      setBusy(false);
    }
  }, [isSpoc, busy, persistWizard, companyArtefactsDocReviewEffective, orgPhaseMutationsLocked]);

  /**
   * LA "Act on behalf of Company SPOC" completion — R026/R027, sibling of
   * `onSpocApproveOrgChartImpersonated` above. Mirrors
   * `onSpocApproveAndReleaseCompanyArtefactsDoc` byte-for-byte except for the same three
   * differences noted on that sibling (no real-`isSpoc` gate, requires a matching live
   * impersonation session, passes `{ impersonated: true }`). Never call this for a real
   * SPOC — `onSpocApproveAndReleaseCompanyArtefactsDoc` stays their only path, unchanged.
   */
  const onSpocApproveAndReleaseCompanyArtefactsDocImpersonated = useCallback(async () => {
    if (orgPhaseMutationsLocked || busy) return;
    if (companyArtefactsDocReviewEffective !== "pending") return;
    const session = getActiveImpersonation();
    if (!session || session.effectiveRole !== "company_spoc" || session.projectId !== project?.id) {
      message.warning(
        "Your impersonation session for Company SPOC is no longer active on this project. Start a new one to continue.",
      );
      return;
    }
    setBusy(true);
    try {
      const now = new Date().toISOString();
      const ok = await persistWizard(
        {
          companyArtefactsDocReviewStatus: "approved",
          companyArtefactsDocSpocApprovedAt: now,
          companyArtefactsReleasedToDelegatesAt: now,
        },
        {},
        { impersonated: true },
      );
      if (ok) {
        message.success("Documents approved and released to assigned delegates (acting on behalf of Company SPOC).");
      }
    } finally {
      setBusy(false);
    }
  }, [busy, persistWizard, companyArtefactsDocReviewEffective, orgPhaseMutationsLocked, project?.id]);

  /** Legacy: SPOC approved before release was merged into the same action. */
  const onSpocReleaseCompanyArtefactsToDelegatesLegacy = useCallback(async () => {
    if (!isSpoc || orgPhaseMutationsLocked || busy) return;
    if (companyArtefactsDocReviewEffective !== "approved") return;
    if (companyArtefactsReleased) return;
    setBusy(true);
    try {
      const ok = await persistWizard({
        companyArtefactsReleasedToDelegatesAt: new Date().toISOString(),
      });
      if (ok) message.success("Artefacts released to assigned delegates.");
    } finally {
      setBusy(false);
    }
  }, [isSpoc, busy, persistWizard, companyArtefactsDocReviewEffective, companyArtefactsReleased, orgPhaseMutationsLocked]);

  const onSpocRequestCompanyArtefactsDocChanges = useCallback(async () => {
    if (!isSpoc || orgPhaseMutationsLocked || busy) return;
    if (companyArtefactsDocReviewEffective !== "pending") return;
    setBusy(true);
    try {
      const ok = await persistWizard({ companyArtefactsDocReviewStatus: "draft" });
      if (ok) message.info("Returned to advisor for changes.");
    } finally {
      setBusy(false);
    }
  }, [isSpoc, busy, persistWizard, companyArtefactsDocReviewEffective, orgPhaseMutationsLocked]);

  const spocPersonnelCsvState = useMemo(() => normalizeSpocPersonnelCsvState(wiz), [wiz]);

  const spocActiveCsvText = useMemo(() => {
    const { versions, selectedId } = spocPersonnelCsvState;
    const v = versions.find((x) => x.id === selectedId);
    const text = v?.csvText ?? wiz.delegateListCsvText ?? "";
    return text.trim();
  }, [spocPersonnelCsvState, wiz.delegateListCsvText]);

  const spocCsvPreview = useMemo(() => {
    if (!spocActiveCsvText) return null;
    return parseOrganizationalSetupCsv(spocActiveCsvText);
  }, [spocActiveCsvText]);

  /** Parsed SPOC file for advisor org-chart tab (same shape as SPOC preview). */
  const advisorDelegateCsvPreview = useMemo(() => {
    const t = wiz.delegateListCsvText?.trim();
    if (!t) return null;
    return parseOrganizationalSetupCsv(t);
  }, [wiz.delegateListCsvText]);

  const effectiveBranding = useMemo(() => {
    const p = project?.profile || {};
    const attrs = p.attributes || [];
    const rawLogo = p.companyLogo || attrValue(attrs, "companyLogo");
    return {
      logo: rawLogo ? durableBlobUrlIfAzure(rawLogo) : "",
      palette: p.companyColorPalette || attrValue(attrs, "companyColorPalette"),
      font: p.companyFont || attrValue(attrs, "companyFont"),
    };
  }, [project?.profile]);

  const brandingSnapshotStale = useMemo(() => {
    if (!wiz.companyArtefactsBrandingAppliedAt) return false;
    const s = wiz.companyArtefactsBrandingSnapshot;
    if (!s || typeof s !== "object") return false;
    const same =
      String(s.logo ?? "") === String(effectiveBranding.logo ?? "") &&
      String(s.palette ?? "") === String(effectiveBranding.palette ?? "") &&
      String(s.font ?? "") === String(effectiveBranding.font ?? "");
    return !same;
  }, [wiz.companyArtefactsBrandingAppliedAt, wiz.companyArtefactsBrandingSnapshot, effectiveBranding]);

  const paletteParsed = useMemo(() => parseCompanyColorPalette(effectiveBranding.palette), [effectiveBranding.palette]);

  useEffect(() => {
    let cancelled = false;
    const raw = effectiveBranding.logo?.trim();
    if (!raw || !isProbablyUrl(raw)) {
      brandingLogoResolveCacheRef.current = { raw: "", display: null };
      setBrandingLogoResolvedSrc(null);
      setBrandingLogoResolveLoading(false);
      return;
    }
    if (raw.startsWith("data:") || raw.startsWith("/")) {
      brandingLogoResolveCacheRef.current = { raw, display: raw };
      setBrandingLogoResolvedSrc(raw);
      setBrandingLogoResolveLoading(false);
      return;
    }
    if (!/^https?:\/\//i.test(raw)) {
      brandingLogoResolveCacheRef.current = { raw: "", display: null };
      setBrandingLogoResolvedSrc(null);
      setBrandingLogoResolveLoading(false);
      return;
    }
    const cache = brandingLogoResolveCacheRef.current;
    if (cache.raw === raw && cache.display) {
      setBrandingLogoResolveLoading(false);
      setBrandingLogoResolvedSrc((prev) => (prev === cache.display ? prev : cache.display));
      return;
    }
    const account = instance.getActiveAccount();
    if (!account) {
      brandingLogoResolveCacheRef.current = { raw, display: raw };
      setBrandingLogoResolvedSrc(raw);
      setBrandingLogoResolveLoading(false);
      return;
    }
    setBrandingLogoResolveLoading(true);
    (async () => {
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account,
        });
        const fresh = await resolveReadableBlobUrl(apiConfig.baseUrl, raw, async () => tokenResponse.accessToken);
        if (!cancelled) {
          const display = fresh || raw;
          brandingLogoResolveCacheRef.current = { raw, display };
          setBrandingLogoResolvedSrc(display);
          setBrandingLogoResolveLoading(false);
        }
      } catch {
        if (!cancelled) {
          brandingLogoResolveCacheRef.current = { raw, display: raw };
          setBrandingLogoResolvedSrc(raw);
          setBrandingLogoResolveLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveBranding.logo, instance, msalActiveAccountId]);

  const runArtefactBrandingApply = useCallback(async () => {
    if (!isAdvisory || isLeadAdvisor || orgPhaseMutationsLocked || !project?.id) return;
    const hasProfileBranding = !!(
      effectiveBranding.logo?.trim() ||
      effectiveBranding.palette?.trim() ||
      effectiveBranding.font?.trim()
    );
    if (!wiz.brandingImportedAt && !hasProfileBranding) {
      message.warning(
        "Import client branding first, or ensure this engagement has a logo, color palette, or font.",
      );
      return;
    }
    setBusy(true);
    try {
      const account = instance.getActiveAccount();
      const tokenResponse = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      const res = await fetch(
        `${apiConfig.baseUrl}/api/v1/projects/${project.id}/artefact-branding/apply`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenResponse.accessToken}`,
          },
          body: JSON.stringify({
            companyArtefactsBrandingSnapshot: {
              logo: effectiveBranding.logo || null,
              palette: effectiveBranding.palette ?? null,
              font: effectiveBranding.font ?? null,
            },
          }),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        message.error(json.error?.message || "Failed to apply branding to templates.");
        return;
      }
      const results = Array.isArray(json.data?.results) ? json.data.results : [];
      const failed = results.filter((r) => r && !r.ok);
      if (failed.length) {
        message.warning(`${failed.length} template(s) could not be fully processed. Assign is limited until each row succeeds.`);
      } else {
        message.success("Branding applied to templates.");
      }
      if (fetchProjectProfile) await fetchProjectProfile(project.id);
    } catch {
      message.error("Failed to apply branding to templates.");
    } finally {
      setBusy(false);
    }
  }, [
    isAdvisory,
    isLeadAdvisor,
    orgPhaseMutationsLocked,
    project?.id,
    instance,
    wiz.brandingImportedAt,
    effectiveBranding.logo,
    effectiveBranding.palette,
    effectiveBranding.font,
    fetchProjectProfile,
  ]);

  const showLeadAdvisorApprovalMatrixBanner = useMemo(() => {
    if (!isLeadAdvisor) return false;
    const orgOk = isOrgChartApprovedForReadonlyDashboard(wiz);
    const gen = canGenerateLeadAdvisorApprovalMatrix(wiz, artefactIndexRows, orgOk);
    if (!gen.ok) return false;
    return !wiz.leadAdvisorApprovalMatrixGeneratedAt;
  }, [isLeadAdvisor, wiz, artefactIndexRows]);

  const orgSetupStatusBarIdx = useMemo(() => computeOrgSetupStatusBarIndex(wiz), [wiz]);

  const orgNavSteps = (() => {
    if (isAdvisory && !isLeadAdvisor) {
      return ADVISOR_ORG_SETUP_NAV.map((s) => {
        const locked = isAdvisorOrgNavStepLocked(wiz, s.key);
        const completed = isAdvisorOrgNavStepComplete(wiz, s.key);
        return { ...s, locked, completed, attentionTitle: orgSetupAttentionTitle(wiz, "advisor", s.key, locked, completed) };
      });
    }
    if (isSpoc) {
      return SPOC_ORG_SETUP_NAV.map((s) => {
        const locked = isSpocOrgNavStepLocked(wiz, s.key);
        const completed = isSpocOrgNavStepComplete(wiz, s.key);
        return { ...s, locked, completed, attentionTitle: orgSetupAttentionTitle(wiz, "spoc", s.key, locked, completed) };
      });
    }
    if (isDelegate) {
      return DELEGATE_ORG_SETUP_NAV.map((s) => ({
        ...s,
        locked: isDelegateOrgNavStepLocked(wiz, s.key),
        completed: isDelegateOrgNavStepComplete(wiz, s.key),
      }));
    }
    if (isLeadAdvisor) {
      return LEAD_ADVISOR_ORG_SETUP_NAV.map((s) => {
        const locked = isLeadAdvisorOrgNavStepLocked(wiz, s.key);
        const completed = isLeadAdvisorOrgNavStepComplete(wiz, s.key);
        return { ...s, locked, completed, attentionTitle: orgSetupAttentionTitle(wiz, "leadAdvisor", s.key, locked, completed) };
      });
    }
    return tabList.map((t) => ({ key: t.key, label: t.label }));
  })();

  return (
    <div className="space-y-6">
      <StageHeader
        title="Organizational Setup"
        actions={<p className="text-right text-sm font-medium text-[color:var(--color-text-muted)] sm:max-w-md">{contextLine}</p>}
      />

      <OrganizationalSetupStatusBar currentIdx={orgSetupStatusBarIdx} />

      {advisoryStatusLine && (
        <div className="rounded-2xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-4 py-3 text-sm font-semibold text-[color:var(--color-accent)]">
          {advisoryStatusLine}
        </div>
      )}

      {showLeadAdvisorApprovalMatrixBanner ? (
        <div className="flex flex-col gap-3 rounded-2xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-[color:var(--color-accent)]">
            All assigned branded Word documents are submitted. Open the approval matrix to generate rows and set
            escalation and SLA.
          </p>
          <button
            type="button"
            onClick={() => void selectOrgSubtab("la-approval-matrix")}
            className="shrink-0 rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-colors hover:opacity-90"
          >
            Go to approval matrix
          </button>
        </div>
      ) : null}

      {!(isDelegate && DELEGATE_ORG_SETUP_NAV.length <= 1) && (
        <SubStepNav
          ariaLabel="Organizational setup sections"
          activeKey={keyForUi}
          onSelect={(k) => void selectOrgSubtab(k)}
          steps={orgNavSteps}
        />
      )}

      {contentSealedReadOnly ? (
        <div className="rounded-2xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-4 py-3 text-[11px] font-semibold text-[color:var(--color-ok-fg)]">
          You advanced past this sub-step (read-only).
        </div>
      ) : null}

      {/* —— Advisory: Import branding —— */}
      {isAdvisory && !isLeadAdvisor && activeTab.key === "branding" && (
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>
          {wiz.brandingImportedAt && (
            <p className="mt-4 text-xs font-bold text-[color:var(--color-ok-fg)]">
              Last imported: {formatDdMmYyyyTime(wiz.brandingImportedAt)}
            </p>
          )}
          {!wiz.brandingImportedAt && (
            <div className="mt-8">
              <button
                type="button"
                disabled={busy || orgPhaseMutationsLocked}
                onClick={onImportBranding}
                className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
              >
                {activeTab.cta}
              </button>
            </div>
          )}

          {wiz.brandingImportedAt && (
            <div className="mt-10 grid gap-6 lg:grid-cols-3">
              {/* Company logo */}
              <div className="flex flex-col rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Company logo</p>
                <div className="mt-4 flex min-h-[160px] flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  {effectiveBranding.logo && isProbablyUrl(effectiveBranding.logo) ? (
                    <>
                      {brandingLogoResolveLoading && !brandingLogoResolvedSrc ? (
                        <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Loading logo…</p>
                      ) : (
                        <img
                          src={brandingLogoResolvedSrc || effectiveBranding.logo}
                          alt="Company logo"
                          className="max-h-28 max-w-full object-contain"
                        />
                      )}
                      <button
                        type="button"
                        disabled={brandingLogoResolveLoading && !brandingLogoResolvedSrc}
                        onClick={() => setBrandingLogoModalOpen(true)}
                        className="mt-3 text-xs font-bold uppercase tracking-wide text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        View file
                      </button>
                    </>
                  ) : effectiveBranding.logo ? (
                    <p className="text-center text-xs font-medium text-[color:var(--color-text-muted-dark)]">{effectiveBranding.logo}</p>
                  ) : (
                    <p className="text-center text-xs font-medium text-[color:var(--color-text-muted)]">No logo in profiling</p>
                  )}
                </div>
              </div>

              {/* Company color palette */}
              <div className="flex flex-col rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Company color palette</p>
                <button
                  type="button"
                  disabled={orgPhaseMutationsLocked}
                  onClick={() => setBrandingPaletteOpen((o) => !o)}
                  className="mt-4 flex w-full items-center justify-between gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-left transition-colors hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span
                      className="h-9 w-9 shrink-0 rounded-full border border-[color:var(--color-border)] shadow-inner"
                      style={{
                        background: paletteParsed.primary || "linear-gradient(135deg, #052873 50%, #fff 50%)",
                      }}
                      title={paletteParsed.primary || "Palette"}
                    />
                    <span className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]">
                      Configure palette
                    </span>
                  </span>
                  <span className="text-[color:var(--color-text-muted)]" aria-hidden>
                    {brandingPaletteOpen ? "▴" : "▾"}
                  </span>
                </button>
                {brandingPaletteOpen && (
                  <div className="mt-4 space-y-2 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-3">
                    {paletteParsed.swatches.length === 0 ? (
                      <p className="text-xs font-medium text-[color:var(--color-text-muted)]">No palette in profiling</p>
                    ) : (
                      paletteParsed.swatches.map(({ key, value }) => (
                        <div key={`${key}-${value}`} className="flex items-center gap-3">
                          <span
                            className="h-8 w-8 shrink-0 rounded-lg border border-[color:var(--color-border)]"
                            style={{ background: value }}
                            title={value}
                          />
                          <div className="min-w-0">
                            <p className="text-[10px] font-black uppercase tracking-wider text-[color:var(--color-text-muted)]">{key}</p>
                            <p className="truncate font-mono text-xs text-[color:var(--color-text-hero)]">{value}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Company font */}
              <div className="flex flex-col rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm">
                <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Company font</p>
                <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Preview</p>
                    <a
                      href="https://fonts.google.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)]"
                    >
                      Fonts
                    </a>
                  </div>
                  <p
                    className="mt-3 text-2xl font-bold text-[color:var(--color-text-hero)]"
                    style={{ fontFamily: effectiveBranding.font || "inherit" }}
                  >
                    {firstFontFamilyLabel(effectiveBranding.font)}
                  </p>
                </div>
                <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Custom CSS font-family (optional)
                </p>
                <div className="mt-2 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-alt)] px-3 py-2 font-mono text-xs text-[color:var(--color-text-hero)]">
                  {effectiveBranding.font || "-"}
                </div>
              </div>
            </div>
          )}
          {wiz.brandingImportedAt &&
            !contentSealedReadOnly &&
            (() => {
              const nextKey = getNextAdvisorOrgSetupKey("branding");
              const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
              return nextTab ? (
                <AdvisorOrgSetupContinueFooter
                  nextTabLabel={nextTab.label}
                  disabled={busy || orgPhaseMutationsLocked}
                  onContinue={() => void selectOrgSubtab(nextKey)}
                />
              ) : null;
            })()}
          <Modal
            title="Company logo"
            open={brandingLogoModalOpen}
            onCancel={() => setBrandingLogoModalOpen(false)}
            footer={null}
            width={720}
            centered
            destroyOnClose
          >
            {brandingLogoResolvedSrc || effectiveBranding.logo ? (
              <img
                src={brandingLogoResolvedSrc || effectiveBranding.logo}
                alt="Company logo"
                className="max-h-[70vh] w-full object-contain"
              />
            ) : null}
          </Modal>
        </div>
      )}

      {/* —— Advisory: Create org chart —— */}
      {isAdvisory && !isLeadAdvisor && activeTab.key === "org-chart" && (
        <div className="space-y-6">
          {!(orgChartReviewEffective === "pending" && wiz.orgChartBuiltAt) && (
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>

            {wiz.delegateListWorkflowStatus !== "sent_to_advisor" && (
              <div className="mt-6 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                The company SPOC has not sent the personnel file for review yet. You will be able to build the chart
                after they submit <strong>Organizational details</strong>.
              </div>
            )}

            {wiz.delegateListWorkflowStatus === "sent_to_advisor" && wiz.delegateListCsvText && (
              <>
                <div className="mt-6">
                  <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">File from SPOC</p>
                  <p className="text-sm font-bold text-[color:var(--color-text-hero)]">{wiz.delegateListFileName || "personnel.csv"}</p>
                </div>

                {advisorDelegateCsvPreview && !advisorDelegateCsvPreview.ok && (
                  <div className="mt-5 space-y-3">
                    <div className="rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                      <span className="font-black uppercase tracking-wide text-[color:var(--color-warn-fg)]">Could not parse CSV - </span>
                      {advisorDelegateCsvPreview.error}
                    </div>
                    <div className="max-h-40 overflow-auto rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-3 font-mono text-[11px] text-[color:var(--color-text-hero)]">
                      <pre className="whitespace-pre-wrap">{wiz.delegateListCsvText}</pre>
                    </div>
                  </div>
                )}

                {advisorDelegateCsvPreview?.ok && (
                  <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3">
                      <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                        SPOC personnel preview
                      </p>
                      <p className="text-xs font-bold text-[color:var(--color-text-muted-dark)]">
                        {advisorDelegateCsvPreview.summary.totalPeople} people ·{" "}
                        {advisorDelegateCsvPreview.summary.departments} departments
                      </p>
                    </div>
                    <div className="max-h-[min(480px,60vh)] overflow-auto">
                      <table className="w-full min-w-[640px] text-left text-sm">
                        <thead className="sticky top-0 z-10 bg-[color:var(--color-accent-fill)] text-[8px] font-black uppercase tracking-[0.15em] text-white/80">
                          <tr>
                            <th className="px-4 py-3">#</th>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Designation</th>
                            <th className="px-4 py-3">Role</th>
                            <th className="px-4 py-3">Department</th>
                            <th className="px-4 py-3">Reports to</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-[color:var(--color-border)]">
                          {advisorDelegateCsvPreview.rows.map((row, i) => (
                            <tr key={i} className="font-medium text-[color:var(--color-text-hero)]">
                              <td className="px-4 py-2.5 text-[color:var(--color-text-muted)]">{i + 1}</td>
                              <td className="px-4 py-2.5">{row.name}</td>
                              <td className="px-4 py-2.5">{row.designation}</td>
                              <td className="px-4 py-2.5">
                                <span
                                  className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                                >
                                  {roleLabel(row.roleNormalized)}
                                </span>
                              </td>
                              <td className="px-4 py-2.5">{row.department || "-"}</td>
                              <td className="px-4 py-2.5 text-[color:var(--color-text-muted-dark)]">
                                {row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={
                      busy ||
                      orgPhaseMutationsLocked ||
                      !!wiz.orgChartBuiltAt ||
                      (advisorDelegateCsvPreview != null && !advisorDelegateCsvPreview.ok)
                    }
                    onClick={onBuildOrgChart}
                    className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                  >
                    {wiz.orgChartBuiltAt ? "Chart built" : activeTab.cta}
                  </button>
                </div>
              </>
            )}
          </div>
          )}

          {wiz.orgChartBuiltAt && Array.isArray(wiz.orgChartRows) && wiz.orgChartRows.length > 0 && orgChartReviewEffective !== "pending" && (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  ["Total people", liveOrgSummary.totalPeople],
                  ["Departments", liveOrgSummary.departments],
                  ["Top level", liveOrgSummary.topLevel],
                  ["Reporting lines", liveOrgSummary.reportingLines],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-5 shadow-sm">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{label}</p>
                    <p className="mt-2 text-2xl font-black text-[color:var(--color-text-hero)]">{val}</p>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                <div>
                  <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Organizational chart</h3>
                  <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted)]">
                    Roles, designations and reporting lines - hover a card for edit or delete, or use the directory below.
                  </p>
                </div>

                {personFormMode === "edit" && editingRowId && (
                  <div className="mt-6">
                    <OrgPersonForm
                      mode="edit"
                      editingRow={editingRowForForm}
                      managerOptions={managerOptionsForForm}
                      onSave={(f) => void onSavePersonForm(f)}
                      onCancel={closePersonForm}
                      disabled={busy || orgPhaseMutationsLocked}
                    />
                  </div>
                )}

                <div className="mt-8 min-h-[120px] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  {/* Commandment VIII: company logo (already resolved via effectiveBranding,
                      same durableBlobUrlIfAzure(attrValue(attrs, "companyLogo")) path as
                      onImportBranding) propagates onto the org-chart canvas itself. */}
                  <OrgChartCanvas
                    forest={orgForest}
                    disabled={busy || orgPhaseMutationsLocked}
                    logoUrl={brandingLogoResolvedSrc || effectiveBranding.logo}
                    onEdit={(row) => {
                      setPersonFormMode("edit");
                      setEditingRowId(row.id);
                    }}
                    onDelete={(row) => void onDeleteOrgPerson(row)}
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Personnel directory</h3>
                <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted)]">
                  Same data as the chart - edit or delete from here or from the chart cards.
                </p>
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[720px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-border)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                        <th className="pb-3 pr-3">#</th>
                        <th className="pb-3 pr-3">Name</th>
                        <th className="pb-3 pr-3">Designation</th>
                        <th className="pb-3 pr-3">Role</th>
                        <th className="pb-3 pr-3">Department</th>
                        <th className="pb-3 pr-3">Reports to</th>
                        <th className="pb-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border)]">
                      {normalizedOrgRows.map((row, i) => (
                        <tr key={row.id} className="font-medium text-[color:var(--color-text-hero)]">
                          <td className="py-3 pr-3 text-[color:var(--color-text-muted)]">{i + 1}</td>
                          <td className="py-3 pr-3">{row.name}</td>
                          <td className="py-3 pr-3">{row.designation}</td>
                          <td className="py-3 pr-3">
                            <span
                              className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                            >
                              {roleLabel(row.roleNormalized)}
                            </span>
                          </td>
                          <td className="py-3 pr-3">{row.department || "-"}</td>
                          <td className="py-3 pr-3 text-[color:var(--color-text-muted-dark)]">{row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}</td>
                          <td className="py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                disabled={busy || orgPhaseMutationsLocked}
                                onClick={() => {
                                  setPersonFormMode("edit");
                                  setEditingRowId(row.id);
                                }}
                                className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] disabled:opacity-50"
                              >
                                Edit
                              </button>
                              <Popconfirm
                                title="Remove this person?"
                                description="Direct reports will move to top level."
                                okText="Remove"
                                cancelText="Cancel"
                                onConfirm={() => void onDeleteOrgPerson(row)}
                                disabled={busy || orgPhaseMutationsLocked}
                              >
                                <button
                                  type="button"
                                  disabled={busy || orgPhaseMutationsLocked}
                                  className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] hover:text-[color:var(--color-danger-fg)] disabled:opacity-50"
                                >
                                  Delete
                                </button>
                              </Popconfirm>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {orgChartReviewEffective === "draft" && (
                <div className="flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={busy || orgPhaseMutationsLocked}
                    onClick={() => void onSendOrgChartToSpoc()}
                    className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                  >
                    Send for review to SPOC
                  </button>
                </div>
              )}
            </>
          )}
          {wiz.orgChartBuiltAt &&
            orgChartReviewEffective === "approved" &&
            !contentSealedReadOnly &&
            (() => {
              const nextKey = getNextAdvisorOrgSetupKey("org-chart");
              const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
              return nextTab ? (
                <AdvisorOrgSetupContinueFooter
                  nextTabLabel={nextTab.label}
                  disabled={busy || orgPhaseMutationsLocked}
                  onContinue={() => void selectOrgSubtab(nextKey)}
                />
              ) : null;
            })()}
        </div>
      )}

      {/* —— SPOC: Organizational details (CSV) —— */}
      {isSpoc && activeTab.key === "organizational-details" && (
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
          <p className="mt-2 max-w-2xl text-sm font-medium leading-relaxed text-[color:var(--color-text-muted)]">{activeTab.description}</p>

          <SpocPersonnelCsvFormatReference />

          {!spocCsvLocked ? (
            <div className="mt-6">
              {spocPersonnelCsvState.versions.length > 0 ? (
                <div className="mb-6 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">CSV versions</p>
                  <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted-dark)]">
                    Select the version to send to your advisor for the org chart. New uploads add the next version
                    number automatically.
                  </p>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[520px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-border)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                          <th className="pb-2 pr-3">Active</th>
                          <th className="pb-2 pr-3">Version</th>
                          <th className="pb-2 pr-3">File</th>
                          <th className="pb-2">Uploaded</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[color:var(--color-border)]">
                        {spocPersonnelCsvState.versions.map((ver) => (
                          <tr key={ver.id} className="font-medium text-[color:var(--color-text-hero)]">
                            <td className="py-2 pr-3">
                              <input
                                type="radio"
                                name="spoc-personnel-csv-version"
                                checked={ver.id === spocPersonnelCsvState.selectedId}
                                onChange={() => void onSpocSelectPersonnelCsvVersion(ver.id)}
                                disabled={busy || orgPhaseMutationsLocked}
                                className="h-4 w-4 accent-[color:var(--color-accent-fill)]"
                                aria-label={`Use version ${ver.version}`}
                              />
                            </td>
                            <td className="py-2 pr-3">{ver.version}</td>
                            <td className="py-2 pr-3">{ver.fileName}</td>
                            <td className="py-2 text-xs text-[color:var(--color-text-muted-dark)]">
                              {ver.uploadedAt ? formatDdMmYyyyTime(ver.uploadedAt) : "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
              <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Upload CSV</p>
              <input
                ref={spocFileInputRef}
                type="file"
                accept=".csv,text/csv"
                disabled={busy || orgPhaseMutationsLocked}
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) ingestSpocCsvFile(f);
                  e.target.value = "";
                }}
              />
              <div
                role="button"
                tabIndex={busy || orgPhaseMutationsLocked ? -1 : 0}
                onKeyDown={(e) => {
                  if (busy || orgPhaseMutationsLocked) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    spocFileInputRef.current?.click();
                  }
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  if (!busy && !orgPhaseMutationsLocked) setSpocDropHover(true);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!busy && !orgPhaseMutationsLocked) e.dataTransfer.dropEffect = "copy";
                }}
                onDragLeave={() => setSpocDropHover(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setSpocDropHover(false);
                  if (busy || orgPhaseMutationsLocked) return;
                  const f = e.dataTransfer.files?.[0];
                  if (f) ingestSpocCsvFile(f);
                }}
                onClick={() => {
                  if (!busy && !orgPhaseMutationsLocked) spocFileInputRef.current?.click();
                }}
                className={`mt-3 flex w-full flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center outline-none transition-all focus-visible:ring-2 focus-visible:ring-[color:var(--color-accent)] focus-visible:ring-offset-2 ${
                  busy || orgPhaseMutationsLocked
                    ? "cursor-not-allowed border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] opacity-60"
                    : spocDropHover
                      ? "cursor-pointer border-[color:var(--color-ok-fg)] bg-[color:var(--color-ok-bg)] shadow-inner"
                      : "cursor-pointer border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] hover:border-[color:var(--color-border-strong)] hover:bg-[color:var(--color-bg-alt)]"
                }`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]">
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                    />
                  </svg>
                </span>
                <span>
                  <span className="text-sm font-bold text-[color:var(--color-text-hero)]">Drop your file here</span>
                  <span className="mt-1 block text-xs font-medium text-[color:var(--color-text-muted)]">or click to browse · .csv</span>
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-6 rounded-xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-4 py-3 text-sm font-semibold text-[color:var(--color-ok-fg)]">
              File sent for review
              {wiz.delegateListSentForReviewAt && (
                <span className="mt-1 block text-xs font-medium text-[color:var(--color-ok-fg)]">
                  {formatDdMmYyyyTime(wiz.delegateListSentForReviewAt)}
                </span>
              )}
            </div>
          )}

          {spocActiveCsvText ? (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="shrink-0 rounded-lg bg-[color:var(--color-bg-white)] px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] shadow-sm">
                  Active for advisor
                </span>
                <span className="min-w-0 truncate text-sm font-bold text-[color:var(--color-text-hero)]">{wiz.delegateListFileName || "personnel.csv"}</span>
                {wiz.delegateListUploadedAt && (
                  <span className="text-xs font-medium text-[color:var(--color-text-muted)]">
                    · Saved {formatDdMmYyyyTime(wiz.delegateListUploadedAt)}
                  </span>
                )}
              </div>
              {!spocCsvLocked && (
                <Popconfirm
                  title="Remove selected version?"
                  description="Removes the active CSV version from the list. You can upload again or select another version if any remain."
                  okText="Remove"
                  cancelText="Cancel"
                  okButtonProps={{ className: "font-semibold" }}
                  onConfirm={() => void onSpocClearDelegateCsv()}
                  disabled={busy || orgPhaseMutationsLocked}
                >
                  <button
                    type="button"
                    disabled={busy || orgPhaseMutationsLocked}
                    className="shrink-0 rounded-lg border border-[color:var(--color-danger-line)] bg-[color:var(--color-bg-white)] px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-danger-fg)] transition-colors hover:bg-[color:var(--color-danger-bg)] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Remove file
                  </button>
                </Popconfirm>
              )}
            </div>
          ) : null}

          {spocCsvPreview && !spocCsvPreview.ok && (
            <div className="mt-5 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
              <span className="font-black uppercase tracking-wide text-[color:var(--color-warn-fg)]">Preview issue - </span>
              {spocCsvPreview.error}
            </div>
          )}

          {spocCsvPreview?.ok && (
            <div className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Personnel preview</p>
                <p className="text-xs font-bold text-[color:var(--color-text-muted-dark)]">
                  {spocCsvPreview.summary.totalPeople} people · {spocCsvPreview.summary.departments} departments
                </p>
              </div>
              <div className="max-h-[min(420px,55vh)] overflow-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead className="sticky top-0 z-10 bg-[color:var(--color-accent-fill)] text-[8px] font-black uppercase tracking-[0.15em] text-white/80">
                    <tr>
                      <th className="px-4 py-3">#</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Designation</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Department</th>
                      <th className="px-4 py-3">Reports to</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--color-border)]">
                    {spocCsvPreview.rows.map((row, i) => (
                      <tr key={i} className="font-medium text-[color:var(--color-text-hero)]">
                        <td className="px-4 py-2.5 text-[color:var(--color-text-muted)]">{i + 1}</td>
                        <td className="px-4 py-2.5">{row.name}</td>
                        <td className="px-4 py-2.5">{row.designation}</td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                          >
                            {roleLabel(row.roleNormalized)}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">{row.department || "-"}</td>
                        <td className="px-4 py-2.5 text-[color:var(--color-text-muted-dark)]">{row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={busy || orgPhaseMutationsLocked || !spocActiveCsvText || spocCsvLocked || (spocCsvPreview && !spocCsvPreview.ok)}
              onClick={() => void onSpocSendForReview()}
              className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
            >
              Send for review
            </button>
          </div>
        </div>
      )}

      {/* —— SPOC: Review & approve org chart —— */}
      {isSpoc && activeTab.key === "review-org-chart" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>

            {!wiz.orgChartBuiltAt || !Array.isArray(wiz.orgChartRows) || wiz.orgChartRows.length === 0 ? (
              <p className="mt-8 text-sm font-medium text-[color:var(--color-text-muted-dark)]">Nothing to review yet. The advisor will build the org chart after you send personnel details.</p>
            ) : orgChartReviewEffective === "pending" ? (
              <>
                <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                  {[
                    ["Total people", liveOrgSummary.totalPeople],
                    ["Departments", liveOrgSummary.departments],
                    ["Top level", liveOrgSummary.topLevel],
                    ["Reporting lines", liveOrgSummary.reportingLines],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-5 shadow-sm">
                      <p className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{label}</p>
                      <p className="mt-2 text-2xl font-black text-[color:var(--color-text-hero)]">{val}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                  <h4 className="text-xs font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Organizational chart</h4>
                  <div className="mt-6 min-h-[120px] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                    <OrgChartCanvas forest={orgForest} readOnly disabled={busy || orgPhaseMutationsLocked} />
                  </div>
                </div>
                <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                  <h4 className="text-xs font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Personnel directory</h4>
                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-border)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                          <th className="pb-3 pr-3">#</th>
                          <th className="pb-3 pr-3">Name</th>
                          <th className="pb-3 pr-3">Designation</th>
                          <th className="pb-3 pr-3">Role</th>
                          <th className="pb-3 pr-3">Department</th>
                          <th className="pb-3 pr-3">Reports to</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[color:var(--color-border)]">
                        {normalizedOrgRows.map((row, i) => (
                          <tr key={row.id} className="font-medium text-[color:var(--color-text-hero)]">
                            <td className="py-3 pr-3 text-[color:var(--color-text-muted)]">{i + 1}</td>
                            <td className="py-3 pr-3">{row.name}</td>
                            <td className="py-3 pr-3">{row.designation}</td>
                            <td className="py-3 pr-3">
                              <span
                                className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                              >
                                {roleLabel(row.roleNormalized)}
                              </span>
                            </td>
                            <td className="py-3 pr-3">{row.department || "-"}</td>
                            <td className="py-3 pr-3 text-[color:var(--color-text-muted-dark)]">{row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    disabled={busy || orgPhaseMutationsLocked}
                    onClick={() => void onSpocRequestOrgChartChanges()}
                    className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50"
                  >
                    Request changes
                  </button>
                  <button
                    type="button"
                    disabled={busy || orgPhaseMutationsLocked}
                    onClick={() => void onSpocApproveOrgChart()}
                    className="rounded-xl bg-[color:var(--color-ok-bg)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-md transition-colors hover:opacity-80 disabled:opacity-50"
                  >
                    Approve
                  </button>
                </div>
              </>
            ) : orgChartReviewEffective === "approved" ? (
              <>
                {wiz.orgChartSpocApprovedAt && (
                  <p className="mt-6 text-xs font-semibold text-[color:var(--color-ok-fg)]">
                    Approved {formatDdMmYyyyTime(wiz.orgChartSpocApprovedAt)}
                  </p>
                )}
                <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
                  {[
                    ["Total people", liveOrgSummary.totalPeople],
                    ["Departments", liveOrgSummary.departments],
                    ["Top level", liveOrgSummary.topLevel],
                    ["Reporting lines", liveOrgSummary.reportingLines],
                  ].map(([label, val]) => (
                    <div key={label} className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-5 shadow-sm">
                      <p className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{label}</p>
                      <p className="mt-2 text-2xl font-black text-[color:var(--color-text-hero)]">{val}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-8 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                  <h4 className="text-xs font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Organizational chart</h4>
                  <div className="mt-6 min-h-[120px] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                    <OrgChartCanvas forest={orgForest} readOnly disabled={busy || orgPhaseMutationsLocked} />
                  </div>
                </div>
                <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                  <h4 className="text-xs font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Personnel directory</h4>
                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full min-w-[640px] text-left text-sm">
                      <thead>
                        <tr className="border-b border-[color:var(--color-border)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                          <th className="pb-3 pr-3">#</th>
                          <th className="pb-3 pr-3">Name</th>
                          <th className="pb-3 pr-3">Designation</th>
                          <th className="pb-3 pr-3">Role</th>
                          <th className="pb-3 pr-3">Department</th>
                          <th className="pb-3 pr-3">Reports to</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[color:var(--color-border)]">
                        {normalizedOrgRows.map((row, i) => (
                          <tr key={row.id} className="font-medium text-[color:var(--color-text-hero)]">
                            <td className="py-3 pr-3 text-[color:var(--color-text-muted)]">{i + 1}</td>
                            <td className="py-3 pr-3">{row.name}</td>
                            <td className="py-3 pr-3">{row.designation}</td>
                            <td className="py-3 pr-3">
                              <span
                                className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                              >
                                {roleLabel(row.roleNormalized)}
                              </span>
                            </td>
                            <td className="py-3 pr-3">{row.department || "-"}</td>
                            <td className="py-3 pr-3 text-[color:var(--color-text-muted-dark)]">{row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            ) : (
              <p className="mt-8 text-sm font-medium text-[color:var(--color-text-muted-dark)]">
                There is no org chart waiting for your review. When the advisor sends it for review, it will appear here.
              </p>
            )}
          </div>
        </div>
      )}

      {/* —— SPOC: Review & approve documents (branded artefacts) —— */}
      {isSpoc && activeTab.key === "review-documents" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>
            {companyArtefactsDocReviewEffective === "pending" ? (
              <p className="mt-2 text-xs font-semibold text-[color:var(--color-warn-fg)]">Awaiting your decision.</p>
            ) : companyArtefactsDocReviewEffective === "approved" ? (
              <p className="mt-2 text-xs font-semibold text-[color:var(--color-ok-fg)]">
                Approved
                {wiz.companyArtefactsDocSpocApprovedAt
                  ? ` ${formatDdMmYyyyTime(wiz.companyArtefactsDocSpocApprovedAt)}`
                  : ""}
                .
                {companyArtefactsReleased
                  ? wiz.companyArtefactsReleasedToDelegatesAt
                    ? ` Released to delegates ${formatDdMmYyyyTime(wiz.companyArtefactsReleasedToDelegatesAt)}.`
                    : " Released to delegates."
                  : " Release to assigned delegates is still pending - use the button below if needed."}
              </p>
            ) : (
              <p className="mt-2 text-xs font-medium text-[color:var(--color-text-muted-dark)]">
                When the advisor sends the branded document package, it will appear here for review.
              </p>
            )}
            {(() => {
              const brandedRows = (artefactIndexRows || []).filter((r) => brandedBlobUrlByRowId[String(r.id)]);
              if (brandedRows.length === 0) {
                return (
                  <p className="mt-6 text-sm font-medium text-[color:var(--color-text-muted-dark)]">No branded templates on record yet.</p>
                );
              }
              return (
                <>
                  <p className="mt-4 text-xs font-medium text-[color:var(--color-text-muted)]">
                    Review each document below. Use <strong>Open</strong> to preview, or <strong>Compare</strong> for
                    original vs branded side by side.
                  </p>
                  <ArtefactTemplatesTable
                    rows={brandedRows}
                    getApiToken={getApiToken}
                    busy={busy}
                    showAssign={false}
                    showCompare
                    sourceBlobUrlByRowId={sourceBlobUrlByRowId}
                    brandedBlobUrlByRowId={brandedBlobUrlByRowId}
                    preferBrandedOpen
                    onCompareClick={(row) => {
                      setCompareHideDownloads(true);
                      setCompareRow(row);
                    }}
                    compareDisabled={busy}
                  />
                  {companyArtefactsDocReviewEffective === "pending" ? (
                    <div className="mt-8 flex flex-wrap gap-3">
                      <button
                        type="button"
                        disabled={busy || orgPhaseMutationsLocked}
                        onClick={() => void onSpocRequestCompanyArtefactsDocChanges()}
                        className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50"
                      >
                        Request changes
                      </button>
                      <button
                        type="button"
                        disabled={busy || orgPhaseMutationsLocked}
                        onClick={() => void onSpocApproveAndReleaseCompanyArtefactsDoc()}
                        className="rounded-xl bg-[color:var(--color-ok-bg)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-md transition-colors hover:opacity-80 disabled:opacity-50"
                      >
                        Approve and release to assigned delegates
                      </button>
                    </div>
                  ) : companyArtefactsDocReviewEffective === "approved" && !companyArtefactsReleased ? (
                    <div className="mt-8 flex flex-wrap gap-3">
                      <button
                        type="button"
                        disabled={busy || orgPhaseMutationsLocked}
                        onClick={() => void onSpocReleaseCompanyArtefactsToDelegatesLegacy()}
                        className="rounded-xl bg-[color:var(--color-ok-bg)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-md transition-colors hover:opacity-80 disabled:opacity-50"
                      >
                        Release to assigned delegates
                      </button>
                    </div>
                  ) : null}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* —— SPOC: Compliance register & owner outreach —— */}
      {isSpoc && activeTab.key === "spoc-compliance-register" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>

            {!complianceRegisterPackage?.rows?.length ? (
              <p className="mt-4 text-sm font-medium text-[color:var(--color-text-muted-dark)]">No compliance register on file yet.</p>
            ) : complianceRegisterPackage.workflow !== "sent_to_spoc" ? (
              <p className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                The advisor has not marked this package as sent to you yet. When they do, you can send owner emails from
                this tab.
              </p>
            ) : (
              <p className="mt-2 text-xs font-semibold text-[color:var(--color-ok-fg)]">
                Advisor handoff
                {complianceRegisterPackage.spocPackageSentAt
                  ? ` - ${formatDdMmYyyyTime(complianceRegisterPackage.spocPackageSentAt)}`
                  : ""}
              </p>
            )}

            {complianceRegisterPackage?.rows?.length ? (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  disabled={
                    busy ||
                    orgPhaseMutationsLocked ||
                    complianceRegisterPackage.workflow !== "sent_to_spoc" ||
                    spocComplianceBulkEligibleRefIds.length === 0
                  }
                  onClick={() =>
                    Modal.confirm({
                      title: "Send all rows with recipient and draft?",
                      content: (
                        <div className="space-y-2 text-sm text-[color:var(--color-text-hero)]">
                          <p>
                            Sends <strong>{spocComplianceBulkEligibleRefIds.length}</strong> message(s) sequentially from your
                            platform SMTP mailbox. Rows without a draft or valid recipient stay skipped.
                          </p>
                          <p className="text-[color:var(--color-warn-fg)]">
                            Delivery depends on SMTP configuration and quotas; recipients may appear as From your PolicyLens
                            sender address.
                          </p>
                        </div>
                      ),
                      okText: "Send",
                      cancelText: "Cancel",
                      okButtonProps: { disabled: busy },
                      onOk: () => onSpocComplianceBulkSend(),
                    })
                  }
                  className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                >
                  Send all with recipient
                </button>
                {spocComplianceBulkEligibleRefIds.length === 0 && complianceRegisterPackage.workflow === "sent_to_spoc" ? (
                  <span className="text-xs font-medium text-[color:var(--color-text-muted)]">
                    Add drafts and valid owner emails per row (or expand a row below) before bulk send.
                  </span>
                ) : null}
              </div>
            ) : null}
            <ComplianceEmailPlaceholderWarning items={placeholderEmails} />

            {Array.isArray(complianceRegisterPackage?.rows) && complianceRegisterPackage.rows.length > 0 ? (
              <div className="mt-8 overflow-x-auto rounded-xl border border-[color:var(--color-border)]">
                <table className="w-full min-w-[1180px] text-left text-xs">
                  <thead className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    <tr>
                      <th className="px-3 py-2">Ref</th>
                      <th className="px-3 py-2">Regulation</th>
                      <th className="px-3 py-2">Clause</th>
                      <th className="px-3 py-2">Requirement</th>
                      <th className="px-3 py-2">Applicability</th>
                      <th className="px-3 py-2">Country</th>
                      <th className="px-3 py-2">Owner</th>
                      <th className="max-w-[140px] px-3 py-2">Recipient</th>
                      <th className="px-3 py-2">Frequency</th>
                      <th className="px-3 py-2">ISO ref</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--color-border)] text-[13px]">
                    {complianceRegisterPackage.rows.map((r) => {
                      const draft = complianceRegisterPackage.emailsByRefId?.[r.refId];
                      const open = spocComplianceOpenRefId === r.refId;
                      const recipient = (r.recipientEmail || "").trim();
                      return (
                        <React.Fragment key={r.refId}>
                          <tr
                            className="cursor-pointer bg-[color:var(--color-bg-white)] hover:bg-[color:var(--color-bg-alt)]"
                            onClick={() => setSpocComplianceOpenRefId(open ? null : r.refId)}
                          >
                            <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] font-bold text-[color:var(--color-accent)]">
                              {r.refId}
                            </td>
                            <td className="max-w-[140px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.regulationStatute}</td>
                            <td className="max-w-[100px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.clauseSection}</td>
                            <td className="max-w-[220px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.requirementDescription}</td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.applicability}</td>
                            <td className="max-w-[100px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.originatingCountry}</td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-hero)]">{(typeof r.responsibleOwner === "string" && r.responsibleOwner.trim()) || <span className="italic text-[color:var(--color-warn-fg)]">{OWNER_NOT_ASSIGNED}</span>}</td>
                            <td
                              className="max-w-[160px] truncate px-3 py-2 text-xs text-[color:var(--color-text-hero)]"
                              title={recipient || undefined}
                            >
                              {recipient ? (
                                <span className="font-medium text-[color:var(--color-ok-fg)]">{recipient}</span>
                              ) : (
                                <span className="text-[color:var(--color-text-muted)]">-</span>
                              )}
                            </td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.frequencyDue}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.isoControlRef}</td>
                          </tr>
                          {open ? (
                            <tr className="bg-[color:var(--color-surface-sunken)]">
                              <td colSpan={10} className="px-4 py-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Recipient email
                                    </p>
                                    <input
                                      type="email"
                                      className="mt-2 w-full max-w-md rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                                      placeholder={
                                        spocRecipientDraft.trim()
                                          ? ""
                                          : "Advisor maps this on Generate owner emails (Recipient column)"
                                      }
                                      value={spocRecipientDraft}
                                      disabled={orgPhaseMutationsLocked}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => setSpocRecipientDraft(e.target.value)}
                                      onBlur={async () => {
                                        const v = spocRecipientDraft.trim();
                                        const pkg = complianceRegisterPackage;
                                        if (!pkg?.rows) return;
                                        const rows = pkg.rows.map((x) =>
                                          x.refId === r.refId ? { ...x, recipientEmail: v } : x,
                                        );
                                        await persistComplianceRegisterPackage({ ...pkg, rows });
                                      }}
                                    />
                                    <p className="mt-1 text-[11px] text-[color:var(--color-text-muted)]">
                                      Populated from advisor mapping (see Recipient column). Saved on blur for platform
                                      send.
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap gap-2 md:items-end md:justify-end">
                                    {draft?.bodyPlain ? (
                                      <button
                                        type="button"
                                        className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm hover:bg-[color:var(--color-bg-alt)] disabled:opacity-50"
                                        disabled={busy || orgPhaseMutationsLocked}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const t = `${draft.subject || ""}\n\n${draft.bodyPlain || ""}`;
                                          void navigator.clipboard.writeText(t);
                                          message.success("Copied to clipboard.");
                                        }}
                                      >
                                        Copy email
                                      </button>
                                    ) : (
                                      <span className="text-xs text-[color:var(--color-warn-fg)]">No draft for this row.</span>
                                    )}
                                    <button
                                      type="button"
                                      disabled={
                                        busy ||
                                        orgPhaseMutationsLocked ||
                                        !(draft?.bodyPlain && findEmailPlaceholders(draft).length === 0 && isComplianceRegisterRecipientEmail(spocRecipientDraft.trim()))
                                      }
                                      title={draft && findEmailPlaceholders(draft).length ? `Remove placeholders first: ${findEmailPlaceholders(draft).join(", ")}` : undefined}
                                      className="rounded-lg bg-[color:var(--color-accent-fill)] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-white shadow-sm hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const to = spocRecipientDraft.trim();
                                        void onSpocComplianceSendEmail(
                                          r.refId,
                                          isComplianceRegisterRecipientEmail(to) ? to : undefined,
                                        );
                                      }}
                                    >
                                      Send via platform
                                    </button>
                                  </div>
                                </div>
                                {draft?.bodyPlain ? (
                                  <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-4">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Subject
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-[color:var(--color-text-hero)]">{draft.subject}</p>
                                    <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Body
                                    </p>
                                    <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-sans text-sm leading-relaxed text-[color:var(--color-text-hero)]">
                                      {draft.bodyPlain}
                                    </pre>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      )}

      {/* —— SPOC: Meetings — same meeting-request UX as Gap Analysis (scoped to org-setup meetings only) —— */}
      {isSpoc && activeTab.key === "spoc-meetings" && (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-[2rem] border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-2xl">
            <div className="border-b border-[color:var(--color-border)] px-8 py-6">
              <h4 className="text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]">Meeting requests</h4>
              <p className="mt-1 text-[10px] font-medium text-[color:var(--color-text-muted)]">
                Organizational setup workshops booked by your advisor appear here (not Gap Analysis interview requests).
                {spocMeetingsAwaitingCount > 0
                  ? " Add delegates so they are notified and each workshop moves to Scheduled."
                  : " When nothing is awaiting your action, use the Meetings page to see every workshop across steps."}
              </p>
              {spocMeetingsAwaitingCount > 0 ? (
                <div className="mt-4 rounded-xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-4 py-3 text-sm font-semibold text-[color:var(--color-accent)]">
                  <span className="font-black">{spocMeetingsAwaitingCount}</span>{" "}
                  {spocMeetingsAwaitingCount === 1 ? "meeting needs" : "meetings need"} delegate assignment before the
                  workshop is fully scheduled.
                </div>
              ) : (
                <p className="mt-4 text-[10px] font-medium text-[color:var(--color-text-muted)]">
                  When your advisor requests a workshop without naming attendees, assign company delegates below. Invited
                  users receive an in-app notification.
                </p>
              )}
            </div>
            <div className="space-y-8 p-8">
              {spocOsMeetLoading ? (
                <div className="py-10 text-center">
                  <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent" />
                  <p className="mt-3 text-xs font-medium text-[color:var(--color-text-muted)]">Loading meetings...</p>
                </div>
              ) : spocOsMeetRaw.length === 0 ? (
                <p className="text-sm font-medium text-[color:var(--color-text-muted-dark)]">
                  No organizational setup meetings on file for this engagement yet.
                </p>
              ) : (
                <>
                  {spocOrgMeetAwaiting.length > 0 ? (
                    <div className="space-y-4">
                      <h5 className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]">
                        Awaiting your action
                      </h5>
                      {spocOrgMeetAwaiting.map((m) => {
                        const pickIds = spocOsDelegatePicksByMeeting[m.id] || [];
                        const saving = spocOsSavingMeetingId === m.id;
                        const assignDisabled = orgPhaseMutationsLocked || busy;
                        return (
                          <div
                            key={m.id}
                            className="overflow-hidden rounded-2xl border border-[color:var(--color-info-line)] bg-gradient-to-br from-[color:var(--color-accent-tint)] to-[color:var(--color-bg-white)] shadow-sm"
                          >
                            <div className="border-b border-[color:var(--color-info-line)] p-4 md:p-5">
                              <span className="block text-xs font-bold text-[color:var(--color-text-hero)]">{m.title}</span>
                              <span className="mt-0.5 block text-[10px] text-[color:var(--color-text-muted)]">
                                {formatSpocMeetingDate(m.meetingDate)} •{" "}
                                {SPOC_MEETING_TYPE_LABELS[m.meetingType] || m.meetingType}
                                {m.department ? ` • Dept: ${m.department}` : ""}
                              </span>
                              {m.notes ? (
                                <p className="mt-2 line-clamp-2 text-[10px] text-[color:var(--color-text-muted)]">{m.notes}</p>
                              ) : null}
                              {m.meetingLocation || m.meetingLink ? (
                                <div className="mt-2 flex flex-wrap gap-3 text-[10px] text-[color:var(--color-text-muted)]">
                                  {m.meetingLocation ? (
                                    <span className="inline-flex items-center gap-1">📍 {m.meetingLocation}</span>
                                  ) : null}
                                  {m.meetingLink ? (
                                    <a
                                      href={m.meetingLink}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="font-semibold text-[color:var(--color-accent)] hover:underline"
                                    >
                                      Join link
                                    </a>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                            <div className="bg-[color:var(--color-bg-white)] px-4 py-4 md:px-5 md:py-5">
                              <p className="mb-0.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-accent)]">
                                Assign attendees
                              </p>
                              <p className="mb-3 text-[10px] text-[color:var(--color-text-muted)]">
                                Select project members below. They receive an in-app notification when you confirm.
                              </p>
                              {spocOrgRosterLoading ? (
                                <p className="mb-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-alt)] px-3 py-2 text-[10px] text-[color:var(--color-text-muted-dark)]">
                                  Loading project roster…
                                </p>
                              ) : null}
                              {!spocOrgRosterLoading && spocOrgTabProjectMembers.length === 0 ? (
                                <p className="mb-2 rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-[10px] text-[color:var(--color-text-muted)]">
                                  No members on this project yet.
                                </p>
                              ) : null}
                              <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-2">
                                {spocOrgTabProjectMembers.map((mem) => {
                                  const selected = pickIds.includes(mem.id);
                                  return (
                                    <button
                                      key={mem.id}
                                      type="button"
                                      disabled={assignDisabled}
                                      onClick={() => !assignDisabled && toggleSpocOsMeetingDelegatePick(m.id, mem.id)}
                                      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-all ${
                                        selected
                                          ? "border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] shadow-sm"
                                          : "border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] hover:border-[color:var(--color-border)] hover:bg-[color:var(--color-bg-white)]"
                                      } disabled:cursor-not-allowed disabled:opacity-50`}
                                    >
                                      <span
                                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-[10px] font-black ${
                                          selected ? "bg-[color:var(--color-accent-fill)] text-white" : "bg-[color:var(--color-bg-alt)] text-white/80"
                                        }`}
                                      >
                                        {selected ? "✓" : ""}
                                      </span>
                                      <div className="min-w-0 flex-1">
                                        <div className="truncate text-xs font-bold text-[color:var(--color-text-hero)]">
                                          {mem.name || mem.email}
                                        </div>
                                        <div className="truncate text-[10px] text-[color:var(--color-text-muted)]">{mem.email}</div>
                                      </div>
                                      <span className="max-w-[100px] shrink-0 truncate text-[8px] font-bold uppercase text-[color:var(--color-text-muted)]">
                                        {(mem.role || "").replace(/_/g, " ")}
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                              <div className="mt-4 flex flex-col gap-3 border-t border-[color:var(--color-border)] pt-3 sm:flex-row sm:items-center sm:justify-between">
                                <span className="text-[10px] font-medium text-[color:var(--color-text-muted)]">
                                  {pickIds.length} attendee{pickIds.length !== 1 ? "s" : ""} selected
                                </span>
                                <button
                                  type="button"
                                  disabled={
                                    pickIds.length === 0 || saving || spocOrgRosterLoading || assignDisabled
                                  }
                                  onClick={() => void confirmSpocOsDelegatesForMeeting(m.id, pickIds)}
                                  className={`w-full rounded-xl px-5 py-2.5 text-[10px] font-black uppercase tracking-widest shadow-sm transition-all sm:w-auto ${
                                    pickIds.length === 0 || saving || spocOrgRosterLoading || assignDisabled
                                      ? "cursor-not-allowed bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]"
                                      : "bg-[color:var(--color-accent-fill)] text-white hover:opacity-90"
                                  }`}
                                >
                                  {saving ? "Saving…" : "Confirm & notify attendees"}
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                  <div
                    className={
                      spocOrgMeetAwaiting.length > 0
                        ? "space-y-4 border-t border-[color:var(--color-border)] pt-8"
                        : "space-y-4"
                    }
                  >
                    <h5 className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Scheduled</h5>
                    {spocOrgMeetScheduled.length === 0 ? (
                      <p className="text-xs text-[color:var(--color-text-muted)]">No scheduled organizational setup meetings yet.</p>
                    ) : (
                      spocOrgMeetScheduled.map((m) => {
                        const att = normalizeMeetingAttendees(m.attendees);
                        return (
                          <div key={m.id} className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                            <span className="block text-xs font-bold text-[color:var(--color-text-hero)]">{m.title}</span>
                            <span className="text-[10px] text-[color:var(--color-text-muted)]">
                              {formatSpocMeetingDate(m.meetingDate)} •{" "}
                              {SPOC_MEETING_TYPE_LABELS[m.meetingType] || m.meetingType}
                              {m.department ? ` • Dept: ${m.department}` : ""}
                            </span>
                            {att.length > 0 ? (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                <span className="mr-1 text-[9px] font-bold uppercase text-[color:var(--color-text-muted)]">Attendees:</span>
                                {att.map((a, i) => (
                                  <span
                                    key={a.userId || i}
                                    className="inline-flex items-center gap-1 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-2 py-0.5 text-[9px] text-[color:var(--color-text-muted-dark)]"
                                  >
                                    {(a.name || "?").charAt(0).toUpperCase()} {a.name}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                            {m.meetingLink ? (
                              <a
                                href={m.meetingLink}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-block text-[10px] font-semibold text-[color:var(--color-accent)] hover:underline"
                              >
                                Join link
                              </a>
                            ) : null}
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* —— SPOC: other tabs (placeholders) —— */}
      {isSpoc &&
        activeTab.key !== "organizational-details" &&
        activeTab.key !== "review-org-chart" &&
        activeTab.key !== "review-documents" &&
        activeTab.key !== "spoc-compliance-register" &&
        activeTab.key !== "spoc-meetings" &&
        "actions" in activeTab &&
        activeTab.actions === "dual" && (
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              disabled={orgPhaseMutationsLocked}
              className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-40"
            >
              Request changes
            </button>
            <button
              type="button"
              disabled={orgPhaseMutationsLocked}
              className="rounded-xl bg-[color:var(--color-ok-bg)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-md transition-colors hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Approve
            </button>
          </div>
        </div>
      )}

      {/* —— Advisory: Create company artefacts (server branding + assign) —— */}
      {isAdvisory && !isLeadAdvisor && activeTab.key === "artefacts" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>

            <input
              ref={companyArtefactFileInputRef}
              type="file"
              accept=".doc,.docx,.pdf,.ppt,.pptx"
              className="hidden"
              onChange={handleCompanyArtefactFileSelected}
            />

            {!wiz.companyArtefactsBrandingAppliedAt ? (
              <>
                <p className="mt-4 text-xs font-medium text-[color:var(--color-text-muted-dark)]">
                  Applies your engagement logo, primary color, and font to each template (DOCX / PPTX / PDF where
                  supported), saves branded copies in secure storage, then you can assign owners. Legacy .doc / .ppt files
                  are left unchanged.
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  <div className="flex min-w-[120px] flex-col gap-1">
                    <span className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Logo</span>
                    {effectiveBranding.logo && isProbablyUrl(effectiveBranding.logo) ? (
                      <img
                        src={brandingLogoResolvedSrc || effectiveBranding.logo}
                        alt=""
                        className="h-10 max-w-[140px] object-contain"
                      />
                    ) : (
                      <span className="text-xs text-[color:var(--color-text-muted)]">None</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Primary</span>
                    <span
                      className="h-8 w-16 rounded-lg border border-[color:var(--color-border)] shadow-inner"
                      style={{
                        backgroundColor:
                          (paletteParsed.primary && /^#?[0-9a-fA-F]{3,8}$/.test(String(paletteParsed.primary).replace(/^#/, ""))
                            ? String(paletteParsed.primary).startsWith("#")
                              ? paletteParsed.primary
                              : `#${paletteParsed.primary}`
                            : null) || "#1e293b",
                      }}
                      title={paletteParsed.primary || ""}
                    />
                  </div>
                  <div className="flex min-w-[140px] flex-col gap-1">
                    <span className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Font</span>
                    <span className="text-xs font-semibold text-[color:var(--color-text-hero)]">{effectiveBranding.font || "-"}</span>
                  </div>
                  <div className="text-xs font-medium text-[color:var(--color-text-muted)]">
                    {artefactIndexRows.length} template{artefactIndexRows.length === 1 ? "" : "s"} from onboarding
                  </div>
                </div>
                {!wiz.brandingImportedAt &&
                  !effectiveBranding.logo?.trim() &&
                  !effectiveBranding.palette?.trim() &&
                  !effectiveBranding.font?.trim() && (
                    <div className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                      Import client branding first so the server has logo, palette, and font to apply.
                    </div>
                  )}
                {artefactIndexRows.length === 0 ? (
                  <div className="mt-6 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                    <p>
                      No artefact templates yet. Complete <strong>Project onboarding</strong> (Artefact Templates), or
                      upload one directly here.
                    </p>
                    <button
                      type="button"
                      disabled={companyArtefactUploadBusy || orgPhaseMutationsLocked}
                      onClick={() => companyArtefactFileInputRef.current?.click()}
                      className="mt-3 rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                    >
                      {companyArtefactUploadBusy ? "Uploading…" : "Upload artefact template"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      disabled={busy || orgPhaseMutationsLocked}
                      onClick={() => void runArtefactBrandingApply()}
                      className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                    >
                      {busy ? "Applying branding…" : activeTab.cta}
                    </button>
                    <button
                      type="button"
                      disabled={companyArtefactUploadBusy || orgPhaseMutationsLocked}
                      onClick={() => companyArtefactFileInputRef.current?.click()}
                      className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {companyArtefactUploadBusy ? "Uploading…" : "Upload another template"}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                {brandingSnapshotStale ? (
                  <div className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                    Engagement branding changed since the last run. Re-apply so templates match the current logo, palette,
                    and font.
                    <button
                      type="button"
                      disabled={busy || orgPhaseMutationsLocked}
                      onClick={() => void runArtefactBrandingApply()}
                      className="ml-3 rounded-lg bg-[color:var(--color-warn-bg)] px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)] hover:opacity-80 disabled:opacity-50"
                    >
                      Re-apply
                    </button>
                  </div>
                ) : null}
                <p className="mt-4 text-xs font-bold text-[color:var(--color-ok-fg)]">
                  Last branding run: {formatDdMmYyyyTime(wiz.companyArtefactsBrandingAppliedAt)}
                </p>
                {Object.keys(brandedBlobUrlByRowId).length > 0 ? (
                  <div className="mt-4 flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-4 sm:flex-row sm:flex-wrap sm:items-center">
                    <div className="flex flex-col gap-1">
                      <span className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                        Document package (SPOC)
                      </span>
                      <span className="text-xs font-bold text-[color:var(--color-text-hero)]">
                        {companyArtefactsDocReviewEffective === "pending"
                          ? "With company SPOC for review"
                          : companyArtefactsDocReviewEffective === "approved"
                            ? companyArtefactsReleased
                              ? "Released to delegates"
                              : "SPOC approved - release to delegates when ready"
                            : "Draft - assign delegates, then send for review"}
                      </span>
                    </div>
                    {companyArtefactsDocReviewEffective === "draft" ? (
                      canSendCompanyArtefactsDocToSpoc ? (
                        <button
                          type="button"
                          disabled={busy || orgPhaseMutationsLocked}
                          onClick={() => void onSendCompanyArtefactsDocToSpoc()}
                          className="rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)] sm:ml-auto"
                        >
                          Send to SPOC for document review
                        </button>
                      ) : (
                        <p className="max-w-md text-xs font-medium text-[color:var(--color-text-muted-dark)] sm:ml-auto sm:text-right">
                          Assign every branded document to a company delegate, then reconfirm each{" "}
                          <strong>Assign</strong> selection so delegate accounts are linked - then you can send for SPOC
                          review.
                        </p>
                      )
                    ) : null}
                  </div>
                ) : null}
                {Object.keys(brandedBlobUrlByRowId).length === 0 ? (
                  <div className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                    No branded files on record. Run the pipeline to generate branded templates before assigning.
                    <div className="mt-3">
                      <button
                        type="button"
                        disabled={busy || orgPhaseMutationsLocked}
                        onClick={() => void runArtefactBrandingApply()}
                        className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                      >
                        {busy ? "Applying…" : "Apply branding to templates"}
                      </button>
                    </div>
                  </div>
                ) : null}
                {artefactIndexRows.length === 0 ? (
                  <div className="mt-6 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                    <p>
                      No artefact templates found. Complete <strong>Project onboarding</strong> (Artefact Templates) -
                      rows appear here automatically - or upload one directly here.
                    </p>
                    <button
                      type="button"
                      disabled={companyArtefactUploadBusy || orgPhaseMutationsLocked}
                      onClick={() => companyArtefactFileInputRef.current?.click()}
                      className="mt-3 rounded-xl bg-[color:var(--color-accent-fill)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                    >
                      {companyArtefactUploadBusy ? "Uploading…" : "Upload artefact template"}
                    </button>
                  </div>
                ) : (
                  <>
                    {!orgPhaseMutationsLocked ? (
                      <div className="mt-4 flex justify-end">
                        <button
                          type="button"
                          disabled={companyArtefactUploadBusy}
                          onClick={() => companyArtefactFileInputRef.current?.click()}
                          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {companyArtefactUploadBusy ? "Uploading…" : "Upload another template"}
                        </button>
                      </div>
                    ) : null}
                    {(() => {
                      const delMap =
                        wiz.companyArtefactAssigneeDelegateMemberIdByRowId &&
                        typeof wiz.companyArtefactAssigneeDelegateMemberIdByRowId === "object"
                          ? wiz.companyArtefactAssigneeDelegateMemberIdByRowId
                          : {};
                      const needsResave = Object.keys(brandedBlobUrlByRowId).some((tid) => {
                        const a = artefactAssigneeByRowId[tid];
                        return a && !delMap[tid];
                      });
                      return needsResave ? (
                        <div className="mt-4 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm font-medium text-[color:var(--color-warn-fg)]">
                          Reconfirm each <strong>Assign</strong> selection so delegate accounts are linked (required
                          before sending the package to the company SPOC).
                        </div>
                      ) : null;
                    })()}
                    {Object.keys(brandedBlobUrlByRowId).length > 0 &&
                    companyArtefactsDocReviewEffective !== "pending" &&
                    !orgPhaseMutationsLocked ? (
                      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3 shadow-sm">
                        {artefactEligibleDelegates.length > 1 ? (
                          <label className="flex min-w-[200px] flex-1 flex-col gap-1 sm:max-w-sm">
                            <span className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                              Delegate for bulk assign
                            </span>
                            <select
                              value={bulkAssignDelegateMemberId}
                              onChange={(e) => setBulkAssignDelegateMemberId(e.target.value)}
                              disabled={busy || artefactEligibleDelegates.length === 0}
                              className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-semibold text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)] disabled:cursor-not-allowed disabled:opacity-50"
                              aria-label="Choose delegate for assign all"
                            >
                              {artefactEligibleDelegates.map(({ member, orgRow }) => (
                                <option key={member.id} value={String(member.id)}>
                                  {displayPerson(member.name, orgRow.designation)}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                        <button
                          type="button"
                          disabled={
                            busy ||
                            artefactEligibleDelegates.length === 0 ||
                            !bulkAssignDelegateMemberId
                          }
                          onClick={() => void onAssignAllArtefactsToDelegate(bulkAssignDelegateMemberId)}
                          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Assign all to delegate
                        </button>
                        {artefactEligibleDelegates.length === 0 ? (
                          <p className="text-xs font-medium text-[color:var(--color-text-muted)]">
                            Invite a <strong>company delegate</strong> on the project and add them to the org chart with
                            role Company Delegate to enable bulk assignment.
                          </p>
                        ) : null}
                      </div>
                    ) : null}
                    <ArtefactTemplatesTable
                      rows={artefactIndexRows}
                      getApiToken={getApiToken}
                      busy={busy}
                      showAssign
                      showCompare
                      sourceBlobUrlByRowId={sourceBlobUrlByRowId}
                      assigneeByRowId={artefactAssigneeByRowId}
                      assignSelectValueByRowId={artefactAssignSelectValueByRowId}
                      orgAssignOptions={artefactDelegateAssignOptions}
                      assignEmptyHint="- No company delegates on this project - invite them in Project team -"
                      onAssignChange={onArtefactAssigneeChange}
                      assignDisabled={orgPhaseMutationsLocked || companyArtefactsDocReviewEffective === "pending"}
                      brandedBlobUrlByRowId={brandedBlobUrlByRowId}
                      preferBrandedOpen
                      assignRequiresBranded
                      onCompareClick={(row) => {
                        setCompareHideDownloads(true);
                        setCompareRow(row);
                      }}
                      compareDisabled={busy}
                    />
                  </>
                )}
                {(() => {
                  const nextKey = getNextAdvisorOrgSetupKey("artefacts");
                  const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
                  if (companyArtefactsDocReviewEffective !== "approved") {
                    return (
                      <p className="mt-8 border-t border-[color:var(--color-border)] pt-6 text-xs font-medium text-[color:var(--color-text-muted-dark)]">
                        Continue to <strong>{nextTab?.label ?? "the next step"}</strong> after the company SPOC approves
                        and releases this document package to assigned delegates.
                      </p>
                    );
                  }
                  if (!companyArtefactsReleased) {
                    return (
                      <p className="mt-8 border-t border-[color:var(--color-border)] pt-6 text-xs font-medium text-[color:var(--color-text-muted-dark)]">
                        The company SPOC releases the package to delegates from{" "}
                        <strong>Review &amp; approve documents</strong> after approving.
                      </p>
                    );
                  }
                  if (contentSealedReadOnly) return null;
                  return nextTab ? (
                    <AdvisorOrgSetupContinueFooter
                      nextTabLabel={nextTab.label}
                      disabled={busy || orgPhaseMutationsLocked}
                      onContinue={() => void selectOrgSubtab(nextKey)}
                    />
                  ) : null;
                })()}
              </>
            )}
          </div>
          <Modal
            title="New artefact from upload"
            open={companyArtefactUploadModalOpen}
            onCancel={closeCompanyArtefactUploadModal}
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCompanyArtefactUploadModal}
                  className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted-dark)] transition-all hover:bg-[color:var(--color-bg-alt)]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void confirmCompanyArtefactUploadModal()}
                  disabled={companyArtefactUploadBusy}
                  className="rounded-lg bg-[color:var(--color-accent-fill)] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                >
                  Add artefact
                </button>
              </div>
            }
            destroyOnClose
          >
            <div className="space-y-4 pt-1">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Filename</label>
                <p className="break-all rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-sm text-[color:var(--color-text-hero)]">
                  {pendingCompanyArtefactFileName || "-"}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">Title</label>
                <input
                  value={companyArtefactUploadTitle}
                  onChange={(e) => setCompanyArtefactUploadTitle(e.target.value)}
                  placeholder="Artefact title"
                  className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-[color:var(--color-text-muted)]">
                  Classification (optional)
                </label>
                <input
                  value={companyArtefactUploadClassification}
                  onChange={(e) => setCompanyArtefactUploadClassification(e.target.value)}
                  placeholder="e.g. Confidential"
                  className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                />
              </div>
            </div>
          </Modal>
        </div>
      )}

      {/* —— Advisory: Internal / external context & scope document —— */}
      {isAdvisory &&
        !isLeadAdvisor &&
        activeTab.key === "context" &&
        effectiveContextDocMeta &&
        internalContextModel && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
              <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
              <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>

              <div className="mt-6">
                <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Certification / standard (footer &amp; register)
                </label>
                <input
                  type="text"
                  className="mt-2 w-full max-w-xl rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm text-[color:var(--color-text-hero)]"
                  value={contextCertTarget}
                  placeholder="Not set: enter the standard for this engagement"
                  onChange={(e) => setContextCertTarget(e.target.value)}
                  disabled={orgPhaseMutationsLocked}
                />
                <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted)]">
                  Drives the ISO reference line and compliance register summary. Adjust if the engagement uses a
                  different edition than the project default.
                </p>
              </div>

              {internalContextModel.warnings.length > 0 ? (
                <div className="mt-6 rounded-xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] px-4 py-3 text-sm text-[color:var(--color-warn-fg)]">
                  <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-warn-fg)]">Data completeness</p>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm font-medium">
                    {internalContextModel.warnings.map((w, i) => (
                      <li key={`${i}-${w}`}>{w}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <section className="mt-10 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-6 shadow-sm md:p-8">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Document control (editable)
                </h4>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Company legal name
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm text-[color:var(--color-text-hero)]"
                            value={effectiveContextDocMeta.companyLegalName ?? ""}
                            onChange={(e) => patchContextDocMeta({ companyLegalName: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Document title
                          </label>
                          <select
                            className="mt-2 w-full max-w-xl rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-2 text-sm text-[color:var(--color-text-hero)]"
                            value={effectiveContextDocMeta.documentTitle ?? ""}
                            onChange={(e) => patchContextDocMeta({ documentTitle: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          >
                            {[...new Set([...CONTEXT_DOCUMENT_TITLE_OPTIONS, effectiveContextDocMeta.documentTitle ?? ""])].filter(Boolean).map((opt) => (
                              <option key={opt} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Review frequency
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.reviewFrequency ?? ""}
                            onChange={(e) => patchContextDocMeta({ reviewFrequency: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Classification
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.documentClassification ?? ""}
                            onChange={(e) => patchContextDocMeta({ documentClassification: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Disclosure / copyright
                          </label>
                          <textarea
                            className="mt-2 min-h-[72px] w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.disclosureCopyrightClause ?? ""}
                            onChange={(e) => patchContextDocMeta({ disclosureCopyrightClause: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Version</label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.version ?? ""}
                            onChange={(e) => patchContextDocMeta({ version: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Dated</label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.dated ?? ""}
                            onChange={(e) => patchContextDocMeta({ dated: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Document author
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.documentAuthor ?? ""}
                            onChange={(e) => patchContextDocMeta({ documentAuthor: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Document owner
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.documentOwner ?? ""}
                            onChange={(e) => patchContextDocMeta({ documentOwner: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Distribution
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.distribution ?? ""}
                            onChange={(e) => patchContextDocMeta({ distribution: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Logo note (optional)
                          </label>
                          <input
                            type="text"
                            className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            placeholder="URL or caption if not using profiling logo"
                            value={effectiveContextDocMeta.logoNote ?? ""}
                            onChange={(e) => patchContextDocMeta({ logoNote: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                        <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                              Approval - name
                            </label>
                            <input
                              type="text"
                              className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                              value={effectiveContextDocMeta.approvalName ?? ""}
                              onChange={(e) => patchContextDocMeta({ approvalName: e.target.value })}
                              disabled={orgPhaseMutationsLocked}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                              Approval - position
                            </label>
                            <input
                              type="text"
                              className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                              value={effectiveContextDocMeta.approvalPosition ?? ""}
                              onChange={(e) => patchContextDocMeta({ approvalPosition: e.target.value })}
                              disabled={orgPhaseMutationsLocked}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                              Approval - signature
                            </label>
                            <input
                              type="text"
                              className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                              value={effectiveContextDocMeta.approvalSignature ?? ""}
                              onChange={(e) => patchContextDocMeta({ approvalSignature: e.target.value })}
                              disabled={orgPhaseMutationsLocked}
                            />
                          </div>
                          <div>
                            <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                              Approval - date
                            </label>
                            <input
                              type="text"
                              className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                              value={effectiveContextDocMeta.approvalDate ?? ""}
                              onChange={(e) => patchContextDocMeta({ approvalDate: e.target.value })}
                              disabled={orgPhaseMutationsLocked}
                            />
                          </div>
                        </div>
                        <div className="md:col-span-2">
                          <label className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                            Introduction
                          </label>
                          <textarea
                            className="mt-2 min-h-[96px] w-full rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                            value={effectiveContextDocMeta.introduction ?? ""}
                            onChange={(e) => patchContextDocMeta({ introduction: e.target.value })}
                            disabled={orgPhaseMutationsLocked}
                          />
                        </div>
                </div>
              </section>

              <section className="mt-10 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-6 md:p-8">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">Export preview</h4>
                <p className="mt-1 max-w-2xl text-xs font-medium leading-relaxed text-[color:var(--color-text-muted)]">
                  Structured layout matching the DOCX export. Scroll the page to read the full document-there is no
                  clipped preview pane.
                </p>
                {internalContextPreviewHtml ? (
                  <div className="mt-6 rounded-xl bg-[color:var(--color-bg-white)] px-5 py-6 shadow-sm ring-1 ring-[color:var(--color-border)] md:px-9 md:py-10">
                    <div
                      className="
                        [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:scroll-mt-4 [&_h2]:border-b [&_h2]:border-[color:var(--color-border)] [&_h2]:pb-2 [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-[color:var(--color-text-hero)] [&_h2:first-of-type]:mt-0
                        [&_h3]:mb-2 [&_h3]:mt-8 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:text-[color:var(--color-text-hero)]
                        [&_p]:text-[15px] [&_p]:leading-relaxed [&_p]:text-[color:var(--color-text-muted-dark)]
                        [&_table]:my-5 [&_table]:w-full [&_table]:border-collapse [&_table]:text-[13px] [&_table]:text-[color:var(--color-text-hero)]
                        [&_th]:border [&_th]:border-[color:var(--color-border)] [&_th]:bg-[color:var(--color-surface-sunken)] [&_th]:px-2 [&_th]:py-2.5 [&_th]:text-left [&_th]:font-semibold [&_th]:text-[color:var(--color-text-hero)]
                        [&_td]:border [&_td]:border-[color:var(--color-border)] [&_td]:px-2 [&_td]:py-2.5 [&_td]:align-top
                      "
                      dangerouslySetInnerHTML={{ __html: internalContextPreviewHtml }}
                    />
                  </div>
                ) : (
                  <p className="mt-4 text-sm font-medium text-[color:var(--color-text-muted)]">Preview unavailable.</p>
                )}
              </section>

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  disabled={busy || orgPhaseMutationsLocked}
                  onClick={() => void persistInternalContextDocMeta()}
                  className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Save document control
                </button>
                <button
                  type="button"
                  disabled={busy || orgPhaseMutationsLocked}
                  onClick={() => void onGenerateInternalContextDocx()}
                  className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                >
                  {busy ? "Working…" : "Generate DOCX"}
                </button>
              </div>

              {(() => {
                const nextKey = getNextAdvisorOrgSetupKey("context");
                const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
                if (contentSealedReadOnly || !nextTab) return null;
                return (
                  <AdvisorOrgSetupContinueFooter
                    nextTabLabel={nextTab.label}
                    disabled={busy || orgPhaseMutationsLocked}
                    onContinue={() => void selectOrgSubtab(nextKey)}
                  />
                );
              })()}
            </div>
          </div>
        )}

      {/* —— Advisory: Compliance register (AI + owner emails) —— */}
      {isAdvisory && !isLeadAdvisor && activeTab.key === "compliance-register" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
            <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
            <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>
            <p className="mt-2 text-xs font-medium text-[color:var(--color-text-muted)]">
              Certification / standard hint for AI:{" "}
              <span className="font-bold text-[color:var(--color-text-hero)]">{crCertificationHint || "-"}</span> (from the project record).
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                disabled={busy || orgPhaseMutationsLocked}
                onClick={() => void onComplianceRegisterGenerate()}
                className="rounded-xl bg-[color:var(--color-accent-fill)] px-6 py-3 text-[10px] font-black uppercase tracking-widest text-white shadow-md transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
              >
                {busy ? "Working…" : "Generate register (AI)"}
              </button>
              <button
                type="button"
                disabled={
                  busy ||
                  orgPhaseMutationsLocked ||
                  !(complianceRegisterPackage?.rows && complianceRegisterPackage.rows.length)
                }
                onClick={() => void onComplianceRegisterGenerateEmails()}
                className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm transition-colors hover:bg-[color:var(--color-bg-alt)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Generate owner emails
              </button>
              <button
                type="button"
                disabled={
                  busy ||
                  orgPhaseMutationsLocked ||
                  placeholderEmails.length > 0 ||
                  !(complianceRegisterPackage?.rows && complianceRegisterPackage.rows.length)
                }
                title={placeholderEmails.length > 0 ? "Some email drafts still contain placeholders" : undefined}
                onClick={() => void onComplianceRegisterSendToSpoc()}
                className="rounded-xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-5 py-2.5 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-ok-fg)] shadow-sm transition-colors hover:bg-[color:var(--color-ok-bg)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                Send package to company SPOC
              </button>
            </div>

            {complianceRegisterPackage?.workflow === "sent_to_spoc" ? (
              <p className="mt-4 rounded-xl border border-[color:var(--color-ok-line)] bg-[color:var(--color-ok-bg)] px-4 py-2 text-xs font-semibold text-[color:var(--color-ok-fg)]">
                Package shared with SPOC
                {complianceRegisterPackage.spocPackageSentAt
                  ? ` - ${formatDdMmYyyyTime(complianceRegisterPackage.spocPackageSentAt)}`
                  : ""}
                .
              </p>
            ) : null}
            {complianceRegisterPackage?.lastEmailsGeneratedAt ? (
              <p className="mt-2 text-xs font-medium text-[color:var(--color-text-muted)]">
                Last email draft run:{" "}
                {formatDdMmYyyyTime(complianceRegisterPackage.lastEmailsGeneratedAt)}
              </p>
            ) : null}
            <ComplianceEmailPlaceholderWarning items={placeholderEmails} />

            {Array.isArray(complianceRegisterPackage?.rows) && complianceRegisterPackage.rows.length > 0 ? (
              <div className="mt-8 overflow-x-auto rounded-xl border border-[color:var(--color-border)]">
                <table className="w-full min-w-[1180px] text-left text-xs">
                  <thead className="border-b border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    <tr>
                      <th className="px-3 py-2">Ref</th>
                      <th className="px-3 py-2">Regulation</th>
                      <th className="px-3 py-2">Clause</th>
                      <th className="px-3 py-2">Requirement</th>
                      <th className="px-3 py-2">Applicability</th>
                      <th className="px-3 py-2">Country</th>
                      <th className="px-3 py-2">Owner</th>
                      <th className="max-w-[140px] px-3 py-2">Recipient</th>
                      <th className="px-3 py-2">Frequency</th>
                      <th className="px-3 py-2">ISO ref</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[color:var(--color-border)] text-[13px]">
                    {complianceRegisterPackage.rows.map((r) => {
                      const draft = complianceRegisterPackage.emailsByRefId?.[r.refId];
                      const open = crOpenRefId === r.refId;
                      const recipientCol = (r.recipientEmail || "").trim();
                      return (
                        <React.Fragment key={r.refId}>
                          <tr
                            className="cursor-pointer bg-[color:var(--color-bg-white)] hover:bg-[color:var(--color-bg-alt)]"
                            onClick={() => setCrOpenRefId(open ? null : r.refId)}
                          >
                            <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] font-bold text-[color:var(--color-accent)]">
                              {r.refId}
                            </td>
                            <td className="max-w-[140px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.regulationStatute}</td>
                            <td className="max-w-[100px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.clauseSection}</td>
                            <td className="max-w-[220px] px-3 py-2 text-[color:var(--color-text-hero)]">{r.requirementDescription}</td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.applicability}</td>
                            <td className="max-w-[100px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.originatingCountry}</td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-hero)]">{(typeof r.responsibleOwner === "string" && r.responsibleOwner.trim()) || <span className="italic text-[color:var(--color-warn-fg)]">{OWNER_NOT_ASSIGNED}</span>}</td>
                            <td
                              className="max-w-[160px] truncate px-3 py-2 text-xs text-[color:var(--color-text-hero)]"
                              title={recipientCol || undefined}
                            >
                              {recipientCol ? (
                                <span className="font-medium text-[color:var(--color-ok-fg)]">{recipientCol}</span>
                              ) : (
                                <span className="text-[color:var(--color-text-muted)]">-</span>
                              )}
                            </td>
                            <td className="max-w-[120px] px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.frequencyDue}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-[color:var(--color-text-muted-dark)]">{r.isoControlRef}</td>
                          </tr>
                          {open ? (
                            <tr className="bg-[color:var(--color-surface-sunken)]">
                              <td colSpan={10} className="px-4 py-4">
                                <div className="grid gap-4 md:grid-cols-2">
                                  <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Recipient email
                                    </p>
                                    <input
                                      type="email"
                                      className="mt-2 w-full max-w-md rounded-xl border border-[color:var(--color-border)] px-3 py-2 text-sm"
                                      placeholder={
                                        crRecipientDraft.trim()
                                          ? ""
                                          : "Filled by owner mapping when you run Generate owner emails"
                                      }
                                      value={crRecipientDraft}
                                      disabled={orgPhaseMutationsLocked}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => setCrRecipientDraft(e.target.value)}
                                      onBlur={async () => {
                                        const v = crRecipientDraft.trim();
                                        const pkg = complianceRegisterPackage;
                                        if (!pkg?.rows) return;
                                        const rows = pkg.rows.map((x) =>
                                          x.refId === r.refId ? { ...x, recipientEmail: v } : x,
                                        );
                                        await persistComplianceRegisterPackage({ ...pkg, rows });
                                      }}
                                    />
                                    <p className="mt-1 text-[11px] text-[color:var(--color-text-muted)]">
                                      Mapped from Owner → delegate on <strong>Generate owner emails</strong> (shows here
                                      and in the Recipient column). Edit if needed; re-run Generate owner emails after
                                      org chart changes.
                                    </p>
                                  </div>
                                  <div className="flex flex-wrap gap-2 md:justify-end">
                                    {draft?.bodyPlain ? (
                                      <button
                                        type="button"
                                        className="rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-hero)] shadow-sm hover:bg-[color:var(--color-bg-alt)]"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const t = `${draft.subject || ""}\n\n${draft.bodyPlain || ""}`;
                                          void navigator.clipboard.writeText(t);
                                          message.success("Copied to clipboard.");
                                        }}
                                      >
                                        Copy email
                                      </button>
                                    ) : (
                                      <span className="text-xs text-[color:var(--color-warn-fg)]">Generate owner emails first.</span>
                                    )}
                                  </div>
                                </div>
                                {draft?.bodyPlain ? (
                                  <div className="mt-4 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-4">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Subject
                                    </p>
                                    <p className="mt-1 text-sm font-semibold text-[color:var(--color-text-hero)]">{draft.subject}</p>
                                    <p className="mt-4 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                      Body
                                    </p>
                                    <pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-[color:var(--color-text-hero)]">
                                      {draft.bodyPlain}
                                    </pre>
                                  </div>
                                ) : null}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mt-8 text-sm font-medium text-[color:var(--color-text-muted)]">
                No register yet. Run <strong>Generate register (AI)</strong> to create rows from profiling, gap, and org
                chart data.
              </p>
            )}

            {(() => {
              const nextKey = getNextAdvisorOrgSetupKey("compliance-register");
              const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
              if (contentSealedReadOnly || !nextTab) return null;
              return (
                <AdvisorOrgSetupContinueFooter
                  nextTabLabel={nextTab.label}
                  disabled={busy || orgPhaseMutationsLocked}
                  onContinue={() => void selectOrgSubtab(nextKey)}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* —— Advisory: Book meetings (same API + flow as Gap Analysis schedule interviews) —— */}
      {isAdvisory && !isLeadAdvisor && activeTab.key === "meetings" && (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-xl">
            <div className="border-b border-[color:var(--color-border)] px-8 py-6">
              <h3 className="text-sm font-black uppercase tracking-widest text-[color:var(--color-text-hero)]">
                Schedule organizational setup meetings
              </h3>
              <p className="mt-1 text-[10px] font-medium text-[color:var(--color-text-muted)]">
                Same workflow as Gap Analysis → Schedule Interviews: enter details and department; the company SPOC is
                notified to add delegates, and meetings appear for everyone under Meetings.
              </p>
            </div>
            <div
              className={`space-y-6 p-8${orgPhaseMutationsLocked ? " pointer-events-none opacity-60" : ""}`}
            >
              <div>
                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Meeting title
                </label>
                <input
                  type="text"
                  value={advMeetTitle}
                  onChange={(e) => setAdvMeetTitle(e.target.value)}
                  placeholder={`e.g. Organizational setup workshop - ${project?.name || "project"}`}
                  disabled={orgPhaseMutationsLocked || advMeetSaving}
                  className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                />
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Department
                </label>
                <select
                  value={advMeetDeptSelect}
                  onChange={(e) => {
                    const v = e.target.value;
                    setAdvMeetDeptSelect(v);
                    if (v !== "Other") setAdvMeetDeptCustom("");
                  }}
                  disabled={orgPhaseMutationsLocked || advMeetSaving}
                  className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                >
                  <option value="">- Select department -</option>
                  {GAP_MEETING_DEPARTMENT_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                  <option value="Other">Other (specify below)</option>
                </select>
                {advMeetDeptSelect === "Other" ? (
                  <input
                    type="text"
                    value={advMeetDeptCustom}
                    onChange={(e) => setAdvMeetDeptCustom(e.target.value)}
                    placeholder="Enter department name"
                    disabled={orgPhaseMutationsLocked || advMeetSaving}
                    className="mt-2 w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                  />
                ) : null}
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    Meeting date
                  </label>
                  <input
                    type="date"
                    value={advMeetDate}
                    onChange={(e) => setAdvMeetDate(e.target.value)}
                    disabled={orgPhaseMutationsLocked || advMeetSaving}
                    className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    Meeting type
                  </label>
                  <select
                    value={advMeetType}
                    onChange={(e) => setAdvMeetType(e.target.value)}
                    disabled={orgPhaseMutationsLocked || advMeetSaving}
                    className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                  >
                    <option value="in_person">In-person Assessment</option>
                    <option value="remote_video">Virtual Assessment (Teams/Zoom)</option>
                    <option value="hybrid">Hybrid Assessment</option>
                  </select>
                </div>
              </div>
              {advMeetType === "in_person" || advMeetType === "hybrid" ? (
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    Meeting location
                  </label>
                  <input
                    type="text"
                    value={advMeetLocation}
                    onChange={(e) => setAdvMeetLocation(e.target.value)}
                    placeholder="e.g. Conference Room A, 5th Floor"
                    disabled={orgPhaseMutationsLocked || advMeetSaving}
                    className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                  />
                </div>
              ) : null}
              {advMeetType === "remote_video" || advMeetType === "hybrid" ? (
                <div>
                  <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    Meeting link
                  </label>
                  <input
                    type="url"
                    value={advMeetLink}
                    onChange={(e) => setAdvMeetLink(e.target.value)}
                    placeholder="e.g. https://teams.microsoft.com/l/meetup-join/..."
                    disabled={orgPhaseMutationsLocked || advMeetSaving}
                    className="w-full rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                  />
                </div>
              ) : null}
              <div className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3">
                <p className="text-[10px] leading-relaxed text-[color:var(--color-text-muted-dark)]">
                  Delegates are not selected here - after you submit, the company SPOC assigns attendees from{" "}
                  <strong className="text-[color:var(--color-text-hero)]">Workbench → Organizational Setup → Meetings &amp; delegates</strong>{" "}
                  or the sidebar <strong className="text-[color:var(--color-text-hero)]">Meetings</strong> page.
                </p>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                  Notes &amp; agenda
                </label>
                <textarea
                  value={advMeetNotes}
                  onChange={(e) => setAdvMeetNotes(e.target.value)}
                  placeholder="Agenda items, areas to cover, preparation notes..."
                  rows={3}
                  disabled={orgPhaseMutationsLocked || advMeetSaving}
                  className="w-full resize-none rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-4 py-3 text-sm font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                />
              </div>
              <button
                type="button"
                disabled={
                  advMeetSaving ||
                  orgPhaseMutationsLocked ||
                  !advMeetDate ||
                  !String(advMeetTitle || "").trim() ||
                  !advisorOrgSetupResolveMeetDept()
                }
                onClick={() => void onAdvisorOrgSetupScheduleMeeting()}
                className={`w-full rounded-xl px-6 py-3.5 text-[10px] font-black uppercase tracking-widest shadow-lg transition-all ${
                  advMeetSaving ||
                  orgPhaseMutationsLocked ||
                  !advMeetDate ||
                  !String(advMeetTitle || "").trim() ||
                  !advisorOrgSetupResolveMeetDept()
                    ? "cursor-not-allowed bg-[color:var(--color-bg-alt)] text-[color:var(--color-text-muted)]"
                    : "bg-[color:var(--color-accent-fill)] text-white hover:opacity-90"
                }`}
              >
                {advMeetSaving ? "Submitting…" : "Notify SPOC & submit meeting request"}
              </button>

              {advMeetScheduled.length > 0 ? (
                <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-5">
                  <h5 className="mb-3 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                    This project&apos;s meetings
                  </h5>
                  <div className="space-y-3">
                    {advMeetScheduled.map((mt, i) => {
                      const isEditing = advMeetRescheduleId === mt.id;
                      const st = mt.status || "scheduled";
                      const statusLabel =
                        st === "rescheduled"
                          ? "Rescheduled"
                          : st === "awaiting_delegates"
                            ? "Awaiting SPOC"
                            : "Scheduled";
                      const statusStyle =
                        st === "rescheduled"
                          ? "bg-[color:var(--color-warn-bg)] text-[color:var(--color-warn-fg)]"
                          : st === "awaiting_delegates"
                            ? "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]"
                            : "bg-[color:var(--color-accent-tint)] text-[color:var(--color-accent)]";
                      const attCount = (mt.attendees || []).length;
                      const deptLine = mt.department ? ` • Dept: ${mt.department}` : "";
                      return (
                        <div
                          key={mt.id || i}
                          className={`rounded-xl border bg-[color:var(--color-bg-white)] transition-all ${
                            isEditing ? "border-[color:var(--color-info-line)] shadow-md" : "border-[color:var(--color-border)]"
                          }`}
                        >
                          <div className="px-4 py-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <span className="block text-xs font-bold text-[color:var(--color-text-hero)]">{mt.title}</span>
                                <span className="text-[10px] text-[color:var(--color-text-muted)]">
                                  {mt.date} • {GAP_MEETING_TYPE_LABELS[mt.type] || mt.type}
                                  {deptLine}
                                  {st === "awaiting_delegates"
                                    ? " • SPOC will add delegates"
                                    : ` • ${attCount} attendee${attCount !== 1 ? "s" : ""}`}
                                </span>
                              </div>
                              <div className="flex shrink-0 flex-wrap items-center gap-2">
                                {!isEditing ? (
                                  <button
                                    type="button"
                                    disabled={advMeetSaving || orgPhaseMutationsLocked}
                                    onClick={() => {
                                      setAdvMeetRescheduleId(mt.id);
                                      setAdvMeetRescheduleData({
                                        meetingDate: mt.date || "",
                                        meetingType: mt.type || "in_person",
                                        meetingLink: mt.meetingLink || "",
                                        meetingLocation: mt.meetingLocation || "",
                                        notes: mt.notes || "",
                                      });
                                    }}
                                    className="rounded-lg border border-transparent px-2.5 py-1 text-[9px] font-bold uppercase tracking-wide text-[color:var(--color-text-muted)] transition-all hover:border-[color:var(--color-info-line)] hover:bg-[color:var(--color-accent-tint)] hover:text-[color:var(--color-accent)]"
                                  >
                                    Reschedule
                                  </button>
                                ) : null}
                                <span
                                  className={`rounded-lg px-2.5 py-0.5 text-[9px] font-black uppercase ${statusStyle}`}
                                >
                                  {statusLabel}
                                </span>
                              </div>
                            </div>
                            {!isEditing && (mt.meetingLocation || mt.meetingLink) ? (
                              <div className="mt-2 flex flex-wrap gap-3 border-t border-[color:var(--color-border)] pt-2">
                                {mt.meetingLocation ? (
                                  <span className="inline-flex items-center gap-1.5 text-[10px] text-[color:var(--color-text-muted)]">
                                    Location: {mt.meetingLocation}
                                  </span>
                                ) : null}
                                {mt.meetingLink ? (
                                  <a
                                    href={mt.meetingLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold text-[color:var(--color-accent)] hover:text-[color:var(--color-accent)] hover:underline"
                                  >
                                    Join meeting
                                  </a>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                          {isEditing ? (
                            <div className="space-y-3 border-t border-[color:var(--color-info-line)] px-4 pb-4 pt-1">
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="mb-1 block text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                    New date
                                  </label>
                                  <input
                                    type="date"
                                    value={advMeetRescheduleData.meetingDate || ""}
                                    onChange={(e) =>
                                      setAdvMeetRescheduleData((p) => ({ ...p, meetingDate: e.target.value }))
                                    }
                                    className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                                  />
                                </div>
                                <div>
                                  <label className="mb-1 block text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                    Meeting type
                                  </label>
                                  <select
                                    value={advMeetRescheduleData.meetingType || "in_person"}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setAdvMeetRescheduleData((p) => ({
                                        ...p,
                                        meetingType: v,
                                        ...(v === "in_person" ? { meetingLink: "" } : {}),
                                        ...(v === "remote_video" ? { meetingLocation: "" } : {}),
                                      }));
                                    }}
                                    className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                                  >
                                    <option value="in_person">In-person Assessment</option>
                                    <option value="remote_video">Virtual Assessment (Teams/Zoom)</option>
                                    <option value="hybrid">Hybrid Assessment</option>
                                  </select>
                                </div>
                              </div>
                              {advMeetRescheduleData.meetingType === "in_person" ||
                              advMeetRescheduleData.meetingType === "hybrid" ? (
                                <div>
                                  <label className="mb-1 block text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                    Location
                                  </label>
                                  <input
                                    type="text"
                                    value={advMeetRescheduleData.meetingLocation || ""}
                                    onChange={(e) =>
                                      setAdvMeetRescheduleData((p) => ({ ...p, meetingLocation: e.target.value }))
                                    }
                                    placeholder="e.g. Conference Room A, 5th Floor"
                                    className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                                  />
                                </div>
                              ) : null}
                              {advMeetRescheduleData.meetingType === "remote_video" ||
                              advMeetRescheduleData.meetingType === "hybrid" ? (
                                <div>
                                  <label className="mb-1 block text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                                    Meeting link
                                  </label>
                                  <input
                                    type="url"
                                    value={advMeetRescheduleData.meetingLink || ""}
                                    onChange={(e) =>
                                      setAdvMeetRescheduleData((p) => ({ ...p, meetingLink: e.target.value }))
                                    }
                                    placeholder="e.g. https://teams.microsoft.com/l/meetup-join/..."
                                    className="w-full rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] px-3 py-2 text-xs font-medium text-[color:var(--color-text-hero)] outline-none focus:ring-2 focus:ring-[color:var(--color-info-line)]"
                                  />
                                </div>
                              ) : null}
                              <div className="flex items-center gap-2 pt-1">
                                <button
                                  type="button"
                                  disabled={advMeetSaving || !advMeetRescheduleData.meetingDate}
                                  onClick={() => void onAdvisorOrgSetupConfirmReschedule()}
                                  className="rounded-lg bg-[color:var(--color-accent-fill)] px-4 py-2 text-[10px] font-black uppercase tracking-widest text-white shadow-sm transition-all hover:opacity-90 disabled:bg-[color:var(--color-bg-alt)] disabled:text-[color:var(--color-text-muted)]"
                                >
                                  Confirm reschedule
                                </button>
                                <button
                                  type="button"
                                  disabled={advMeetSaving}
                                  onClick={() => {
                                    setAdvMeetRescheduleId(null);
                                    setAdvMeetRescheduleData({});
                                  }}
                                  className="rounded-lg px-4 py-2 text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)] transition-all hover:bg-[color:var(--color-bg-alt)]"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {(() => {
                const nextKey = getNextAdvisorOrgSetupKey("meetings");
                const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
                if (contentSealedReadOnly || !nextTab) return null;
                return (
                  <AdvisorOrgSetupContinueFooter
                    nextTabLabel={nextTab.label}
                    disabled={busy || orgPhaseMutationsLocked}
                    onContinue={() => void selectOrgSubtab(nextKey)}
                  />
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* —— Advisory: other tabs (placeholders) —— */}
      {isAdvisory && !isLeadAdvisor && !["branding", "org-chart", "artefacts", "context", "compliance-register", "meetings"].includes(
        activeTab.key,
      ) && (
        <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted)]">{activeTab.description}</p>
          {(() => {
            const nextKey = getNextAdvisorOrgSetupKey(activeTab.key);
            const nextTab = nextKey ? ORG_SETUP_SUBTABS.find((t) => t.key === nextKey) : null;
            if (contentSealedReadOnly || !nextTab) return null;
            return (
              <AdvisorOrgSetupContinueFooter
                nextTabLabel={nextTab.label}
                disabled={busy || orgPhaseMutationsLocked}
                onContinue={() => void selectOrgSubtab(nextKey)}
              />
            );
          })()}
        </div>
      )}

      {isDelegate && activeTab.key === "delegate-company-artefacts" && project?.id ? (
        <DelegateCompanyArtefactWorkspace
          projectId={project.id}
          user={user}
          getApiToken={getApiToken}
          fetchProjectProfile={fetchProjectProfile}
          wiz={wiz}
          artefactIndexRows={artefactIndexRows}
          brandedBlobUrlByRowId={brandedBlobUrlByRowId}
        />
      ) : null}

      {/* —— Lead Advisor: readonly org chart + placeholders (no branding / build-chart tabs) —— */}
      {isLeadAdvisor && activeTab.key === "la-org-chart" && (
        <div className="space-y-6">
          {readOnlyOrgChartGateMessage ? (
            <div className="rounded-2xl border border-[color:var(--color-info-line)] bg-[color:var(--color-accent-tint)] px-4 py-3 text-sm font-semibold text-[color:var(--color-accent)]">
              {readOnlyOrgChartGateMessage}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  ["Total people", liveOrgSummary.totalPeople],
                  ["Departments", liveOrgSummary.departments],
                  ["Top level", liveOrgSummary.topLevel],
                  ["Reporting lines", liveOrgSummary.reportingLines],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-5 shadow-sm">
                    <p className="text-[9px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">{label}</p>
                    <p className="mt-2 text-2xl font-black text-[color:var(--color-text-hero)]">{val}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Organizational chart</h3>
                <p className="mt-1 text-xs font-medium text-[color:var(--color-text-muted)]">Approved by company SPOC - view only.</p>
                <div className="mt-6 min-h-[120px] rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-surface-sunken)] p-4">
                  <OrgChartCanvas forest={orgForest} readOnly disabled={busy || orgPhaseMutationsLocked} />
                </div>
              </div>
              <div className="rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] p-8 shadow-xl">
                <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">Personnel directory</h3>
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[640px] text-left text-sm">
                    <thead>
                      <tr className="border-b border-[color:var(--color-border)] text-[10px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
                        <th className="pb-3 pr-3">#</th>
                        <th className="pb-3 pr-3">Name</th>
                        <th className="pb-3 pr-3">Designation</th>
                        <th className="pb-3 pr-3">Role</th>
                        <th className="pb-3 pr-3">Department</th>
                        <th className="pb-3 pr-3">Reports to</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[color:var(--color-border)]">
                      {normalizedOrgRows.map((row, i) => (
                        <tr key={row.id} className="font-medium text-[color:var(--color-text-hero)]">
                          <td className="py-3 pr-3 text-[color:var(--color-text-muted)]">{i + 1}</td>
                          <td className="py-3 pr-3">{row.name}</td>
                          <td className="py-3 pr-3">{row.designation}</td>
                          <td className="py-3 pr-3">
                            <span
                              className={`inline-block rounded-lg border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${ROLE_BADGE_CLASS[row.roleNormalized] || ROLE_BADGE_CLASS.UNKNOWN}`}
                            >
                              {roleLabel(row.roleNormalized)}
                            </span>
                          </td>
                          <td className="py-3 pr-3">{row.department || "-"}</td>
                          <td className="py-3 pr-3 text-[color:var(--color-text-muted-dark)]">{row.reportsToRaw?.trim() ? row.reportsToRaw : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {isLeadAdvisor && activeTab.key === "la-approval-matrix" ? (
        <LeadAdvisorApprovalMatrixPanel
          wiz={wiz}
          artefactIndexRows={artefactIndexRows}
          normalizedOrgRows={normalizedOrgRows}
          projectMembers={project?.members || []}
          persistWizard={persistWizard}
          busy={busy}
          setBusy={setBusy}
          orgChartApproved={isOrgChartApprovedForReadonlyDashboard(wiz)}
          readOnly={approvalMatrixReadOnly}
          onAfterGenerate={() => void selectOrgSubtab("la-approval-matrix")}
          onContinueToRiskKpis={onLeadAdvisorContinueFromApprovalMatrix}
          projectId={project?.id}
          projectName={project?.companyName || project?.name}
          onActOnBehalfOrgChartApprove={onSpocApproveOrgChartImpersonated}
          onActOnBehalfArtefactsApproveRelease={onSpocApproveAndReleaseCompanyArtefactsDocImpersonated}
        />
      ) : null}

      {isLeadAdvisor && activeTab.key === "la-risk-kpis" ? (
        <LeadAdvisorRiskScaleAndKpisPanel project={project} contentSealedReadOnly={contentSealedReadOnly} />
      ) : null}

      {showViewOnlyBanner && (
        <div className="rounded-2xl border border-[color:var(--color-warn-line)] bg-[color:var(--color-warn-bg)] p-8 shadow-xl">
          <h3 className="text-sm font-black uppercase tracking-tight text-[color:var(--color-text-hero)]">{activeTab.title}</h3>
          <p className="mt-2 text-sm font-medium text-[color:var(--color-text-muted-dark)]">{activeTab.description}</p>
          <p className="mt-6 text-sm font-semibold text-[color:var(--color-warn-fg)]">
            View only. Advisor, Lead Advisor, Admin, and company SPOC can perform actions in this phase.
          </p>
        </div>
      )}

      <ArtefactBrandedCompareModal
        open={compareRow != null}
        onClose={() => {
          setCompareRow(null);
          setCompareHideDownloads(false);
        }}
        originalBlobUrl={compareRow ? artefactOriginalBlobUrl(compareRow, sourceBlobUrlByRowId) : undefined}
        brandedBlobUrl={compareRow ? brandedBlobUrlByRowId[String(compareRow.id)] : null}
        fileLabel={compareRow ? artefactFileBasename(compareRow) || compareRow.templateId : ""}
        getApiToken={getApiToken}
        hideDownloads={compareHideDownloads}
      />
    </div>
  );
}
